import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import "firebase/storage";

import firebaseConfig from "./firebase.config";
import { trackActivity } from "../utils/activityIndicator";
import { timeQuery } from "../utils/queryProfiler";
import {
  sumLotOrderTotals,
  lotIsFullyDelivered,
} from "../utils/lotOrderMath";
import {
  LOT_DIGITS_FIELD,
  lotNoDigits,
  lotDigitsQuery,
} from "../utils/lotSearchKeys";
import {
  USER_ID_DIGITS_FIELD,
  NAME_LOWER_FIELD,
  MOBILE_DIGITS_FIELD,
  EMAIL_LOWER_FIELD,
  userIdDigitsQuery,
  nameLower,
  mobileDigits,
  emailLower,
  staleUserSearchKeys,
} from "../utils/userSearchKeys";

firebase.initializeApp(firebaseConfig);

// Stamped on every document this app creates in the collections the profile
// page pages through. Nothing else in these documents is orderable: the
// date fields are locale display strings ("8-5-2026") that sort wrongly as
// text, and a payment's id is `Math.random() * Date.now()`. Existing
// documents get the same field from scripts/backfill-created-at.js.
const createdAtStamp = () => firebase.firestore.FieldValue.serverTimestamp();

export const auth = firebase.auth();
export const firestore = firebase.firestore();
export const storage = firebase.storage();

// Serve repeat reads from IndexedDB instead of the network. Much of what this
// app loads on every boot barely changes between sessions — offices, cnfs,
// employees, taxes, rate cards, the lot list — and without a local cache every
// one of those is a cold fetch each time an admin opens the panel.
//
// Deliberately fire-and-forget: persistence is an optimisation, and none of
// the two ways it can be refused are errors worth interrupting anyone for.
// `failed-precondition` means another tab already holds the lease (that tab
// keeps the cache; this one simply runs online), and `unimplemented` means the
// browser has no IndexedDB — private windows, older Safari. Both must leave
// the app fully working, which is why this never rejects.
// Count every Firestore read so the shell can show that something is loading.
// Done here, once, rather than as a `loading` flag in each of the 138 page
// components — most of them fire their fetches without awaiting and render an
// empty table meanwhile, with no indication that data is on its way.
//
// `.get()` is the public read entry point on all three reference types, and
// wrapping the prototype leaves call sites untouched: the same promise is
// returned, so results and rejections behave exactly as before.
// `CollectionReference` inherits `get` from `Query`, so the flag is found
// through the prototype chain and it isn't wrapped twice.
const instrumentFirestoreReads = () => {
  const namespace = firebase.firestore;
  [
    namespace.Query,
    namespace.DocumentReference,
    namespace.CollectionReference,
  ].forEach((Ctor) => {
    if (!Ctor || !Ctor.prototype || Ctor.prototype.__algActivityTracked) return;
    const originalGet = Ctor.prototype.get;
    if (typeof originalGet !== "function") return;
    Ctor.prototype.get = function trackedGet(...args) {
      // timeQuery is a pass-through unless the profiler is switched on from
      // the console (algProfiler.start()).
      return trackActivity(
        timeQuery(this, () => originalGet.apply(this, args)),
      );
    };
    Ctor.prototype.__algActivityTracked = true;
  });
};

try {
  instrumentFirestoreReads();
} catch (error) {
  // A progress bar is never worth breaking data access for.
  console.warn("Could not instrument Firestore reads for the loader:", error);
}

// Feature-checked as well: the unit suites stub Firestore, and a stub has no
// `enablePersistence`. An optimisation must never be the reason a module fails
// to load.
if (firestore && typeof firestore.enablePersistence === "function") {
  try {
    firestore.enablePersistence({ synchronizeTabs: true }).catch((error) => {
      const code = error && error.code;
      if (code !== "failed-precondition" && code !== "unimplemented") {
        console.warn("Firestore offline persistence unavailable:", error);
      }
    });
  } catch (error) {
    console.warn("Firestore offline persistence could not be enabled:", error);
  }
}

export default firebase;
export const createAdminProfileDocument = async (userAuth, additionalData) => {
  if (!userAuth) return;

  const adminRef = firestore.doc(`admins/${userAuth.uid}`);

  const snapShot = await adminRef.get();
  if (!snapShot.exists) {
    const { name, email } = userAuth;
    const createdAt = new Date();
    try {
      await adminRef.set({
        name,
        email,
        createdAt,
        ...additionalData,
      });
    } catch (error) {
      console.log("error creating admin", error.message);
    }
  }
  return adminRef;
};

export const uploadLot = async (lotObj) => {
  const lotRef = firestore.doc(`lots/${lotObj.lotNo}`);
  const snapShot = await lotRef.get();
  if (!snapShot.exists) {
    try {
      await lotRef.set({
        ...lotObj,
        // After the spread, never before: a lot object that has been round
        // tripped through Firestore and the edit form carries the old value,
        // and the derived one must win. See utils/lotSearchKeys.js.
        [LOT_DIGITS_FIELD]: lotNoDigits(lotObj.lotNo),
        createdAt: createdAtStamp(),
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await lotRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert(
      "there is already a lot with this given Lot no, please change the Lot no and try again",
    );
    return null;
  }
};
export const createNotice = async (noticeObj) => {
  const noticeRef = firestore.doc(`notices/${noticeObj.id}`);
  const snapShot = await noticeRef.get();
  if (!snapShot.exists) {
    try {
      await noticeRef.set({
        ...noticeObj,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      });

      const uploadedSnapShot = await noticeRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert("there is already a Notice with similar Id, please try again later");
  }
};
export const createBlog = async (blogObj) => {
  const blogRef = firestore.doc(`blogs/${blogObj.id}`);
  const snapShot = await blogRef.get();
  if (!snapShot.exists) {
    try {
      await blogRef.set({
        ...blogObj,
        createdAt: createdAtStamp(),
      });

      const uploadedSnapShot = await blogRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert("there is already a Blogs with similar Id, please try again later");
  }
};
export const createIntro = async (introObj) => {
  const introRef = firestore.doc(`intros/${introObj.id}`);
  const snapShot = await introRef.get();

  delete introObj.file;
  if (!snapShot.exists) {
    try {
      await introRef.set({
        ...introObj,
        createdAt: createdAtStamp(),
      });

      const uploadedSnapShot = await introRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert(
      "there is already an Intro image with similar Id, please try again later",
    );
  }
};
export const uploadExpressRatesDocuments = async (countryObj) => {
  const countryRef = firestore.doc(
    `expressRatesDocuments/${countryObj.country}`,
  );
  const snapShot = await countryRef.get();
  if (!snapShot.exists) {
    try {
      await countryRef.set({
        ...countryObj,
        createdAt: createdAtStamp(),
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await countryRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert(
      "there is already a country with similar name, please change the country name and try again",
    );
  }
};
export const uploadOffice = async (officeObj) => {
  const officeRef = firestore.doc(`offices/${officeObj.officeId}`);
  const snapShot = await officeRef.get();
  if (!snapShot.exists) {
    try {
      await officeRef.set({
        ...officeObj,
        createdAt: createdAtStamp(),
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await officeRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert(
      "there is already a office with similar name, please change the country name and try again",
    );
  }
};
export const uploadBookingOffice = async (officeObj) => {
  const officeRef = firestore.doc(`bookingOffices/${officeObj.officeId}`);
  const snapShot = await officeRef.get();
  if (!snapShot.exists) {
    try {
      await officeRef.set({
        ...officeObj,
        createdAt: createdAtStamp(),
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await officeRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert(
      "there is already a office with similar name, please change the country name and try again",
    );
  }
};
export const uploadCnf = async (cnfObj) => {
  const cnfRef = firestore.doc(`cnfs/${cnfObj.cnfId}`);
  const snapShot = await cnfRef.get();
  if (!snapShot.exists) {
    try {
      await cnfRef.set({
        ...cnfObj,
        createdAt: createdAtStamp(),
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await cnfRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert(
      "there is already a cnf with similar name, please change the name and try again",
    );
  }
};
// export const uploadCnfBill = async (billObj) => {
//   let res = await firestore.runTransaction(async (t) => {
//     const cnfBillRef = firestore.doc(`cnfBills/${billObj.id}`);
//     const cnfBillMonthRef = firestore.doc(`cnfBillMonths/${billObj.month}`);

//     const snapShot = await t.get(cnfBillRef);
//     const cnfBillMonth = await t.get(cnfBillMonthRef);
//     if (!snapShot.exists) {
//       try {
//         await t.set(cnfBillRef, {
//           ...billObj,
//         });
//         console.log(snapShot.data());
//       } catch (error) {
//         alert(error);
//       }
//     } else {
//       alert("there is already a bill with similar id, please try again later");
//     }
//     if (!cnfBillMonth.exists) {
//       t.set(cnfBillMonthRef, { month: billObj.month });
//     }
//   });
//   return res;
// };

export const uploadCnfBill = async (billObj) => {
  const batch = firestore.batch();
  const cnfBillRef = firestore.doc(`cnfBills/${billObj.id}`);
  const cnfBillMonthRef = firestore.doc(`cnfBillMonths/${billObj.month}`);

  // Get the documents
  const cnfBillSnapShot = await cnfBillRef.get();
  const cnfBillMonthSnapShot = await cnfBillMonthRef.get();

  // Check if the bill document exists
  if (!cnfBillSnapShot.exists) {
    try {
      // Set the bill document
      batch.set(cnfBillRef, {
        ...billObj,
        createdAt: createdAtStamp(),
      });
      console.log(cnfBillSnapShot.data());
    } catch (error) {
      alert(error);
    }
  } else {
    alert("there is already a bill with similar id, please try again later");
  }

  // Check if the bill month document exists
  if (!cnfBillMonthSnapShot.exists) {
    // Set the bill month document
    batch.set(cnfBillMonthRef, {
      month: billObj.month,
      createdAt: createdAtStamp(),
    });
  }

  // Commit the batch
  await batch.commit();

  return true;
};

export const getAllLotsByDate = async (startDate, endDate) => {
  let lotsCollectionRef;
  // --- Fix date issue by setting full-day timestamps ---
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  const startTs = firebase.firestore.Timestamp.fromDate(start);
  const endTs = firebase.firestore.Timestamp.fromDate(end);

  lotsCollectionRef = firestore
    .collection("lots")
    .where("localWarehouseDateTimeStamp", ">=", startTs)
    .where("localWarehouseDateTimeStamp", "<=", endTs);

  // try {
  const lots = await lotsCollectionRef.get();
  const lotsArray = [];
  lots.forEach((doc) => {
    lotsArray.push(doc.data());
  });
  return lotsArray
    .filter((lot) => lot.shippingLine != "China airlines")
    .sort((a, b) => {
      const timeA =
        (a.localWarehouseDateTimeStamp &&
          a.localWarehouseDateTimeStamp.toDate &&
          a.localWarehouseDateTimeStamp.toDate()) ||
        new Date(0);
      const timeB =
        (b.localWarehouseDateTimeStamp &&
          b.localWarehouseDateTimeStamp.toDate &&
          b.localWarehouseDateTimeStamp.toDate()) ||
        new Date(0);

      return timeB - timeA; // newest first
    });
  // } catch (error) {
  //   alert(error);
  // }
};

/**
 * Parcels booked into the origin warehouse within the last `hours`.
 *
 * A parcel's record is created at the warehouse counter as it is received —
 * creation *is* the intake event, there is no separate "mark as received"
 * transition — so `createdAt` is the arrival moment. See warehouseTime.js,
 * which renders it in the warehouse's own timezone rather than the viewer's.
 *
 * Deliberately not built on the lot-level `abroadWarehouseDate`: that is a
 * display string ("8/18/2026 (3:20:11 PM)"), is stamped only when the lot is
 * first created, and has no Timestamp twin — so it can be neither ordered nor
 * range-queried. Only `localWarehouseDateTimeStamp` (Dhaka) ever got one.
 *
 * A range plus an orderBy on that same single field is served by Firestore's
 * automatic index — no composite index needed, so long as nothing else is
 * filtered here.
 *
 * `limit` is what keeps this bounded: a busy 48 hours is a lot of parcels and
 * this feeds a dashboard panel, not a report.
 */
export const getRecentWarehouseParcels = async ({
  hours = 48,
  limit = 50,
} = {}) => {
  const cutoff = firebase.firestore.Timestamp.fromMillis(
    Date.now() - hours * 60 * 60 * 1000,
  );
  try {
    const snapshot = await firestore
      .collection("orders")
      .where("createdAt", ">=", cutoff)
      .orderBy("createdAt", "desc")
      .limit(limit)
      .get();
    const parcels = [];
    snapshot.forEach((doc) => parcels.push(doc.data()));
    return parcels;
  } catch (error) {
    // One panel failing must not take the dashboard down with it, and an
    // alert() on every page load would be worse than an empty list.
    console.error("Could not load recently received parcels:", error);
    return [];
  }
};

/**
 * The most recent ledger entries — cash in and cash out, newest first.
 *
 * Ordered by document id rather than by any field, which is the whole point:
 * Firestore silently omits documents that lack the field being ordered on, and
 * a document id always exists. Ordering by `createdAt` would have looked
 * right and been badly wrong — six of the ten paths that write `dailyCashIn`
 * (parcel invoices, P2P, sourcing, express, hand carry) never stamp it, so the
 * busiest sources of cash-in would simply be absent from this list.
 *
 * The id sorts chronologically for free: every write path builds it from a
 * 13-digit `Date.now()` prefix, optionally followed by a disambiguator. The
 * prefix width is constant, so lexicographic order reads the timestamp
 * positionally and only falls through to the suffix on a same-millisecond
 * tie. (`date` is a "18/08/2026" display string and would sort 31/01 above
 * 01/12 — not usable for this.)
 *
 * Single-field ordering on `__name__` is served by the automatic index, so
 * there is no composite index to create.
 */
// The 13-digit `Date.now()` prefix every id starts with. Parsed rather than
// compared as text because ids are not all the same length, and because a
// legacy id that isn't epoch-prefixed should sort to the bottom instead of
// silently taking a slot at the top.
const ledgerEntryTime = (entry) => {
  const digits = String((entry && entry.id) || "").match(/^\d{13}/);
  return digits ? parseInt(digits[0], 10) : 0;
};

const readRecentLedger = async (collectionName, limit) => {
  try {
    const snapshot = await firestore
      .collection(collectionName)
      .orderBy(firebase.firestore.FieldPath.documentId(), "desc")
      .limit(limit)
      .get();
    const entries = [];
    snapshot.forEach((doc) => entries.push(doc.data()));
    // Document-id order is byte order, so any id that isn't epoch-prefixed
    // (a letter sorts above every digit) would otherwise sit at the top
    // pretending to be the newest entry. Re-sorting on the parsed timestamp
    // puts them where they belong.
    return entries.sort((a, b) => ledgerEntryTime(b) - ledgerEntryTime(a));
  } catch (error) {
    // Per collection, so a failure on one side of the ledger still leaves the
    // other side showing its entries rather than blanking both columns.
    console.error(`Could not load recent ${collectionName} entries:`, error);
    return [];
  }
};

export const getRecentLedgerEntries = async ({ limit = 20 } = {}) => {
  const [cashIn, cashOut] = await Promise.all([
    readRecentLedger("dailyCashIn", limit),
    readRecentLedger("dailyExpenses", limit),
  ]);
  return { cashIn, cashOut };
};

export const getAllExpressLotsByDate = async (startDate, endDate) => {
  let lotsCollectionRef;
  // --- Fix date issue by setting full-day timestamps ---
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  const startTs = firebase.firestore.Timestamp.fromDate(start);
  const endTs = firebase.firestore.Timestamp.fromDate(end);

  lotsCollectionRef = firestore
    .collection("lots")
    .where("shippingLine", "==", "China airlines");

  const startTime = startTs.toDate().getTime();
  const endTime = endTs.toDate().getTime();

  const withinRange = (item) => {
    const tsField = item.localWarehouseDateTimeStamp;
    if (!tsField) return false;
    const ts = tsField.toDate().getTime();
    return ts >= startTime && ts <= endTime;
  };

  // This function already built `startTs`/`endTs` but never applied them to
  // the query — it read *every* China-airlines lot ever recorded and then
  // dropped most of them in JS. The dashboard calls it on every visit, so the
  // cost grew with the lot history rather than with the range being shown.
  //
  // Combining the equality filter with a range on another field needs a
  // composite index, which isn't created automatically, so a missing one falls
  // back to the old read-everything path rather than leaving the dashboard
  // broken until someone follows the console link.
  let lotsArray = [];
  try {
    const lots = await lotsCollectionRef
      .where("localWarehouseDateTimeStamp", ">=", startTs)
      .where("localWarehouseDateTimeStamp", "<=", endTs)
      .get();
    lots.forEach((doc) => lotsArray.push(doc.data()));
  } catch (error) {
    if (error && error.code === "failed-precondition") {
      console.warn(
        "Missing Firestore index on lots (shippingLine ==, localWarehouseDateTimeStamp range). Falling back to a full scan — create the composite index to make this query cheap.",
        error,
      );
      const lots = await lotsCollectionRef.get();
      lots.forEach((doc) => lotsArray.push(doc.data()));
    } else {
      throw error;
    }
  }

  // Kept for the fallback path, and harmless on the indexed one: documents
  // without the timestamp field are excluded by a range query anyway.
  const filteredLots = lotsArray.filter(withinRange);
  return filteredLots.sort((a, b) => {
    const timeA =
      (a.localWarehouseDateTimeStamp &&
        a.localWarehouseDateTimeStamp.toDate &&
        a.localWarehouseDateTimeStamp.toDate()) ||
      new Date(0);
    const timeB =
      (b.localWarehouseDateTimeStamp &&
        b.localWarehouseDateTimeStamp.toDate &&
        b.localWarehouseDateTimeStamp.toDate()) ||
      new Date(0);

    return timeB - timeA; // newest first
  });
  // } catch (error) {
  //   alert(error);
  // }
};
export const getAllSourcingInvoicesByDate = async (startDate, endDate) => {
  let lotsCollectionRef;
  // --- Fix date issue by setting full-day timestamps ---
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  const startTs = firebase.firestore.Timestamp.fromDate(start);
  const endTs = firebase.firestore.Timestamp.fromDate(end);

  lotsCollectionRef = firestore
    .collection("sourcing")
    .where("localWarehouseDateTimeStamp", ">=", startTs)
    .where("localWarehouseDateTimeStamp", "<=", endTs);

  // try {
  const lots = await lotsCollectionRef.get();
  const lotsArray = [];
  lots.forEach((doc) => {
    lotsArray.push(doc.data());
  });
  return lotsArray.sort((a, b) => {
    const timeA =
      (a.localWarehouseDateTimeStamp &&
        a.localWarehouseDateTimeStamp.toDate &&
        a.localWarehouseDateTimeStamp.toDate()) ||
      new Date(0);
    const timeB =
      (b.localWarehouseDateTimeStamp &&
        b.localWarehouseDateTimeStamp.toDate &&
        b.localWarehouseDateTimeStamp.toDate()) ||
      new Date(0);

    return timeB - timeA; // newest first
  });
  // } catch (error) {
  //   alert(error);
  // }
};
export const getAllPurchaseInvoicesByDate = async (startDate, endDate) => {
  let lotsCollectionRef;
  // --- Fix date issue by setting full-day timestamps ---
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  const startTs = firebase.firestore.Timestamp.fromDate(start);
  const endTs = firebase.firestore.Timestamp.fromDate(end);

  lotsCollectionRef = firestore
    .collection("purchasing")
    .where("localWarehouseDateTimeStamp", ">=", startTs)
    .where("localWarehouseDateTimeStamp", "<=", endTs);

  // try {
  const lots = await lotsCollectionRef.get();
  const lotsArray = [];
  lots.forEach((doc) => {
    lotsArray.push(doc.data());
  });
  return lotsArray.sort((a, b) => {
    const timeA =
      (a.localWarehouseDateTimeStamp &&
        a.localWarehouseDateTimeStamp.toDate &&
        a.localWarehouseDateTimeStamp.toDate()) ||
      new Date(0);
    const timeB =
      (b.localWarehouseDateTimeStamp &&
        b.localWarehouseDateTimeStamp.toDate &&
        b.localWarehouseDateTimeStamp.toDate()) ||
      new Date(0);

    return timeB - timeA; // newest first
  });
  // } catch (error) {
  //   alert(error);
  // }
};
export const getAllSoldProductsByDate = async (startDate, endDate) => {
  let lotsCollectionRef;
  // --- Fix date issue by setting full-day timestamps ---
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  const startTs = firebase.firestore.Timestamp.fromDate(start);
  const endTs = firebase.firestore.Timestamp.fromDate(end);

  lotsCollectionRef = firestore
    .collection("sold-products")
    .where("localWarehouseDateTimeStamp", ">=", startTs)
    .where("localWarehouseDateTimeStamp", "<=", endTs);

  // try {
  const lots = await lotsCollectionRef.get();
  const lotsArray = [];
  lots.forEach((doc) => {
    lotsArray.push(doc.data());
  });
  return lotsArray.sort((a, b) => {
    const timeA =
      (a.localWarehouseDateTimeStamp &&
        a.localWarehouseDateTimeStamp.toDate &&
        a.localWarehouseDateTimeStamp.toDate()) ||
      new Date(0);
    const timeB =
      (b.localWarehouseDateTimeStamp &&
        b.localWarehouseDateTimeStamp.toDate &&
        b.localWarehouseDateTimeStamp.toDate()) ||
      new Date(0);

    return timeB - timeA; // newest first
  });
  // } catch (error) {
  //   alert(error);
  // }
};
const chunkArray = (array, size) => {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

export const getAllOrdersByLots = async (lotArray) => {
  if (!lotArray || lotArray.length === 0) return [];

  // 1. Extract lotNos
  const lotNos = lotArray;

  // 2. Split into chunks of 10 (Firestore limit)
  const chunks = chunkArray(lotNos, 10);

  const allOrders = [];

  // 3. Run queries in parallel
  const promises = chunks.map((chunk) => {
    return firestore.collection("orders").where("lotNo", "in", chunk).get();
  });

  const snapshots = await Promise.all(promises);

  // 4. Merge results
  snapshots.forEach((snapshot) => {
    snapshot.forEach((doc) => {
      allOrders.push({
        id: doc.id,
        ...doc.data(),
      });
    });
  });

  console.log("Total orders fetched:", allOrders.length);

  return allOrders;
};

// ── Per-lot order totals ──────────────────────────────────────────────────
//
// The dashboard's shipment tiles are four numbers, and producing them used to
// mean pulling every order in every lot of the last 30 days into the browser
// (getAllOrdersByLots above) to take four sums and throw the documents away.
// These totals are kept per lot instead, so that page reads a handful of small
// documents rather than thousands of orders.
//
// Why a recomputed cache and not FieldValue.increment(): eleven code paths in
// this file write to `orders`, and the two highest-volume ones —
// `updateOrdersInBatches` and `addPaymentWritesToBatch` — are blind
// full-document rewrites from client state that never re-read the order, so no
// delta can be computed there. Same rule as `aggregates/customerDues` below:
// few centralised writers earn increments (see handCarryMonthlyStats), many
// scattered writers get a cache that is always derived from the orders
// themselves. A missed increment is wrong forever and silent; a stale cache is
// bounded, self-heals, and `scripts/backfill-lot-order-totals.js --verify`
// can prove it.
//
// Why its own collection and not fields on the lot document:
// `createOrderModal.jsx` updates a lot with `updateLotRedux({ ...singleLot })`
// — a blind spread of a lot object held in component state — and `updateLot`
// does `update({ ...lotObj })` with no merge. Aggregates living on the lot doc
// would be rewritten from whatever stale copy that component happened to hold,
// *including* their freshness stamps, which is the one way a stale value could
// come back looking trusted. A separate document cannot be caught by that.
// IndexedDB stores objects by structured clone, which keeps the data but drops
// the prototype — so a Firestore `Timestamp` comes back as a bare
// `{seconds, nanoseconds}` and loses `.toDate()`/`.toMillis()`. Rebuilding the
// real instances on the way out means a cached document is indistinguishable
// from a freshly fetched one, instead of working only for the fields nothing
// happens to call a method on today.
export const rehydrateTimestamps = (value) => {
  if (!value || typeof value !== "object") return value;

  if (Array.isArray(value)) return value.map(rehydrateTimestamps);

  // Anything that already behaves like a Timestamp — or is any other class
  // instance — is returned untouched. Without this, walking a *real* Timestamp
  // would copy its `seconds`/`nanoseconds` into a bare object and strip the
  // very prototype this function exists to restore.
  if (typeof value.toDate === "function") return value;
  if (value instanceof Date) return value;

  if (
    typeof value.seconds === "number" &&
    typeof value.nanoseconds === "number"
  ) {
    return new firebase.firestore.Timestamp(value.seconds, value.nanoseconds);
  }

  // Only plain objects are walked; a class instance would lose its prototype.
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;

  const out = {};
  Object.keys(value).forEach((key) => {
    out[key] = rehydrateTimestamps(value[key]);
  });
  return out;
};

export const LOT_ORDER_TOTALS_COLLECTION = "lotOrderTotals";

// The backstop for a write path I failed to hook, not the primary mechanism —
// `ordersChangedAt` is what normally triggers a recompute. Deliberately long:
// a short TTL would re-scan every lot's orders on a timer and hand back most of
// what this change buys.
const LOT_TOTALS_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * The four dashboard figures for one lot's orders.
 *
 * Transcribed one-for-one from what dashboard.js computed inline, including
 * details that look like bugs but are load-bearing for matching the old
 * numbers: the filter is `order.ratePerKg` **truthiness** (so 0 and "" are
 * excluded but the string "0" is not), `finalTotal` falls back to
 * rate × weight only when falsy, and `parseInt` truncates per order — summing
 * per lot and then across lots gives an identical result only because the
 * truncation stays per order.
 *
 * `orderCount` counts every order in the lot; revenue and weight count only
 * priced ones. That asymmetry is in the original and is preserved.
 *
 * The arithmetic itself lives in utils/lotOrderMath.js and is re-exported
 * here. It is kept in its own CommonJS module so that
 * scripts/backfill-lot-order-totals.js — a plain Node script that cannot load
 * an ES module — computes byte-for-byte the same figures as the app. Two
 * copies of money arithmetic would eventually disagree.
 */
export { sumLotOrderTotals, lotIsFullyDelivered };

const lotTotalsRef = (lotNo) =>
  firestore.doc(`${LOT_ORDER_TOTALS_COLLECTION}/${lotNo}`);

const toMillis = (value) =>
  value && typeof value.toMillis === "function" ? value.toMillis() : 0;

// Stored totals can be trusted only if they were computed after the last
// change to the lot's orders, and are not older than the backstop window.
const totalsAreTrusted = (data, maxAgeMs) => {
  if (!data || typeof data.revenue !== "number") return false;
  const computedAt = toMillis(data.computedAt);
  if (!computedAt) return false;
  if (computedAt < toMillis(data.ordersChangedAt)) return false;
  return Date.now() - computedAt < maxAgeMs;
};

/**
 * Records that a lot's orders changed, so the next read recomputes its totals.
 *
 * Every write path that can change lot membership, `ratePerKg`, `grossWeight`,
 * `finalTotal`, or whether an order exists calls this. Never awaited by the
 * caller's critical path and never throws: failing to invalidate costs
 * freshness, and must not cost the write that triggered it.
 */
export const markLotOrdersChanged = async (lotNo) => {
  if (!lotNo) return;
  try {
    await lotTotalsRef(lotNo).set(
      {
        lotNo,
        // Client clock rather than serverTimestamp for the same reason as
        // aggregates/customerDues: a pending server timestamp reads back as
        // null, and this value is compared against `computedAt` written by the
        // same client moments later.
        ordersChangedAt: firebase.firestore.Timestamp.now(),
      },
      { merge: true },
    );
  } catch (error) {
    console.warn(`Could not invalidate lot totals for ${lotNo}:`, error);
  }
};

/** Invalidates each distinct lot once, however many of its orders changed. */
export const markLotsOrdersChanged = async (lotNos) => {
  const distinct = [...new Set((lotNos || []).filter(Boolean))];
  await Promise.all(distinct.map((lotNo) => markLotOrdersChanged(lotNo)));
};

/**
 * Recomputes `allDelivered` for each given lot from the current state of its
 * orders. `allDelivered` is the field the Delivered Lots page queries on.
 *
 * Until this existed, that field was written in exactly one place — the lot
 * expense form's "Update Expense" button — which made it a snapshot of the
 * moment an admin happened to enter C&F/freight costs. Those costs are
 * entered once, when the bills arrive, which is normally long before the last
 * customer has paid; nothing recomputed the flag afterwards, so a lot that
 * became fully paid later never reached the page. Payment is the event that
 * can actually complete a lot, so it is the event that recomputes this.
 *
 * Never throws. The payment it follows has already committed, and a stale
 * delivery flag is a display problem — it must not surface as a failed
 * payment. The next payment on the lot recomputes it anyway.
 */
export const recomputeLotsDelivered = async (lotNos) => {
  const distinct = [...new Set((lotNos || []).filter(Boolean))];
  if (!distinct.length) return;
  try {
    const orders = await getAllOrdersByLots(distinct);
    const ordersByLot = new Map(distinct.map((lotNo) => [lotNo, []]));
    orders.forEach((order) => {
      const bucket = ordersByLot.get(order.lotNo);
      if (bucket) bucket.push(order);
    });

    await Promise.all(
      distinct.map(async (lotNo) => {
        const allDelivered = lotIsFullyDelivered(ordersByLot.get(lotNo));
        const lotRef = firestore.doc(`lots/${lotNo}`);
        const snapshot = await lotRef.get();
        // A lot deleted between the payment and this recompute has nothing to
        // update, and writing would resurrect it as a stub document that the
        // lot list would then render as a blank row.
        if (!snapshot.exists) return;
        // Orders far outnumber lots that actually change state, so compare
        // before writing: a payment on an already-delivered lot, or one that
        // leaves invoices outstanding, costs a read and no write at all.
        if (!!snapshot.data().allDelivered === allDelivered) return;
        await lotRef.update({ allDelivered });
      }),
    );
  } catch (error) {
    console.warn("Could not recompute delivered state for lots:", error);
  }
};

const readStoredLotTotals = async (lotNos) => {
  const stored = new Map();
  const snapshots = await Promise.all(
    chunkArray(lotNos, 10).map((chunk) =>
      firestore
        .collection(LOT_ORDER_TOTALS_COLLECTION)
        .where(firebase.firestore.FieldPath.documentId(), "in", chunk)
        .get(),
    ),
  );
  snapshots.forEach((snapshot) =>
    snapshot.forEach((doc) => stored.set(doc.id, doc.data())),
  );
  return stored;
};

// Reads the orders of the given lots and writes fresh totals for each. Lots
// with no orders are stored as explicit zeros, so "no orders" is distinguished
// from "never computed" and stops being recomputed on every load.
const recomputeLotTotals = async (lotNos) => {
  const orders = await getAllOrdersByLots(lotNos);

  const grouped = new Map(lotNos.map((lotNo) => [lotNo, []]));
  orders.forEach((order) => {
    const bucket = grouped.get(order.lotNo);
    if (bucket) bucket.push(order);
  });

  const computed = new Map();
  const computedAt = firebase.firestore.Timestamp.now();
  await Promise.all(
    [...grouped.entries()].map(async ([lotNo, lotOrders]) => {
      const totals = sumLotOrderTotals(lotOrders);
      computed.set(lotNo, totals);
      try {
        await lotTotalsRef(lotNo).set(
          { lotNo, ...totals, computedAt },
          { merge: true },
        );
      } catch (error) {
        // A read-only admin still gets the right figures; they just aren't cached.
        console.warn(`Could not cache lot totals for ${lotNo}:`, error);
      }
    }),
  );
  return computed;
};

/**
 * Summed totals across the given lots, using stored per-lot figures wherever
 * they can be trusted and recomputing only the rest.
 *
 * With nothing cached this costs exactly what reading the orders directly cost
 * before; in steady state it is a few small document reads.
 *
 * @returns {Promise<{revenue: number, weight: number, orderCount: number, recomputedLots: number}>}
 */
export const getLotOrderTotals = async (
  lotNos,
  { maxAgeMs = LOT_TOTALS_MAX_AGE_MS } = {},
) => {
  const distinct = [...new Set((lotNos || []).filter(Boolean))];
  const empty = { revenue: 0, weight: 0, orderCount: 0, recomputedLots: 0 };
  if (distinct.length === 0) return empty;

  let stored = new Map();
  try {
    stored = await readStoredLotTotals(distinct);
  } catch (error) {
    // Unreadable cache must not break the page — fall through and recompute.
    console.warn("Lot totals cache unreadable, recomputing:", error);
  }

  const stale = distinct.filter(
    (lotNo) => !totalsAreTrusted(stored.get(lotNo), maxAgeMs),
  );
  const recomputed = stale.length ? await recomputeLotTotals(stale) : new Map();

  return distinct.reduce(
    (acc, lotNo) => {
      const totals = recomputed.get(lotNo) || stored.get(lotNo) || {};
      return {
        revenue: acc.revenue + (totals.revenue || 0),
        weight: acc.weight + (totals.weight || 0),
        orderCount: acc.orderCount + (totals.orderCount || 0),
        recomputedLots: acc.recomputedLots,
      };
    },
    Object.assign({}, empty, { recomputedLots: stale.length }),
  );
};
export const backfillLocalWarehouseTimestamp = async () => {
  const snapshot = await firestore.collection("sold-products").get();

  let batch = firestore.batch();
  let operationCount = 0;

  for (const doc of snapshot.docs) {
    const data = doc.data();

    // Skip if already exists and not empty
    if (
      data.localWarehouseDateTimeStamp &&
      data.localWarehouseDateTimeStamp !== ""
    ) {
      continue;
    }

    if (!data.date) continue;

    try {
      // Clean + parse date string
      const cleaned = data.date.replace(/[^\d/]/g, "");
      const parts = cleaned.split("/");

      if (parts.length !== 3) {
        console.log("Invalid format:", doc.id);
        continue;
      }

      const [day, month, year] = parts;
      const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));

      console.log("Parsed date for doc", doc.id, ":", parsedDate);

      if (isNaN(parsedDate)) {
        console.log("Invalid date:", doc.id);
        continue;
      }

      const timestamp = firebase.firestore.Timestamp.fromDate(parsedDate);

      batch.update(doc.ref, {
        localWarehouseDateTimeStamp: timestamp,
      });

      operationCount++;

      // Firestore batch limit = 500
      if (operationCount >= 500) {
        await batch.commit(); // ✅ safe now
        batch = firestore.batch(); // ✅ new batch
        operationCount = 0;
      }
    } catch (err) {
      console.error("Error parsing doc:", doc.id, err);
    }
  }

  // ✅ Commit remaining updates
  if (operationCount > 0) {
    await batch.commit();
  }

  console.log("Migration complete ✅");
};

export const uploadEmployee = async (employeeObj) => {
  const employeeRef = firestore.doc(`employees/${employeeObj.employeeId}`);
  const snapShot = await employeeRef.get();
  if (!snapShot.exists) {
    try {
      await employeeRef.set({
        ...employeeObj,
        createdAt: createdAtStamp(),
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await employeeRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert(
      "there is already a employee with similar name, please change the name and try again",
    );
  }
};
export const uploadTransaction = async (transactionObj) => {
  const transactionRef = firestore.doc(`transactions/${transactionObj.id}`);
  const snapShot = await transactionRef.get();
  if (!snapShot.exists) {
    try {
      await transactionRef.set({
        ...transactionObj,
        createdAt: createdAtStamp(),
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await transactionRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert(
      "there is already a transaction with similar name, please change the name and try again",
    );
  }
};
export const uploadExpense = async (expenseObj) => {
  const expenseRef = firestore.doc(`dailyExpenses/${expenseObj.id}`);
  const snapShot = await expenseRef.get();
  if (!snapShot.exists) {
    try {
      await expenseRef.set({
        ...expenseObj,
        createdAt: createdAtStamp(),
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await expenseRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert("there is already a expense with similar id,try again later");
  }
};
export const uploadForwarder = async (forwarderObj) => {
  const forwarderRef = firestore.doc(`freight-forwarder/${forwarderObj.id}`);
  const snapShot = await forwarderRef.get();
  if (!snapShot.exists) {
    try {
      await forwarderRef.set({
        ...forwarderObj,
        createdAt: createdAtStamp(),
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await forwarderRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert("there is already a forwarder with similar id,try again later");
  }
};
export const uploadSourcing = async (sourcingObj) => {
  const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);
  const snapShot = await sourcingRef.get();
  if (!snapShot.exists) {
    try {
      await sourcingRef.set({
        ...sourcingObj,
        createdAt: createdAtStamp(),
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await sourcingRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert("there is already a sourcing with similar id,try again later");
  }
};
export const uploadPurchasing = async (purchasingObj) => {
  const purchasingRef = firestore.doc(`purchasing/${purchasingObj.id}`);
  const snapShot = await purchasingRef.get();
  if (!snapShot.exists) {
    try {
      await purchasingRef.set({
        ...purchasingObj,
        createdAt: createdAtStamp(),
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await purchasingRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert("there is already a purchasing with similar id,try again later");
  }
};
export const uploadCashIn = async (cashInObj) => {
  const cashInRef = firestore.doc(`dailyCashIn/${cashInObj.id}`);
  const snapShot = await cashInRef.get();
  if (!snapShot.exists) {
    try {
      await cashInRef.set({
        ...cashInObj,
        createdAt: createdAtStamp(),
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await cashInRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert("there is already a cash in with similar id,try again later");
  }
};
export const uploadExpressRatesParcel = async (countryObj) => {
  const countryRef = firestore.doc(`expressRatesParcel/${countryObj.country}`);
  const snapShot = await countryRef.get();
  if (!snapShot.exists) {
    try {
      await countryRef.set({
        ...countryObj,
        createdAt: createdAtStamp(),
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await countryRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert(
      "there is already a country with similar name, please change the country name and try again",
    );
  }
};
export const uploadD2DRates = async (freightType, country, typeObj) => {
  const productTypeRef = firestore.doc(
    `d2d-rates-${freightType}-${country}/${typeObj.id}`,
  );
  const snapShot = await productTypeRef.get();
  if (!snapShot.exists) {
    try {
      await productTypeRef.set({
        ...typeObj,
        createdAt: createdAtStamp(),
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await productTypeRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert(
      "there is already a product type with similar name, please change the product type name and try again",
    );
  }
};

export const uploadOrder = async (orderObj) => {
  const orderRef = firestore.doc(`orders/${orderObj.parcelId}`);
  const snapShot = await orderRef.get();
  if (!snapShot.exists) {
    try {
      await orderRef.set({
        ...orderObj,
        createdAt: createdAtStamp(),
      });
      const uploadedSnapShot = await orderRef.get();
      await markLotOrdersChanged(orderObj.lotNo);
      console.log(uploadedSnapShot.data());
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert(
      "there is already an order with similar parcel Id. please try again later.",
    );
  }
};
export const changeLotOrder = async (orderObj, previousParcelId) => {
  const orderRef = firestore.doc(`orders/${orderObj.parcelId}`);
  const snapShot = await orderRef.get();
  if (!snapShot.exists) {
    try {
      await orderRef.set({
        ...orderObj,
        // Moving a parcel to another lot rewrites it under a new id, but it
        // is the same parcel — keep its original age rather than making a
        // re-lotted parcel look like today's arrival.
        createdAt: orderObj.createdAt || createdAtStamp(),
      });
      const uploadedSnapShot = await orderRef.get();
      // The destination lot. The source lot is invalidated by the caller
      // (sales/changeLotModal.js), which is the only place still holding the
      // parcel's previous lotNo.
      await markLotOrdersChanged(orderObj.lotNo);
      console.log(uploadedSnapShot.data());
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert(
      "there is already an order with similar parcel Id. please try again later.",
    );
    return null;
  }
};

export const rechargeUser = async (rechargeObj) => {
  // first upload the recharge day in rechargeDays
  console.log(rechargeObj);
  try {
    const rechargeDayRef = firestore.doc(
      `rechargeDays/${rechargeObj.rechargedAt}`,
    );
    const snapShot = await rechargeDayRef.get();
    if (!snapShot.exists) {
      try {
        await rechargeDayRef.set({
          date: rechargeObj.rechargedAt,
          day: rechargeObj.day,
          total: rechargeObj.amount,
          createdAt: createdAtStamp(),
        });
      } catch (error) {
        alert(error);
      }
    } else {
      try {
        await rechargeDayRef.update({
          total: parseInt(snapShot.data().total) + parseInt(rechargeObj.amount),
        });
      } catch (error) {
        alert(error);
      }
    }

    // update rechargeRequest object to recharged or rejected
    const updatedRechargeRequestObj =
      await updateRechargeRequestStatus(rechargeObj);

    // upload full recharge object inside that day of recharge History
    const rechargeRef = firestore.doc(
      `rechargeHistory/${rechargeObj.rechargeId}`,
    );
    const recharge = await rechargeRef.get();
    if (!recharge.exists) {
      try {
        await rechargeRef.set({
          ...rechargeObj,
          createdAt: createdAtStamp(),
        });
      } catch (error) {
        alert(error);
      }
    } else {
      alert("an error occurred. please try again later");
    }

    // update user object with recharge balance
    const userRef = firestore.doc(`users/${rechargeObj.uid}`);

    try {
      const userSnapShot = await userRef.get();
      console.log(userSnapShot.data());
      await userRef.update({
        myWallet:
          parseInt(userSnapShot.data().myWallet) + parseInt(rechargeObj.amount),

        totalRecharge: userSnapShot.data().totalRecharge
          ? parseInt(userSnapShot.data().totalRecharge) +
            parseInt(rechargeObj.amount)
          : parseInt(rechargeObj.amount),
      });
      const rechargedUserSnapShot = await userRef.get();
      return rechargedUserSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } catch (error) {
    alert(error);
  }
};
export const rechargeUserFromRechargeRequest = async (rechargeObj) => {
  // first upload the recharge day in rechargeDays
  console.log(rechargeObj);
  try {
    const rechargeDayRef = firestore.doc(
      `rechargeDays/${rechargeObj.rechargedAt}`,
    );
    const snapShot = await rechargeDayRef.get();
    if (!snapShot.exists) {
      try {
        await rechargeDayRef.set({
          date: rechargeObj.rechargedAt,
          day: rechargeObj.day,
          total: rechargeObj.amount,
          createdAt: createdAtStamp(),
        });
      } catch (error) {
        alert(error);
      }
    } else {
      try {
        await rechargeDayRef.update({
          total: parseInt(snapShot.data().total) + parseInt(rechargeObj.amount),
        });
      } catch (error) {
        alert(error);
      }
    }

    // update rechargeRequest object to recharged or rejected
    const updatedRechargeRequestObj =
      await updateRechargeRequestStatus(rechargeObj);

    // upload full recharge object inside that day of recharge History
    const rechargeRef = firestore.doc(
      `rechargeHistory/${rechargeObj.rechargeId}`,
    );
    const recharge = await rechargeRef.get();
    if (!recharge.exists) {
      try {
        await rechargeRef.set({
          ...rechargeObj,
          createdAt: createdAtStamp(),
        });
      } catch (error) {
        alert(error);
      }
    } else {
      alert("an error occurred. please try again later");
    }

    // update user object with recharged balance
    const userRef = firestore.doc(`users/${rechargeObj.uid}`);

    try {
      const userSnapShot = await userRef.get();
      console.log(userSnapShot.data());
      await userRef.update({
        myWallet:
          parseInt(userSnapShot.data().myWallet) + parseInt(rechargeObj.amount),

        totalRecharge: userSnapShot.data().totalRecharge
          ? parseInt(userSnapShot.data().totalRecharge) +
            parseInt(rechargeObj.amount)
          : parseInt(rechargeObj.amount),
      });
      return updatedRechargeRequestObj;
    } catch (error) {
      alert(error);
    }
  } catch (error) {
    alert(error);
  }
};

export const updateOrder = async (orderObj) => {
  const orderRef = firestore.doc(`orders/${orderObj.parcelId}`);
  const order = await orderRef.get();
  try {
    if (order.data().invoiceStatus === "Paid") {
      const updatedSnapShot = await orderRef.get();

      return updatedSnapShot.data();
    } else {
      if (!orderObj.from) {
        orderObj.editApproved = true;
      }
      await orderRef.update({
        ...orderObj,
      });
      const updatedSnapShot = await orderRef.get();
      // Read back rather than trusting orderObj: this is a partial update, so
      // the stored lotNo is the authoritative one.
      await markLotOrdersChanged(
        (updatedSnapShot.data() && updatedSnapShot.data().lotNo) ||
          orderObj.lotNo,
      );

      return updatedSnapShot.data();
    }
  } catch (error) {
    alert(error);
  }
};
// Writes many order documents in chunked firestore.batch() commits instead
// of one `updateOrder()` round-trip per order (each of which is itself a
// read + write + read) — dramatically fewer network round-trips for a bulk
// operation like "set the rate on every parcel in this lot". Chunk size is
// deliberately smaller than Firestore's 500-op batch cap: this is driven by
// an interactive progress bar, not a background migration, so `onProgress`
// needs to fire in visible increments. Callers must exclude any order that
// shouldn't be touched (e.g. already Paid) themselves — unlike `updateOrder`,
// a batch `.update()` has no per-document "skip if already Paid" guard.
export const updateOrdersInBatches = async (
  orderObjArray,
  chunkSize,
  onProgress,
) => {
  const succeeded = [];
  const failed = [];

  for (let i = 0; i < orderObjArray.length; i += chunkSize) {
    const chunk = orderObjArray.slice(i, i + chunkSize);
    const batch = firestore.batch();
    chunk.forEach((orderObj) => {
      batch.update(firestore.doc(`orders/${orderObj.parcelId}`), {
        ...orderObj,
      });
    });

    let chunkSucceeded = [];
    let chunkFailed = [];
    try {
      await batch.commit();
      chunkSucceeded = chunk;
      succeeded.push(...chunk);
    } catch (error) {
      console.error("Batch update failed for chunk:", error);
      chunkFailed = chunk;
      failed.push(...chunk);
    }

    if (onProgress) {
      onProgress(
        succeeded.length + failed.length,
        orderObjArray.length,
        chunkSucceeded,
        chunkFailed,
      );
    }
  }

  // Once per distinct lot for the whole run, not once per order — a bulk
  // price update over hundreds of parcels in one lot is a single invalidation.
  // Only lots whose writes actually committed.
  await markLotsOrdersChanged(succeeded.map((orderObj) => orderObj.lotNo));

  return { succeeded, failed };
};

export const updateOrderDeliveryStatus = async (orderObj) => {
  const orderRef = firestore.doc(`orders/${orderObj.parcelId}`);
  const order = await orderRef.get();
  try {
    if (!orderObj.from) {
      orderObj.editApproved = true;
    }
    await orderRef.update({
      ...orderObj,
    });
    const updatedSnapShot = await orderRef.get();
    await markLotOrdersChanged(
      (updatedSnapShot.data() && updatedSnapShot.data().lotNo) ||
        orderObj.lotNo,
    );

    return updatedSnapShot.data();
  } catch (error) {
    alert(error);
  }
};

const getDay = () => {
  const t = new Date();
  const dayInDigit = t.getDay();
  let day;
  if (dayInDigit == 0) {
    day = "Sunday";
  }
  if (dayInDigit == 1) {
    day = "Monday";
  }
  if (dayInDigit == 2) {
    day = "Tuesday";
  }
  if (dayInDigit == 3) {
    day = "Wednesday";
  }
  if (dayInDigit == 4) {
    day = "Thursday";
  }
  if (dayInDigit == 5) {
    day = "Friday";
  }
  if (dayInDigit == 6) {
    day = "Saturday";
  }
  return day;
};

export const getMonthName = () => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const d = new Date();
  return `${monthNames[d.getMonth()]},${d.getFullYear()}`;
};

// export const makePayment = async (
//   total,
//   invoicesToPay,
//   currentUser,
//   admin,
//   parcelsArray,
//   paymentMethod
// ) => {
//   const res = await firestore.runTransaction(async (t) => {
//     //first create a payment object
//     const paymentObj = {
//       paymentId: Math.floor(Math.random() * Date.now()),
//       paidAt: new Date().toLocaleDateString("en-US").replaceAll("/", "-"),
//       amount: total,
//       paymentMethod,
//       paidInvoice: [...invoicesToPay],
//       approvedBy: admin.name,
//     };
//     console.log(paymentObj.paidAt);
//     // for transaction all reads should be done before all writes
//     const paymentDayRef = firestore.doc(`paymentDays/${paymentObj.paidAt}`);
//     const paymentDay = await t.get(paymentDayRef);

//     const paymentHistoryRef = firestore.doc(
//       `paymentHistory/${paymentObj.paymentId}`
//     );
//     const paymentHistory = await t.get(paymentHistoryRef);
//     // updatating the status invoiceStatus=Paid in parcelArray in admin
//     for (let i = 0; i < parcelsArray.length; i++) {
//       const orderRef = firestore.doc(`orders/${parcelsArray[i].parcelId}`);
//       let date = new Date();
//       let cashInObj = {
//         id: `${date.getTime().toString()}${i}`,
//         category: paymentMethod,
//         subCategory: `Shipping Mark-${parcelsArray[i].shippingMark}`,
//         month: getMonthName(),
//         date: date.toLocaleDateString("en-GB"),
//         note: `Parcel Id-${parcelsArray[i].lotNo}-${parcelsArray[i].cartonNo}`,
//         amount: parcelsArray[i].finalTotal,
//         receiveBy: admin.name || currentUser.displayName,
//         status: "pending",
//         unEditable: true,
//       };
//       console.log(cashInObj);
//       const cashInRef = firestore.doc(`dailyCashIn/${cashInObj.id}`);

//       t.set(cashInRef, {
//         ...cashInObj,
//       });

//       t.update(orderRef, {
//         ...parcelsArray[i],
//         invoiceStatus: "Paid",
//       });
//     }

//     // make all writes
//     // make a payment in paymentdays
//     console.log(paymentDay.data());
//     const day = getDay();
//     if (!paymentDay.exists) {
//       t.set(paymentDayRef, {
//         date: paymentObj.paidAt,
//         total: total,
//         day,
//       });
//     } else {
//       t.update(paymentDayRef, {
//         total: paymentDay.data().total + total,
//       });
//     }
//     console.log(paymentDay.data());

//     // make a payment in paymentHistory

//     console.log(paymentHistory.data());
//     if (!paymentHistory.exists) {
//       t.set(paymentHistoryRef, {
//         Email: currentUser
//           ? currentUser.email && currentUser.email
//           : admin.email,
//         Name: currentUser
//           ? currentUser.displayName && currentUser.displayName
//           : admin.name,
//         Id: currentUser ? currentUser.userId : "admin",
//         uid: currentUser ? currentUser.uid : admin.adminId,
//         Mobile: currentUser
//           ? currentUser.mobileNo
//             ? currentUser.mobileNo
//             : ""
//           : admin.mobileNo,
//         ...paymentObj,
//         day,
//       });
//     } else {
//       alert("Your paymentId already exist. please try again later.");
//     }
//     console.log(paymentHistory.data());

//     const newArray = parcelsArray.map((parcel) => {
//       return { ...parcel, invoiceStatus: "Paid" };
//     });

//     console.log(parcelsArray[0]);
//     console.log(newArray[0]);
//     return newArray;
//   });
//   return res;
// };
// jodi uporer ta fail kore shekhetre batch kore korte hobe
// Writes every order/dailyCashIn/paymentDays/paymentHistory change for a
// payment onto a caller-supplied, uncommitted batch — shared by makePayment
// (used from /pay-invoice) and approvePaymentRequest (used from
// /payment-request, which needs the payment-request status flip in the
// *same* atomic write as the money, see approvePaymentRequest below). The
// caller is responsible for calling batch.commit().
const addPaymentWritesToBatch = async (
  batch,
  total,
  invoicesToPay,
  currentUser,
  admin,
  parcelsArray,
  paymentMethod,
  extraOrderFields = {},
) => {
  const paymentObj = {
    paymentId: Math.floor(Math.random() * Date.now()),
    paidAt: new Date().toLocaleDateString("en-US").replaceAll("/", "-"),
    amount: total,
    paymentMethod,
    paidInvoice: [...invoicesToPay],
    approvedBy: admin.name,
    createdAt: createdAtStamp(),
  };

  // Read data before starting the batch
  const paymentDayRef = firestore.doc(`paymentDays/${paymentObj.paidAt}`);
  const paymentDay = await paymentDayRef.get();
  const paymentHistoryRef = firestore.doc(
    `paymentHistory/${paymentObj.paymentId}`,
  );
  const paymentHistory = await paymentHistoryRef.get();

  // Update orders and dailyCashIn in a batch
  for (let i = 0; i < parcelsArray.length; i++) {
    // Never touch an invoice with a missing/invalid weight, rate, or total —
    // it must not be marked Paid or written into dailyCashIn with a NaN
    // amount. Its existing status is left exactly as it was.
    if (
      isNaN(parseFloat(parcelsArray[i].grossWeight)) ||
      isNaN(parseFloat(parcelsArray[i].ratePerKg)) ||
      isNaN(parseFloat(parcelsArray[i].finalTotal))
    ) {
      continue;
    }

    let date = new Date();
    const orderRef = firestore.doc(`orders/${parcelsArray[i].parcelId}`);
    const cashInObj = {
      id: `${date.getTime().toString()}${i}`,
      category: paymentMethod,
      subCategory: `Shipping Mark-${parcelsArray[i].shippingMark}`,
      month: getMonthName(),
      date: date.toLocaleDateString("en-GB"),
      note: `Parcel Id-${parcelsArray[i].lotNo}-${parcelsArray[i].cartonNo}`,
      amount: parcelsArray[i].finalTotal,
      receiveBy: admin.name || currentUser.displayName,
      status: "pending",
      unEditable: true,
    };
    const cashInRef = firestore.doc(`dailyCashIn/${cashInObj.id}`);

    batch.update(orderRef, {
      ...parcelsArray[i],
      invoiceStatus: "Paid",
      ...extraOrderFields,
    });
    batch.set(cashInRef, {
      ...cashInObj,
    });
  }

  // Update paymentDays and paymentHistory in the batch
  const day = getDay();
  if (!paymentDay.exists) {
    batch.set(paymentDayRef, {
      date: paymentObj.paidAt,
      total: total,
      day: getDay(),
      createdAt: createdAtStamp(),
    });
  } else {
    batch.update(paymentDayRef, {
      total: paymentDay.data().total + total,
    });
  }

  if (!paymentHistory.exists) {
    batch.set(paymentHistoryRef, {
      // ... your paymentHistory object
      Email: currentUser
        ? currentUser.email
          ? currentUser.email
          : admin.email
        : admin.email,
      Name: currentUser
        ? currentUser.displayName
          ? currentUser.displayName
          : admin.name
        : admin.name,
      Id: currentUser ? currentUser.userId : "admin",
      uid: currentUser ? currentUser.uid : admin.adminId,
      Mobile: currentUser
        ? currentUser.mobileNo
          ? currentUser.mobileNo
          : ""
        : admin.mobileNo,
      ...paymentObj,
      day,
      createdAt: createdAtStamp(),
    });
  } else {
    // Handle the case where paymentHistory already exists
    alert("Your paymentId already exist. please try again later.");
  }

  const validParcels = parcelsArray
    .filter(
      (parcel) =>
        !isNaN(parseFloat(parcel.grossWeight)) &&
        !isNaN(parseFloat(parcel.ratePerKg)) &&
        !isNaN(parseFloat(parcel.finalTotal)),
    )
    .map((parcel) => {
      return { ...parcel, invoiceStatus: "Paid" };
    });

  return validParcels;
};

export const makePayment = async (
  total,
  invoicesToPay,
  currentUser,
  admin,
  parcelsArray,
  paymentMethod,
) => {
  const batch = firestore.batch();
  const validParcels = await addPaymentWritesToBatch(
    batch,
    total,
    invoicesToPay,
    currentUser,
    admin,
    parcelsArray,
    paymentMethod,
  );
  await batch.commit();
  // After the commit, and only for the parcels actually written — a payment
  // rewrites finalTotal/ratePerKg/grossWeight from client state, so the lot's
  // totals have to be recomputed. One invalidation per distinct lot.
  const paidLotNos = validParcels.map((parcel) => parcel.lotNo);
  await markLotsOrdersChanged(paidLotNos);
  // This payment may have been the last invoice outstanding on its lot.
  await recomputeLotsDelivered(paidLotNos);
  return validParcels;
};

// Approving a payment request used to be three separate, non-atomic write
// phases: makePayment()'s own batch, then an unbatched Promise.all flipping
// each order's `paymentRequest` field, then a separate write to the
// paymentRequest doc's `status`. If anything failed between phases, the
// money/Paid-invoice writes from phase one had already committed while the
// request stayed "pending" — leaving it re-approvable (and re-payable) from
// the UI. This puts every write from all three phases on one batch, so the
// payment and the request's status change together or not at all.
export const approvePaymentRequest = async (
  total,
  paymentRequestObj,
  currentUser,
  admin,
  validOrders,
  paymentMethod,
) => {
  const batch = firestore.batch();
  // Only flip the `paymentRequest` field on orders that were *actually*
  // paid (validOrders) — the old code flipped it for every invoice on the
  // request, including ones skipped for bad weight/rate data that never
  // got paid at all. Passed as extraOrderFields so it merges into the same
  // update() call the helper already makes per order, instead of a second
  // separate update() on the same document within this batch.
  const validParcels = await addPaymentWritesToBatch(
    batch,
    total,
    paymentRequestObj.paidInvoices,
    currentUser,
    admin,
    validOrders,
    paymentMethod,
    { paymentRequest: "recharged" },
  );

  const paymentRequestRef = firestore.doc(
    `paymentRequest/${paymentRequestObj.paymentId}`,
  );
  batch.update(paymentRequestRef, { status: "recharged" });

  await batch.commit();
  // Same reasoning as makePayment above.
  const paidLotNos = validParcels.map((parcel) => parcel.lotNo);
  await markLotsOrdersChanged(paidLotNos);
  await recomputeLotsDelivered(paidLotNos);

  const updatedPaymentRequestSnap = await paymentRequestRef.get();
  return {
    orders: validParcels,
    paymentRequest: updatedPaymentRequestSnap.data(),
  };
};

// export const makePaymentP2p = async (
//   total,
//   invoicesToPay,
//   currentUser,
//   admin,
//   paymentMethod
// ) => {
//   const res = await firestore.runTransaction(async (t) => {
//     //first create a payment object
//     const paymentObj = {
//       paymentId: Math.floor(Math.random() * Date.now()),
//       paidAt: new Date().toLocaleDateString("en-US").replaceAll("/", "-"),
//       amount: total,
//       paymentMethod,
//       paidInvoice: [...invoicesToPay],
//       approvedBy: admin.name,
//     };

//     // for transaction all reads should be done before all writes
//     const paymentDayRef = firestore.doc(`paymentDays/${paymentObj.paidAt}`);
//     const paymentDay = await t.get(paymentDayRef);

//     const paymentHistoryRef = firestore.doc(
//       `paymentHistory/${paymentObj.paymentId}`
//     );
//     const paymentHistory = await t.get(paymentHistoryRef);
//     // updatating the status invoiceStatus=Paid in parcelArray in admin
//     for (let i = 0; i < invoicesToPay.length; i++) {
//       const p2pRef = firestore.doc(`p2p/${invoicesToPay[i].id}`);
//       let date = new Date();
//       let cashInObj = {
//         id: `${date.getTime().toString()}${i}`,
//         category: paymentMethod,
//         subCategory: `P2P`,
//         month: getMonthName(),
//         date: date.toLocaleDateString("en-GB"),
//         note: `${invoicesToPay[i].bookingId}`,
//         amount: invoicesToPay[i].shippingCost,
//         receiveBy: admin.name,
//         status: "pending",
//         unEditable: true,
//       };
//       console.log(cashInObj);
//       const cashInRef = firestore.doc(`dailyCashIn/${cashInObj.id}`);

//       t.set(cashInRef, {
//         ...cashInObj,
//       });

//       t.update(p2pRef, {
//         paymentStatus: "Paid",
//         paymentDate: date.toLocaleDateString("en-GB"),
//       });
//     }

//     // make all writes
//     // make a payment in paymentdays
//     console.log(paymentDay.data());
//     const day = getDay();
//     if (!paymentDay.exists) {
//       t.set(paymentDayRef, {
//         date: paymentObj.paidAt,
//         total: total,
//         day,
//       });
//     } else {
//       t.update(paymentDayRef, {
//         total: paymentDay.data().total + total,
//       });
//     }
//     console.log(paymentDay.data());

//     // make a payment in paymentHistory

//     console.log(paymentHistory.data());
//     if (!paymentHistory.exists) {
//       t.set(paymentHistoryRef, {
//         Email: currentUser
//           ? currentUser.email && currentUser.email
//           : admin.email,
//         Name: currentUser
//           ? currentUser.displayName && currentUser.displayName
//           : admin.name,
//         Id: currentUser ? currentUser.userId : "admin",
//         uid: currentUser ? currentUser.uid : admin.adminId,
//         Mobile: currentUser
//           ? currentUser.mobileNo
//             ? currentUser.mobileNo
//             : ""
//           : admin.mobileNo,
//         ...paymentObj,
//         day,
//       });
//     } else {
//       alert("Your paymentId already exist. please try again later.");
//     }
//     console.log(paymentHistory.data());

//     const newArray = invoicesToPay.map((booking) => {
//       return { ...booking, paymentStatus: "Paid" };
//     });

//     return newArray;
//   });
//   return res;
// };

// Uses a transaction (not a plain batch) because the paymentDays/{date}
// total is derived from a freshly-read current value (current + total,
// computed in JS). A plain batch's read happens before the write commits,
// so two same-day payments (e.g. a Payment Request approval and a Pay
// Selected batch payment both landing today) can both read the same stale
// total and the commit that lands last silently drops the other's amount.
// A transaction detects that conflict and retries automatically.
const addPaymentP2pWritesToTransaction = (
  t,
  total,
  invoicesToPay,
  currentUser,
  admin,
  paymentMethod,
  paymentDaySnapShot,
  paymentHistorySnapShot,
  paymentObj,
) => {
  const paymentDayRef = firestore.doc(`paymentDays/${paymentObj.paidAt}`);
  const paymentHistoryRef = firestore.doc(
    `paymentHistory/${paymentObj.paymentId}`,
  );

  // Update invoice status and create daily cash-in entries
  for (let i = 0; i < invoicesToPay.length; i++) {
    // Never touch a booking with a missing/invalid shipping cost or weight —
    // it must not be marked Paid or written into dailyCashIn with a NaN
    // amount. Its existing status is left exactly as it was.
    if (
      isNaN(parseInt(invoicesToPay[i].shippingCost)) ||
      isNaN(parseFloat(invoicesToPay[i].totalWeight))
    ) {
      continue;
    }

    const p2pRef = firestore.doc(`p2p/${invoicesToPay[i].id}`);
    let date = new Date();
    let cashInObj = {
      id: `${date.getTime().toString()}${i}`,
      category: paymentMethod,
      subCategory: `P2P`,
      month: getMonthName(),
      date: date.toLocaleDateString("en-GB"),
      note: `${invoicesToPay[i].bookingId}`,
      amount: invoicesToPay[i].shippingCost,
      receiveBy: admin.name,
      status: "pending",
      unEditable: true,
    };
    const cashInRef = firestore.doc(`dailyCashIn/${cashInObj.id}`);

    // Update p2p document
    t.update(p2pRef, {
      paymentStatus: "Paid",
      paymentDate: date.toLocaleDateString("en-GB"),
    });

    // Set daily cash-in document
    t.set(cashInRef, {
      ...cashInObj,
    });
  }

  // Update paymentDay document
  const day = getDay();
  if (!paymentDaySnapShot.exists) {
    t.set(paymentDayRef, {
      date: paymentObj.paidAt,
      total: total,
      day,
      createdAt: createdAtStamp(),
    });
  } else {
    t.update(paymentDayRef, {
      total: paymentDaySnapShot.data().total + total,
    });
  }

  // Update paymentHistory document
  if (!paymentHistorySnapShot.exists) {
    t.set(paymentHistoryRef, {
      Email: currentUser ? currentUser.email && currentUser.email : admin.email,
      Name: currentUser
        ? currentUser.displayName && currentUser.displayName
        : admin.name,
      Id: currentUser ? currentUser.userId : "admin",
      uid: currentUser ? currentUser.uid : admin.adminId,
      Mobile: currentUser
        ? currentUser.mobileNo
          ? currentUser.mobileNo
          : ""
        : admin.mobileNo,
      ...paymentObj,
      day,
      createdAt: createdAtStamp(),
    });
  } else {
    alert("Your paymentId already exist. please try again later.");
  }

  return invoicesToPay
    .filter(
      (booking) =>
        !isNaN(parseInt(booking.shippingCost)) &&
        !isNaN(parseFloat(booking.totalWeight)),
    )
    .map((booking) => {
      return { ...booking, paymentStatus: "Paid" };
    });
};

export const makePaymentP2p = async (
  total,
  invoicesToPay,
  currentUser,
  admin,
  paymentMethod,
) => {
  const paymentObj = {
    paymentId: Math.floor(Math.random() * Date.now()),
    paidAt: new Date().toLocaleDateString("en-US").replaceAll("/", "-"),
    amount: total,
    paymentMethod,
    paidInvoice: [...invoicesToPay],
    approvedBy: admin.name,
    createdAt: createdAtStamp(),
  };
  const paymentDayRef = firestore.doc(`paymentDays/${paymentObj.paidAt}`);
  const paymentHistoryRef = firestore.doc(
    `paymentHistory/${paymentObj.paymentId}`,
  );

  try {
    let newArray;
    await firestore.runTransaction(async (t) => {
      const paymentDaySnapShot = await t.get(paymentDayRef);
      const paymentHistorySnapShot = await t.get(paymentHistoryRef);
      newArray = addPaymentP2pWritesToTransaction(
        t,
        total,
        invoicesToPay,
        currentUser,
        admin,
        paymentMethod,
        paymentDaySnapShot,
        paymentHistorySnapShot,
        paymentObj,
      );
    });
    return newArray;
  } catch (error) {
    alert(error);
    return false;
  }
};

// Same atomicity guarantee as approvePaymentRequestSourcing/
// approvePaymentRequestExpress: the money writes and the payment request's
// own status flip happen in one transaction, so a failure partway through
// can never leave the invoices paid but the request still "pending" (or
// vice versa).
export const approvePaymentRequestP2p = async (
  total,
  paymentRequestObj,
  currentUser,
  admin,
  paymentMethod,
) => {
  const paymentObj = {
    paymentId: Math.floor(Math.random() * Date.now()),
    paidAt: new Date().toLocaleDateString("en-US").replaceAll("/", "-"),
    amount: total,
    paymentMethod,
    paidInvoice: [...paymentRequestObj.paidInvoices],
    approvedBy: admin.name,
    createdAt: createdAtStamp(),
  };
  const paymentDayRef = firestore.doc(`paymentDays/${paymentObj.paidAt}`);
  const paymentHistoryRef = firestore.doc(
    `paymentHistory/${paymentObj.paymentId}`,
  );
  const paymentRequestRef = firestore.doc(
    `paymentRequestP2p/${paymentRequestObj.paymentId}`,
  );

  try {
    let orders;
    await firestore.runTransaction(async (t) => {
      const paymentDaySnapShot = await t.get(paymentDayRef);
      const paymentHistorySnapShot = await t.get(paymentHistoryRef);
      orders = addPaymentP2pWritesToTransaction(
        t,
        total,
        paymentRequestObj.paidInvoices,
        currentUser,
        admin,
        paymentMethod,
        paymentDaySnapShot,
        paymentHistorySnapShot,
        paymentObj,
      );
      t.update(paymentRequestRef, { status: "Paid" });
    });
    const updatedPaymentRequestSnap = await paymentRequestRef.get();
    return { orders, paymentRequest: updatedPaymentRequestSnap.data() };
  } catch (error) {
    alert(error);
    return false;
  }
};

// export const makePaymentSourcing = async (
//   total,
//   invoicesToPay,
//   currentUser,
//   admin,
//   parcelsArray,
//   paymentMethod
// ) => {
//   const res = await firestore.runTransaction(async (t) => {
//     //first create a payment object
//     const paymentObj = {
//       paymentId: Math.floor(Math.random() * Date.now()),
//       paidAt: new Date().toLocaleDateString("en-US").replaceAll("/", "-"),
//       amount: total,
//       paymentMethod,
//       paidInvoice: [...invoicesToPay],
//       approvedBy: admin.name,
//     };

//     // for transaction all reads should be done before all writes
//     const paymentDayRef = firestore.doc(`paymentDays/${paymentObj.paidAt}`);
//     const paymentDay = await t.get(paymentDayRef);

//     const paymentHistoryRef = firestore.doc(
//       `paymentHistory/${paymentObj.paymentId}`
//     );
//     const paymentHistory = await t.get(paymentHistoryRef);
//     // updatating the status invoiceStatus=Paid in parcelArray in admin
//     for (let i = 0; i < parcelsArray.length; i++) {
//       if (parcelsArray[i].category === "sold-products") {
//         const orderRef = firestore.doc(
//           `${parcelsArray[i].category}/${parcelsArray[i].paymentId}`
//         );
//         let date = new Date();
//         let cashInObj = {
//           id: `${date.getTime().toString()}${i}`,
//           category: paymentMethod,
//           subCategory: `${parcelsArray[i].category}`,
//           month: getMonthName(),
//           date: date.toLocaleDateString("en-GB"),
//           note: `Invoice No- ${parcelsArray[i].id || parcelsArray[i].paymentId}
//           Date:- ${parcelsArray[i].date}`,
//           amount:
//             parseInt(parcelsArray[i].totalBill) -
//               (parseInt(parcelsArray[i].totalPaid) || 0) ||
//             parseInt(parcelsArray[i].amount) *
//               parseInt(parcelsArray[i].quantity) -
//               (parseInt(parcelsArray[i].totalPaid) || 0),
//           receiveBy: admin.name || currentUser.displayName,
//           status: "pending",
//           unEditable: true,
//         };
//         console.log(cashInObj);
//         const cashInRef = firestore.doc(`dailyCashIn/${cashInObj.id}`);

//         t.set(cashInRef, {
//           ...cashInObj,
//         });

//         t.update(orderRef, {
//           ...parcelsArray[i],
//           invoiceStatus: "Paid",
//         });
//       } else {
//         const orderRef = firestore.doc(
//           `${parcelsArray[i].category}/${parcelsArray[i].id}`
//         );
//         let date = new Date();
//         let cashInObj = {
//           id: `${date.getTime().toString()}${i}`,
//           category: `${parcelsArray[i].category}`,
//           subCategory: paymentMethod,
//           month: getMonthName(),
//           date: date.toLocaleDateString("en-GB"),
//           note: `Invoice No- ${parcelsArray[i].id || parcelsArray[i].paymentId}
//           Date:- ${parcelsArray[i].date}`,
//           amount:
//             parseInt(parcelsArray[i].totalBill) -
//               (parseInt(parcelsArray[i].totalPaid) || 0) ||
//             parseInt(parcelsArray[i].amount) *
//               parseInt(parcelsArray[i].quantity) -
//               (parseInt(parcelsArray[i].totalPaid) || 0),
//           receiveBy: admin.name || currentUser.displayName,
//           status: "pending",
//           unEditable: true,
//         };
//         console.log(cashInObj);
//         const cashInRef = firestore.doc(`dailyCashIn/${cashInObj.id}`);

//         t.set(cashInRef, {
//           ...cashInObj,
//         });
//         if (parcelsArray[i].category == "purchasing") {
//           console.log(parcelsArray[i]);
//           console.log(parcelsArray[i].cashIns);
//           console.log(date.getTime().toString());
//           console.log(parcelsArray[i].totalBill);
//           console.log(parcelsArray[i].totalPaid);
//           console.log(date.toLocaleDateString("en-GB"));
//           console.log(admin.name);
//           t.update(orderRef, {
//             ...parcelsArray[i],
//             invoiceStatus: "Paid",
//             totalPaid: parseInt(parcelsArray[i].totalBill),
//             finalProfit:
//               parseInt(parcelsArray[i].totalBill) -
//               (parseInt(parcelsArray[i].buyingCost) || 0),
//             status: "approved",
//             cashIns:
//               parcelsArray[i].cashIns && parcelsArray[i].cashIns.length > 0
//                 ? [
//                     {
//                       id: date.getTime().toString(),
//                       amount:
//                         parseInt(parcelsArray[i].totalBill) -
//                         (parseInt(parcelsArray[i].totalPaid) || 0),
//                       date: date.toLocaleDateString("en-GB"),
//                       expenseBy: admin.name,
//                     },
//                     ...parcelsArray[i].cashIns,
//                   ]
//                 : [
//                     {
//                       id: date.getTime().toString(),
//                       amount:
//                         parseInt(parcelsArray[i].totalBill) -
//                         (parseInt(parcelsArray[i].totalPaid) || 0),
//                       date: date.toLocaleDateString("en-GB"),
//                       expenseBy: admin.name,
//                     },
//                   ],
//           });
//         } else {
//           t.update(orderRef, {
//             ...parcelsArray[i],
//             invoiceStatus: "Paid",
//             totalPaid: parseInt(parcelsArray[i].totalBill),
//             initialProfit:
//               parseInt(parcelsArray[i].totalBill) -
//               (parseInt(parcelsArray[i].buyingCost) || 0),
//             finalProfit:
//               parseInt(parcelsArray[i].totalBill) -
//               (parseInt(parcelsArray[i].buyingCost) || 0) -
//               (parseInt(parcelsArray[i].agentPaid) || 0),
//             status: "approved",
//           });
//         }
//       }
//     }

//     // make all writes
//     // make a payment in paymentdays
//     console.log(paymentDay.data());
//     const day = getDay();
//     if (!paymentDay.exists) {
//       t.set(paymentDayRef, {
//         date: paymentObj.paidAt,
//         total: total,
//         day,
//       });
//     } else {
//       t.update(paymentDayRef, {
//         total: paymentDay.data().total + total,
//       });
//     }
//     console.log(paymentDay.data());

//     // make a payment in paymentHistory

//     console.log(paymentHistory.data());
//     if (!paymentHistory.exists) {
//       t.set(paymentHistoryRef, {
//         Email: currentUser
//           ? currentUser.email && currentUser.email
//           : admin.email,
//         Name: currentUser
//           ? currentUser.displayName && currentUser.displayName
//           : admin.name,
//         Id: currentUser ? currentUser.userId : "admin",
//         uid: currentUser ? currentUser.uid : admin.adminId,
//         Mobile: currentUser
//           ? currentUser.mobileNo
//             ? currentUser.mobileNo
//             : ""
//           : admin.mobileNo,
//         ...paymentObj,
//         day,
//       });
//     } else {
//       alert("Your paymentId already exist. please try again later.");
//     }
//     console.log(paymentHistory.data());

//     const newArray = parcelsArray.map((parcel) => {
//       return { ...parcel, invoiceStatus: "Paid" };
//     });

//     console.log(parcelsArray[0]);
//     console.log(newArray[0]);
//     return newArray;
//   });
//   return res;
// };

// Writes onto a caller-supplied, uncommitted batch instead of creating and
// committing its own — lets makePaymentSourcing() and
// approvePaymentRequestSourcing() share this logic while each keeps its own
// atomic commit (see addPaymentWritesToBatch for the lot-based equivalent).
const addPaymentSourcingWritesToBatch = async (
  batch,
  total,
  invoicesToPay,
  currentUser,
  admin,
  parcelsArray,
  paymentMethod,
  extraOrderFields = {},
) => {
  const paymentObj = {
    paymentId: Math.floor(Math.random() * Date.now()),
    paidAt: new Date().toLocaleDateString("en-US").replaceAll("/", "-"),
    amount: total,
    paymentMethod,
    paidInvoice: [...invoicesToPay],
    approvedBy: admin.name,
    createdAt: createdAtStamp(),
  };

  // Get document references
  const paymentDayRef = firestore.doc(`paymentDays/${paymentObj.paidAt}`);
  const paymentHistoryRef = firestore.doc(
    `paymentHistory/${paymentObj.paymentId}`,
  );

  // Get document snapshots
  const paymentDaySnapShot = await paymentDayRef.get();
  const paymentHistorySnapShot = await paymentHistoryRef.get();

  // Update invoice status and create daily cash-in entries
  for (let i = 0; i < parcelsArray.length; i++) {
    // Never touch an invoice with a missing/invalid totalBill or
    // amount/quantity — it must not be marked Paid or written into
    // dailyCashIn with a NaN amount. Its existing status is left exactly as
    // it was.
    const dueAmount =
      (parcelsArray[i].totalBill ||
        parseInt(parcelsArray[i].amount) * parseInt(parcelsArray[i].quantity)) -
      (parcelsArray[i].totalPaid || 0);
    if (isNaN(dueAmount)) {
      continue;
    }

    let date = new Date();
    let cashInObj = {
      id: `${date.getTime().toString()}${i}`,
      category: paymentMethod,
      subCategory: `${parcelsArray[i].category}`,
      month: getMonthName(),
      date: date.toLocaleDateString("en-GB"),
      note: `Invoice No- ${parcelsArray[i].id || parcelsArray[i].paymentId}
          Date:- ${parcelsArray[i].date}`,
      amount:
        parseInt(parcelsArray[i].totalBill) -
          (parseInt(parcelsArray[i].totalPaid) || 0) ||
        parseInt(parcelsArray[i].amount) * parseInt(parcelsArray[i].quantity) -
          (parseInt(parcelsArray[i].totalPaid) || 0),
      receiveBy: admin.name || currentUser.displayName,
      status: "pending",
      unEditable: true,
    };
    const cashInRef = firestore.doc(`dailyCashIn/${cashInObj.id}`);

    if (parcelsArray[i].category === "sold-products") {
      const orderRef = firestore.doc(
        `${parcelsArray[i].category}/${parcelsArray[i].paymentId}`,
      );
      batch.set(cashInRef, {
        ...cashInObj,
      });
      batch.update(orderRef, {
        ...parcelsArray[i],
        invoiceStatus: "Paid",
        ...extraOrderFields,
      });
    } else {
      const orderRef = firestore.doc(
        `${parcelsArray[i].category}/${parcelsArray[i].id}`,
      );
      batch.set(cashInRef, {
        ...cashInObj,
      });
      if (parcelsArray[i].category == "purchasing") {
        batch.update(orderRef, {
          ...parcelsArray[i],
          invoiceStatus: "Paid",
          totalPaid: parseInt(parcelsArray[i].totalBill),
          finalProfit:
            parseInt(parcelsArray[i].totalBill) -
            (parseInt(parcelsArray[i].buyingCost) || 0),
          status: "approved",
          cashIns:
            parcelsArray[i].cashIns && parcelsArray[i].cashIns.length > 0
              ? [
                  {
                    id: date.getTime().toString(),
                    amount:
                      parseInt(parcelsArray[i].totalBill) -
                      (parseInt(parcelsArray[i].totalPaid) || 0),
                    date: date.toLocaleDateString("en-GB"),
                    expenseBy: admin.name,
                  },
                  ...parcelsArray[i].cashIns,
                ]
              : [
                  {
                    id: date.getTime().toString(),
                    amount:
                      parseInt(parcelsArray[i].totalBill) -
                      (parseInt(parcelsArray[i].totalPaid) || 0),
                    date: date.toLocaleDateString("en-GB"),
                    expenseBy: admin.name,
                  },
                ],
          ...extraOrderFields,
        });
      } else {
        batch.update(orderRef, {
          ...parcelsArray[i],
          invoiceStatus: "Paid",
          totalPaid: parseInt(parcelsArray[i].totalBill),
          initialProfit:
            parseInt(parcelsArray[i].totalBill) -
            (parseInt(parcelsArray[i].buyingCost) || 0),
          finalProfit:
            parseInt(parcelsArray[i].totalBill) -
            (parseInt(parcelsArray[i].buyingCost) || 0) -
            (parseInt(parcelsArray[i].agentPaid) || 0),
          status: "approved",
          ...extraOrderFields,
        });
      }
    }
  }

  // Update paymentDay document
  const day = getDay();
  if (!paymentDaySnapShot.exists) {
    batch.set(paymentDayRef, {
      date: paymentObj.paidAt,
      total: total,
      day,
      createdAt: createdAtStamp(),
    });
  } else {
    batch.update(paymentDayRef, {
      total: paymentDaySnapShot.data().total + total,
    });
  }

  // Update paymentHistory document
  if (!paymentHistorySnapShot.exists) {
    batch.set(paymentHistoryRef, {
      Email: currentUser ? currentUser.email && currentUser.email : admin.email,
      Name: currentUser
        ? currentUser.displayName && currentUser.displayName
        : admin.name,
      Id: currentUser ? currentUser.userId : "admin",
      uid: currentUser ? currentUser.uid : admin.adminId,
      Mobile: currentUser
        ? currentUser.mobileNo
          ? currentUser.mobileNo
          : ""
        : admin.mobileNo,
      ...paymentObj,
      day,
      createdAt: createdAtStamp(),
    });
  } else {
    alert("Your paymentId already exist. please try again later.");
  }

  return parcelsArray
    .filter((parcel) => {
      const dueAmount =
        (parcel.totalBill ||
          parseInt(parcel.amount) * parseInt(parcel.quantity)) -
        (parcel.totalPaid || 0);
      return !isNaN(dueAmount);
    })
    .map((parcel) => {
      return { ...parcel, invoiceStatus: "Paid" };
    });
};

export const makePaymentSourcing = async (
  total,
  invoicesToPay,
  currentUser,
  admin,
  parcelsArray,
  paymentMethod,
) => {
  const batch = firestore.batch();
  const validParcels = await addPaymentSourcingWritesToBatch(
    batch,
    total,
    invoicesToPay,
    currentUser,
    admin,
    parcelsArray,
    paymentMethod,
  );
  try {
    await batch.commit();
    return validParcels;
  } catch (error) {
    alert(error);
    return false;
  }
};

// Approve path for a Business Consultancy (Sourcing/Purchasing) Payment
// Request — same atomicity guarantee as approvePaymentRequest()/
// approvePaymentRequestExpress(): the money writes and the request's own
// status flip land in one batch/commit so a failure between them can't
// leave the cash-in recorded but the request still "pending" (which made
// its Approve button clickable again and risked a duplicate payment on
// retry — the exact bug reported for this page).
export const approvePaymentRequestSourcing = async (
  total,
  paymentRequestObj,
  currentUser,
  admin,
  parcelsArray,
  paymentMethod,
) => {
  const batch = firestore.batch();
  const validParcels = await addPaymentSourcingWritesToBatch(
    batch,
    total,
    paymentRequestObj.paidInvoices,
    currentUser,
    admin,
    parcelsArray,
    paymentMethod,
    { paymentRequest: "recharged" },
  );

  const paymentRequestRef = firestore.doc(
    `paymentRequestSourcing/${paymentRequestObj.paymentId}`,
  );
  batch.update(paymentRequestRef, { status: "recharged" });

  try {
    await batch.commit();
  } catch (error) {
    alert(error);
    return false;
  }

  const updatedPaymentRequestSnap = await paymentRequestRef.get();
  return {
    orders: validParcels,
    paymentRequest: updatedPaymentRequestSnap.data(),
  };
};

// export const makePaymentExpress = async (
//   total,
//   invoicesToPay,
//   currentUser,
//   admin,
//   parcelsArray,
//   paymentMethod
// ) => {
//   const res = await firestore.runTransaction(async (t) => {
//     //first create a payment object
//     const paymentObj = {
//       paymentId: Math.floor(Math.random() * Date.now()),
//       paidAt: new Date().toLocaleDateString("en-US").replaceAll("/", "-"),
//       amount: total,
//       paymentMethod,
//       paidInvoice: [...invoicesToPay],
//       approvedBy: admin.name,
//     };

//     // for transaction all reads should be done before all writes
//     const paymentDayRef = firestore.doc(
//       `paymentDaysExpress/${paymentObj.paidAt}`
//     );
//     const paymentDay = await t.get(paymentDayRef);
//     const paymentHistoryRef = firestore.doc(
//       `paymentHistoryExpress/${paymentObj.paymentId}`
//     );
//     const paymentHistory = await t.get(paymentHistoryRef);
//     // updatating the status invoiceStatus=Paid in parcelArray in admin
//     for (let i = 0; i < parcelsArray.length; i++) {
//       let booking = parcelsArray[i];
//       const orderRef = firestore.doc(`bookingRequest/${booking.bookingId}`);
//       let date = new Date();
//       let cashInObj = {
//         id: date.getTime().toString(),
//         category: paymentMethod,
//         subCategory: `Booking Id - ${booking.bookingId}`,
//         month: getMonthName(),
//         date: date.toLocaleDateString("en-GB"),
//         note: `${booking.weight ? `${booking.weight}Kg` : booking.parcelBox}`,
//         amount: booking.totalCost || booking.total,
//         receiveBy: admin.name || currentUser.displayName,
//         status: "pending",
//         unEditable: true,
//       };
//       console.log(cashInObj);
//       const cashInRef = firestore.doc(`dailyCashIn/${cashInObj.id}`);

//       t.set(cashInRef, {
//         ...cashInObj,
//       });

//       t.update(orderRef, {
//         ...booking,
//         paid: true,
//       });
//     }

//     // make all writes
//     // make a payment in paymentdays
//     console.log(paymentDay.data());
//     const day = getDay();
//     if (!paymentDay.exists) {
//       t.set(paymentDayRef, {
//         date: paymentObj.paidAt,
//         total: total,
//         day,
//       });
//     } else {
//       t.update(paymentDayRef, {
//         total: paymentDay.data().total + total,
//       });
//     }
//     console.log(paymentDay.data());

//     // make a payment in paymentHistory

//     console.log(paymentHistory.data());
//     if (!paymentHistory.exists) {
//       t.set(paymentHistoryRef, {
//         Email: currentUser
//           ? currentUser.email && currentUser.email
//           : admin.email,
//         Name: currentUser
//           ? currentUser.displayName && currentUser.displayName
//           : admin.name,
//         Id: currentUser ? currentUser.userId : "admin",
//         uid: currentUser ? currentUser.uid : admin.adminId,
//         Mobile: currentUser
//           ? currentUser.mobileNo
//             ? currentUser.mobileNo
//             : ""
//           : admin.mobileNo,
//         ...paymentObj,
//         day,
//       });
//     } else {
//       alert("Your paymentId already exist. please try again later.");
//     }
//     console.log(paymentHistory.data());

//     const newArray = parcelsArray.map((parcel) => {
//       return { ...parcel, invoiceStatus: "Paid" };
//     });

//     console.log(parcelsArray[0]);
//     console.log(newArray[0]);
//     return newArray;
//   });
//   return res;
// };

// Writes onto a caller-supplied, uncommitted batch instead of creating and
// committing its own — lets makePaymentExpress() and
// approvePaymentRequestExpress() share this logic while each keeps its own
// atomic commit (see addPaymentWritesToBatch for the lot-based equivalent).
const addPaymentExpressWritesToBatch = async (
  batch,
  total,
  invoicesToPay,
  currentUser,
  admin,
  parcelsArray,
  paymentMethod,
  extraOrderFields = {},
) => {
  const paymentObj = {
    paymentId: Math.floor(Math.random() * Date.now()),
    paidAt: new Date().toLocaleDateString("en-US").replaceAll("/", "-"),
    amount: total,
    paymentMethod,
    paidInvoice: [...invoicesToPay],
    approvedBy: admin.name,
    createdAt: createdAtStamp(),
  };

  // Get document references
  const paymentDayRef = firestore.doc(
    `paymentDaysExpress/${paymentObj.paidAt}`,
  );
  const paymentHistoryRef = firestore.doc(
    `paymentHistoryExpress/${paymentObj.paymentId}`,
  );

  // Get document snapshots
  const paymentDaySnapShot = await paymentDayRef.get();
  const paymentHistorySnapShot = await paymentHistoryRef.get();

  // Update invoice status and create daily cash-in entries
  for (let i = 0; i < parcelsArray.length; i++) {
    let booking = parcelsArray[i];
    const orderRef = firestore.doc(`bookingRequest/${booking.bookingId}`);
    let date = new Date();
    let cashInObj = {
      id: date.getTime().toString(),
      category: paymentMethod,
      subCategory: `Booking Id - ${booking.bookingId}`,
      month: getMonthName(),
      date: date.toLocaleDateString("en-GB"),
      note: `${booking.weight ? `${booking.weight}Kg` : booking.parcelBox}`,
      amount: booking.totalCost || booking.total,
      receiveBy: admin.name || currentUser.displayName,
      status: "pending",
      unEditable: true,
    };
    const cashInRef = firestore.doc(`dailyCashIn/${cashInObj.id}`);

    batch.set(cashInRef, {
      ...cashInObj,
    });

    batch.update(orderRef, {
      ...booking,
      paid: true,
      ...extraOrderFields,
    });
  }

  // Update paymentDay document
  const day = getDay();
  if (!paymentDaySnapShot.exists) {
    batch.set(paymentDayRef, {
      date: paymentObj.paidAt,
      total: total,
      day,
      createdAt: createdAtStamp(),
    });
  } else {
    batch.update(paymentDayRef, {
      total: paymentDaySnapShot.data().total + total,
    });
  }

  // Update paymentHistory document
  if (!paymentHistorySnapShot.exists) {
    batch.set(paymentHistoryRef, {
      Email: currentUser ? currentUser.email && currentUser.email : admin.email,
      Name: currentUser
        ? currentUser.displayName && currentUser.displayName
        : admin.name,
      Id: currentUser ? currentUser.userId : "admin",
      uid: currentUser ? currentUser.uid : admin.adminId,
      Mobile: currentUser
        ? currentUser.mobileNo
          ? currentUser.mobileNo
          : ""
        : admin.mobileNo,
      ...paymentObj,
      day,
      createdAt: createdAtStamp(),
    });
  } else {
    alert("Your paymentId already exist. please try again later.");
  }
};

export const makePaymentExpress = async (
  total,
  invoicesToPay,
  currentUser,
  admin,
  parcelsArray,
  paymentMethod,
) => {
  const batch = firestore.batch();
  await addPaymentExpressWritesToBatch(
    batch,
    total,
    invoicesToPay,
    currentUser,
    admin,
    parcelsArray,
    paymentMethod,
  );

  try {
    await batch.commit();
    const newArray = parcelsArray.map((parcel) => {
      return { ...parcel, invoiceStatus: "Paid" };
    });
    return newArray;
  } catch (error) {
    alert(error);
    return false;
  }
};

// Approve path for a Payment Request Express — same atomicity guarantee as
// approvePaymentRequest(): the money writes and the request's own status
// flip land in one batch/commit so a failure between them can't leave the
// cash-in recorded but the request still "pending" (which made its Approve
// button clickable again and risked a duplicate payment on retry).
export const approvePaymentRequestExpress = async (
  total,
  paymentRequestObj,
  currentUser,
  admin,
  parcelsArray,
  paymentMethod,
) => {
  const batch = firestore.batch();
  // paymentRequest: "recharged" is passed as extraOrderFields so it merges
  // into the same update() call the helper already makes per booking,
  // instead of a second separate update() on the same document within this
  // batch.
  await addPaymentExpressWritesToBatch(
    batch,
    total,
    paymentRequestObj.paidInvoices,
    currentUser,
    admin,
    parcelsArray,
    paymentMethod,
    { paymentRequest: "recharged" },
  );
  const paymentRequestRef = firestore.doc(
    `paymentRequestExpress/${paymentRequestObj.paymentId}`,
  );
  batch.update(paymentRequestRef, { status: "recharged" });
  await batch.commit();

  const newArray = parcelsArray.map((parcel) => ({
    ...parcel,
    invoiceStatus: "Paid",
  }));
  const updatedPaymentRequestSnap = await paymentRequestRef.get();
  return { orders: newArray, paymentRequest: updatedPaymentRequestSnap.data() };
};

export const updateOrderInInvoice = async (orderObj) => {
  const orderRef = firestore.doc(`orders/${orderObj.parcelId}`);
  const order = await orderRef.get();

  try {
    if (order.data().ratePerKg) {
      let parcelObj = order.data();
      const insurance =
        parcelObj.insurance && parcelObj.insurance !== ""
          ? parseInt(parseInt(parcelObj.productsValue) * (3 / 100))
          : 0;
      console.log(insurance);
      const total = parseInt(
        parseInt(parcelObj.ratePerKg) * parseFloat(orderObj.grossWeight),
      );
      const qcCheck =
        parcelObj.qcCheck && parcelObj.qcCheck === "true" ? 100 : 0;

      let orderObj2 = {
        ...orderObj,
        discountInvoice: 0,
        otherCharges: 0,
        ratePerKg: order.data().ratePerKg,
        invoiceGenerated: true,
        // Editing an already-Paid parcel (only reachable in development) must
        // not silently knock it back to Not Paid.
        invoiceStatus: parcelObj.invoiceStatus === "Paid" ? "Paid" : "Not Paid",
        total: parseInt(
          parseInt(parcelObj.ratePerKg) * parseFloat(orderObj.grossWeight),
        ),
        insurance,
        subTotal:
          parseInt(insurance) +
          parseInt(total) +
          parseInt(orderObj.packagingCost ? orderObj.packagingCost : 0) +
          parseInt(parcelObj.deliveryCost ? parcelObj.deliveryCost : 0) +
          qcCheck,
        finalTotal:
          parseInt(insurance) +
          parseInt(total) +
          parseInt(orderObj.packagingCost ? orderObj.packagingCost : 0) +
          parseInt(parcelObj.deliveryCost ? parcelObj.deliveryCost : 0) +
          qcCheck,
      };
      await orderRef.update({
        ...orderObj2,
      });
    } else {
      await orderRef.update({
        ...orderObj,
      });
    }

    const updatedSnapShot = await orderRef.get();
    await markLotOrdersChanged(
      (updatedSnapShot.data() && updatedSnapShot.data().lotNo) ||
        orderObj.lotNo,
    );
    return updatedSnapShot.data();
  } catch (error) {
    alert(error);
  }
};

// Development-only escape hatch for flipping an order between Paid / Not Paid
// while testing locally. It writes invoiceStatus straight onto the order doc,
// bypassing the "Paid orders are frozen" guard in updateOrder(). It deliberately
// does NOT create or remove any payment history, dailyCashIn or wallet records —
// those only ever come from the real payment flow. No-op in production.
export const setInvoiceStatusDev = async (parcelId, invoiceStatus) => {
  if (process.env.NODE_ENV === "production") return null;
  const orderRef = firestore.doc(`orders/${parcelId}`);
  try {
    await orderRef.update({ invoiceStatus });
    const updatedSnapShot = await orderRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    alert(error);
    return null;
  }
};

export const uploadImageQcCheck = async (file) => {
  const imageRef = storage.ref(`qcCheck/${file.name}`);
  try {
    await imageRef.put(file);
    var imgUrl = [];
    await imageRef.getDownloadURL().then((url) => {
      console.log(url);
      imgUrl.push(url);
    });

    return imgUrl[0];
  } catch (error) {
    return null;
  }
};

export const uploadImageIntro = async (file) => {
  const imageRef = storage.ref(`intro/${file.name}`);
  try {
    await imageRef.put(file);
    var imgUrl = [];
    await imageRef.getDownloadURL().then((url) => {
      console.log(url);
      imgUrl.push(url);
    });

    return imgUrl[0];
  } catch (error) {
    return null;
  }
};

export const updateUserStatus = async (userObj) => {
  const userRef = firestore.doc(`users/${userObj.uid}`);
  try {
    const snapShot = await userRef.get();
    console.log(snapShot.data());
    await userRef.update({
      status: userObj.status,
    });
    const updatedSnapShot = await userRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    alert(error);
  }
};

export const updateAdminStatus = async (adminObj) => {
  const adminRef = firestore.doc(`admins/${adminObj.adminId}`);
  try {
    const snapShot = await adminRef.get();
    console.log(snapShot.data());
    await adminRef.update({
      status: adminObj.status,
    });
    const updatedSnapShot = await adminRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    alert(error);
  }
};

// Backs /admins/detail-admin/:adminId. The list page reads admins out of
// redux, but a direct link or a hard reload lands before that preload
// finishes, so the detail page fetches the one document it needs.
//
// The id comes back on the document because the auth listener and
// getAllAdmins disagree about where it lives (`id` vs `adminId`), and a
// missing document is `null` rather than the undefined `.data()` returns —
// otherwise "no such admin" is indistinguishable from a read that failed.
// Errors propagate so the caller can offer a retry instead of showing a
// not-found page for what was really a network problem.
export const getSingleAdmin = async (uid) => {
  const adminRef = firestore.doc(`admins/${uid}`);
  const snapShot = await adminRef.get();
  if (!snapShot.exists) return null;
  return { adminId: snapShot.id, id: snapShot.id, ...snapShot.data() };
};

export const uploadProductTax = async (productObj) => {
  const productRef = firestore.doc(`taxes/${productObj.id}`);
  const snapShot = await productRef.get();
  const newProductObj = { ...productObj };
  if (!snapShot.exists) {
    try {
      productRef.set({
        ...newProductObj,
        createdAt: createdAtStamp(),
      });
    } catch (error) {
      alert(error);
    }
  } else {
    alert(
      "there is already a product with this given prodcut Id, please change the product Id and upload again",
    );
  }
};

export const uploadProduct = async (productObj) => {
  const productRef = firestore.doc(`stock-products/${productObj.id}`);
  const snapShot = await productRef.get();
  const newProductObj = { ...productObj };
  if (!snapShot.exists) {
    try {
      await productRef.set({
        ...newProductObj,
        createdAt: createdAtStamp(),
      });
      const updatedSnapShot = await productRef.get();
      return updatedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    try {
      await productRef.update({
        ...newProductObj,
      });
      const updatedSnapShot = await productRef.get();
      return updatedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  }
};

export const getAllProducts = async () => {
  const productsCollectionRef = firestore
    .collection("stock-products")
    .where("status", "==", "stock");
  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    alert(error);
  }
};

export const getAllSourcingPurchasingByTrackingNo = async (trackingNo) => {
  const sourcingsCollectionRef = firestore
    .collection("sourcing")
    .where("trackingNo", "==", trackingNo);
  const purchasingsCollectionRef = firestore
    .collection("purchasing")
    .where("trackingNo", "==", trackingNo);
  try {
    const sourcing = await sourcingsCollectionRef.get();
    const purchasing = await purchasingsCollectionRef.get();
    const productsArray = [];

    sourcing.forEach((doc) => {
      productsArray.push(doc.data());
    });
    purchasing.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    alert(error);
    return [];
  }
};
export const getAllOrdersByTrackingNo = async (trackingNo) => {
  const ordersCollectionRef = firestore
    .collection("orders")
    .where("trackingNo", "==", trackingNo);

  try {
    const orders = await ordersCollectionRef.get();
    const productsArray = [];
    orders.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    alert(error);
    return [];
  }
};
export const getAllOrdersByTrackingNo2 = async (trackingNo) => {
  const ordersCollectionRef = firestore
    .collection("orders")
    .where("trackingNo", "==", trackingNo);

  try {
    const orders = await ordersCollectionRef.get();
    const productsArray = [];
    orders.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    alert(error);
    return [];
  }
};
export const deletePreviousLotBill = async (lotNo) => {
  const billsCollectionRef = firestore
    .collection("cnfBills")
    .where("lotNumber", "==", lotNo);
  try {
    const bills = await billsCollectionRef.get();
    bills.forEach(async (doc) => {
      const billRef = firestore.doc(`cnfBills/${doc.data().id}`);
      await billRef.delete();
    });
  } catch (error) {
    alert(error);
  }
};
// export const getPreviousLotBill = async (lotNo) => {
//   const billsCollectionRef = firestore
//     .collection("cnfBills")
//     .where("lotNumber", "==", lotNo);
//   try {
//     const bills = await billsCollectionRef.get();
//     bills.forEach(async (doc) => {
//       const billRef = firestore.doc(`cnfBills/${doc.data().id}`);
//       await billRef.delete();
//     });
//   } catch (error) {
//     alert(error);
//   }
// };
export const getAllSoldProducts = async () => {
  const productsCollectionRef = firestore.collection("sold-products");

  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllSoldProductsMonthly = async (month) => {
  const productsCollectionRef = firestore
    .collection("sold-products")
    .where("month", "==", month);

  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    alert(error);
  }
};

export const getAllLots = async () => {
  const lotsCollectionRef = firestore.collection("lots");

  try {
    const lots = await lotsCollectionRef.orderBy("shipmentDate", "desc").get();
    const lotsArray = [];
    lots.forEach((doc) => {
      lotsArray.push(doc.data());
    });

    return lotsArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllDeliveredLots = async () => {
  const lotsCollectionRef = firestore
    .collection("lots")
    .where("allDelivered", "==", true);
  try {
    const lots = await lotsCollectionRef.orderBy("shipmentDate", "desc").get();
    const lotsArray = [];
    lots.forEach((doc) => {
      lotsArray.push(doc.data());
    });
    return lotsArray;
  } catch (error) {
    alert(error);
    return [];
  }
};

// The admin document's own id. Admins registered through the app store it as
// `adminId`, but the auth listener in allRoutes.js puts the doc id on `id` —
// so an admin created before `adminId` existed (or straight from the Firebase
// console) has only `id`. Writing to `admins/undefined` fails, and the failure
// used to come back as a null admin, which signs the panel out.
const adminDocId = (admin) => (admin && (admin.adminId || admin.id)) || null;

export const uploadImage = async (currentAdmin, file) => {
  const adminId = adminDocId(currentAdmin);
  if (!adminId || !file) {
    console.error("uploadImage: missing admin id or file", currentAdmin);
    return null;
  }
  // Namespaced per admin: on the old flat `users/<filename>` path two admins
  // who both uploaded "profile.jpg" overwrote each other's photo.
  const imageRef = storage.ref(`admins/${adminId}/${file.name}`);
  try {
    await imageRef.put(file);
    const imgUrl = await imageRef.getDownloadURL();
    const adminRef = firestore.doc(`admins/${adminId}`);
    // Not awaiting this update meant the re-read below could race it and
    // return the document without the new photo.
    await adminRef.update({ imageUrl: imgUrl });
    const updatedSnapShot = await adminRef.get();
    return { adminId, id: adminId, ...updatedSnapShot.data() };
  } catch (error) {
    console.error("uploadImage failed", error);
    return null;
  }
};

export const uploadImageRechargeRequest = async (file) => {
  const imageRef = storage.ref(`rechargeRequests/${file.name}`);

  try {
    await imageRef.put(file);
    var imgUrl = [];
    await imageRef.getDownloadURL().then((url) => {
      console.log(url);
      imgUrl.push(url);
    });
    return imgUrl[0];
  } catch (error) {
    return null;
  }
};

export const updateAdmin = async (currentAdmin) => {
  const adminId = adminDocId(currentAdmin);
  if (!adminId) {
    console.error("updateAdmin: missing admin id", currentAdmin);
    return null;
  }
  const adminRef = firestore.doc(`admins/${adminId}`);
  try {
    await adminRef.update({ ...currentAdmin, adminId });
    const updatedSnapShot = await adminRef.get();
    return { adminId, id: adminId, ...updatedSnapShot.data() };
  } catch (error) {
    console.error("updateAdmin failed", error);
    return null;
  }
};

export const getAllNotices = async () => {
  const noticesCollectionRef = firestore.collection("notices");
  try {
    const notices = await noticesCollectionRef.get();
    const noticesArray = [];
    notices.forEach((doc) => {
      noticesArray.push(doc.data());
    });
    return noticesArray;
  } catch (error) {
    alert(error);
  }
};

export const getNoticesPaginated = async (lastDoc = null) => {
  try {
    let query = firestore
      .collection("notices")
      .orderBy("createdAt", "desc")
      .limit(20);
    if (lastDoc) {
      query = query.startAfter(lastDoc);
    }
    const snapshot = await query.get();
    const notices = [];
    snapshot.forEach((doc) => notices.push(doc.data()));
    const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
    return { notices, lastVisible, hasMore: snapshot.docs.length === 20 };
  } catch (error) {
    alert(error);
    return { notices: [], lastVisible: null, hasMore: false };
  }
};
export const getAllBlogs = async () => {
  const blogsCollectionRef = firestore.collection("blogs");
  try {
    const blogs = await blogsCollectionRef.get();
    const blogsArray = [];
    blogs.forEach((doc) => {
      blogsArray.push(doc.data());
    });
    return blogsArray;
  } catch (error) {
    alert(error);
  }
};

export const getAllIntros = async () => {
  const introsCollectionRef = firestore.collection("intros");
  try {
    const intros = await introsCollectionRef.get();
    const introsArray = [];
    intros.forEach((doc) => {
      introsArray.push(doc.data());
    });
    return introsArray;
  } catch (error) {
    alert(error);
  }
};

export const getAllRechargeDays = async () => {
  const rechargeDaysCollectionRef = firestore.collection("rechargeDays");

  try {
    const rechargeDays = await rechargeDaysCollectionRef
      .orderBy("date", "desc")
      .get();
    const rechargeDaysArray = [];
    rechargeDays.forEach((doc) => {
      rechargeDaysArray.push(doc.data());
    });
    return rechargeDaysArray;
  } catch (error) {
    alert(error);
    return [];
  }
};
// One document per day, forever — so this is read on every boot and grows by
// ~365 documents a year. Capped to the recent window the Payments page
// actually shows.
//
// It cannot be capped on `date`: that field is a display string ("8-5-2026")
// which sorts lexicographically, so `orderBy("date").limit()` would keep the
// wrong days. `createdAt` is a real timestamp and sorts correctly — but
// documents written before that field existed do not have it, and Firestore
// silently omits documents missing the field it is ordering on. So an empty
// result is treated as "this collection has not been stamped" and falls back
// to the old unbounded read rather than showing an empty payment history.
// `npm run backfill:created-at -- --collections=paymentDays` stamps the old
// ones and makes the fast path apply to everything.
export const PAYMENT_DAYS_PAGE_SIZE = 120;

export const getAllPaymentDays = async (
  { pageSize = PAYMENT_DAYS_PAGE_SIZE } = {},
) => {
  const byDateDesc = (a, b) => new Date(b.date) - new Date(a.date);
  const collect = (snapshot) => {
    const out = [];
    snapshot.forEach((doc) => out.push(doc.data()));
    return out;
  };

  try {
    const recent = await firestore
      .collection("paymentDays")
      .orderBy("createdAt", "desc")
      .limit(pageSize)
      .get();
    const paymentDaysArray = collect(recent);
    if (paymentDaysArray.length > 0) return paymentDaysArray.sort(byDateDesc);
  } catch (error) {
    console.warn(
      "Could not read paymentDays by createdAt, falling back to a full read:",
      error,
    );
  }

  try {
    const all = await firestore
      .collection("paymentDays")
      .orderBy("date", "desc")
      .get();
    return collect(all).sort(byDateDesc);
  } catch (error) {
    alert(error);
    return [];
  }
};
export const getAllPaymentDaysExpress = async () => {
  const paymentDaysCollectionRef = firestore
    .collection("paymentDaysExpress")
    .orderBy("date", "desc");
  try {
    const paymentDays = await paymentDaysCollectionRef.get();
    const paymentDaysArray = [];
    paymentDays.forEach((doc) => {
      paymentDaysArray.push(doc.data());
    });
    return paymentDaysArray.sort((a, b) => new Date(b.date) - new Date(a.date));
  } catch (error) {
    alert(error);
    return [];
  }
};

export const makeBookingReceived = async (bookingObj) => {
  console.log(bookingObj);

  const bookingMonthRef = firestore.doc(`bookingMonths/${bookingObj.month}`);
  const bookingMonth = await bookingMonthRef.get();

  if (!bookingMonth.exists) {
    bookingMonthRef.set({
      totalOrder: 1,
      deliveredOrder: 0,
      pendingOrder: 1,
      month: bookingObj.month,
      createdAt: createdAtStamp(),
    });
  } else {
    bookingMonthRef.update({
      totalOrder: bookingMonth.data().totalOrder + 1,
      pendingOrder: bookingMonth.data().pendingOrder + 1,
    });
  }
};
export const updateExpressMonth = async (month) => {
  const bookingMonthRef = firestore.doc(`bookingMonths/${month}`);
  const bookingMonth = await bookingMonthRef.get();
  console.log(bookingMonth.data());
  bookingMonthRef.update({
    pendingOrder: bookingMonth.data().pendingOrder - 1,
    deliveredOrder: bookingMonth.data().deliveredOrder + 1,
  });
};

export const getAllRechargeRequest = async () => {
  const rechargeRequestCollectionRef = firestore
    .collection("rechargeRequest")
    .where("status", "==", "pending");

  try {
    const rechargeRequest = await rechargeRequestCollectionRef.get();
    const rechargeRequestArray = [];
    rechargeRequest.forEach((doc) => {
      rechargeRequestArray.push(doc.data());
    });
    console.log(rechargeRequestArray);
    return rechargeRequestArray;
  } catch (error) {
    alert(error);
  }
};

export const getAllPaymentRequest = async () => {
  const paymentRequestCollectionRef = firestore
    .collection("paymentRequest")
    .where("status", "==", "pending");

  try {
    const paymentRequest = await paymentRequestCollectionRef.get();
    const paymentRequestArray = [];
    paymentRequest.forEach((doc) => {
      paymentRequestArray.push(doc.data());
    });
    console.log(paymentRequestArray);
    return paymentRequestArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllPaymentRequestP2p = async () => {
  const paymentRequestCollectionRef = firestore
    .collection("paymentRequestP2p")
    .where("status", "==", "pending");

  try {
    const paymentRequest = await paymentRequestCollectionRef.get();
    const paymentRequestArray = [];
    paymentRequest.forEach((doc) => {
      paymentRequestArray.push(doc.data());
    });
    console.log(paymentRequestArray);
    return paymentRequestArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllPaymentRequestSourcings = async () => {
  const paymentRequestCollectionRef = firestore
    .collection("paymentRequestSourcing")
    .where("status", "==", "pending");

  try {
    const paymentRequest = await paymentRequestCollectionRef.get();
    const paymentRequestArray = [];
    paymentRequest.forEach((doc) => {
      paymentRequestArray.push(doc.data());
    });
    console.log(paymentRequestArray);
    return paymentRequestArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllPaymentRequestExpress = async () => {
  const paymentRequestCollectionRef = firestore
    .collection("paymentRequestExpress")
    .where("status", "==", "pending");

  try {
    const paymentRequest = await paymentRequestCollectionRef.get();
    const paymentRequestArray = [];
    paymentRequest.forEach((doc) => {
      paymentRequestArray.push(doc.data());
    });
    console.log(paymentRequestArray);
    return paymentRequestArray;
  } catch (error) {
    alert(error);
  }
};

export const getAllBookings = async (bookingStatus) => {
  console.log(bookingStatus);
  const bookingsCollectionRef = firestore
    .collection("bookingRequest")
    .where("bookingStatus", "==", bookingStatus);

  try {
    const bookings = await bookingsCollectionRef.get();
    const bookingsArray = [];
    bookings.forEach((doc) => {
      bookingsArray.push(doc.data());
    });
    return bookingsArray.sort((a, b) => {
      const aTime = a.createdAt
        ? a.createdAt.toMillis
          ? a.createdAt.toMillis()
          : (a.createdAt.seconds || 0) * 1000
        : 0;
      const bTime = b.createdAt
        ? b.createdAt.toMillis
          ? b.createdAt.toMillis()
          : (b.createdAt.seconds || 0) * 1000
        : 0;
      return bTime - aTime;
    });
  } catch (error) {
    alert(error);
  }
};

export const getAllReceivedExpressBookings = async (month) => {
  if (month) {
    const bookingsCollectionRef = firestore
      .collection("bookingRequest")
      .where("bookingStatus", "==", "Received")
      .where("month", "==", month);

    try {
      const bookings = await bookingsCollectionRef.get();
      const bookingsArray = [];
      bookings.forEach((doc) => {
        bookingsArray.push(doc.data());
      });
      return bookingsArray;
    } catch (error) {
      alert(error);
    }
  } else {
    const bookingsCollectionRef = firestore
      .collection("bookingRequest")
      .where("bookingStatus", "==", "Received");
    try {
      const bookings = await bookingsCollectionRef.get();
      const bookingsArray = [];
      bookings.forEach((doc) => {
        bookingsArray.push(doc.data());
      });
      return bookingsArray;
    } catch (error) {
      alert(error);
    }
  }
};
export const getAllRefundRequest = async (refundStatus) => {
  const refundsCollectionRef = firestore
    .collection("refundRequest")
    .where("refundStatus", "==", refundStatus);
  try {
    const refunds = await refundsCollectionRef.get();
    const refundsArray = [];
    refunds.forEach((doc) => {
      refundsArray.push(doc.data());
    });
    return refundsArray;
  } catch (error) {
    alert(error);
  }
};

export const getAllDocumentExpressRates = async () => {
  const expressRatesDocumentsCollectionRef = firestore.collection(
    "expressRatesDocuments",
  );
  try {
    const expressRatesDocuments =
      await expressRatesDocumentsCollectionRef.get();
    const expressRatesDocumentsArray = [];
    expressRatesDocuments.forEach((doc) => {
      expressRatesDocumentsArray.push(doc.data());
    });
    return expressRatesDocumentsArray;
  } catch (error) {
    alert(error);
  }
};

export const getAllOffices = async () => {
  const officesCollectionRef = firestore.collection("offices");
  try {
    const offices = await officesCollectionRef.get();
    const officesArray = [];
    offices.forEach((doc) => {
      officesArray.push(doc.data());
    });
    return officesArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllBookingOffices = async () => {
  const officesCollectionRef = firestore.collection("bookingOffices");
  try {
    const offices = await officesCollectionRef.get();
    const officesArray = [];
    offices.forEach((doc) => {
      officesArray.push(doc.data());
    });
    return officesArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllCnfs = async () => {
  const cnfsCollectionRef = firestore.collection("cnfs");
  try {
    const cnfs = await cnfsCollectionRef.get();
    const cnfsArray = [];
    cnfs.forEach((doc) => {
      cnfsArray.push(doc.data());
    });
    return cnfsArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllCnfBills = async (month, name) => {
  const cnfsCollectionRef = firestore
    .collection("cnfBills")
    .where("month", "==", month)
    .where("cnf", "==", name);
  try {
    const cnfs = await cnfsCollectionRef.get();
    const cnfsArray = [];
    cnfs.forEach((doc) => {
      cnfsArray.push(doc.data());
    });
    return cnfsArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllCnfExpenses = async (month, name) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("month", "==", month)
    .where("subCategory", "==", name);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllCnfBillsAllMonths = async (name) => {
  const cnfsCollectionRef = firestore
    .collection("cnfBills")
    .where("cnf", "==", name);
  try {
    const cnfs = await cnfsCollectionRef.get();
    const cnfsArray = [];
    cnfs.forEach((doc) => {
      cnfsArray.push(doc.data());
    });
    return cnfsArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllCnfExpensesAllMonths = async (name) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("subCategory", "==", name);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllEmployees = async () => {
  const employeesCollectionRef = firestore.collection("employees");
  try {
    const employees = await employeesCollectionRef.get();
    const employeesArray = [];
    employees.forEach((doc) => {
      employeesArray.push(doc.data());
    });
    return employeesArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllTransactions = async (employeeId, month) => {
  const employeesCollectionRef = firestore
    .collection("transactions")
    .where("employeeId", "==", employeeId)
    .where("month", "==", month);
  try {
    const employees = await employeesCollectionRef.get();
    const employeesArray = [];
    employees.forEach((doc) => {
      employeesArray.push(doc.data());
    });
    return employeesArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllExpenses = async (day) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("date", "==", day);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllForwarders = async () => {
  const forwardersCollectionRef = firestore.collection("freight-forwarder");

  try {
    const forwarders = await forwardersCollectionRef.get();
    const forwardersArray = [];
    forwarders.forEach((doc) => {
      forwardersArray.push(doc.data());
    });
    return forwardersArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllSourcings = async (month) => {
  const sourcingsCollectionRef = firestore
    .collection("sourcing")
    .where("month", "==", month);
  try {
    const sourcings = await sourcingsCollectionRef.get();
    const sourcingsArray = [];
    sourcings.forEach((doc) => {
      sourcingsArray.push(doc.data());
    });
    return sourcingsArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllSourcingRequest = async () => {
  const sourcingsCollectionRef = firestore
    .collection("sourcing")
    .where("agent", "==", "");
  const purchasingsCollectionRef = firestore
    .collection("purchasing")
    .where("agent", "==", "");
  try {
    const sourcings = await sourcingsCollectionRef.get();
    const purchasings = await purchasingsCollectionRef.get();
    const sourcingsArray = [];
    sourcings.forEach((doc) => {
      sourcingsArray.push(doc.data());
    });
    purchasings.forEach((doc) => {
      sourcingsArray.push(doc.data());
    });
    return sourcingsArray.sort((a, b) => b.id - a.id);
  } catch (error) {
    alert(error);
  }
};
export const getAllSourcingsAgent = async (agent, month) => {
  const sourcingsCollectionRef = firestore
    .collection("sourcing")
    .where("agent", "==", agent)
    .where("month", "==", month);
  try {
    const sourcings = await sourcingsCollectionRef.get();
    const sourcingsArray = [];
    sourcings.forEach((doc) => {
      sourcingsArray.push(doc.data());
    });
    return sourcingsArray;
  } catch (error) {
    alert(error);
  }
};

export const getAllPurchasings = async (month) => {
  const purchasingsCollectionRef = firestore
    .collection("purchasing")
    .where("month", "==", month);
  try {
    const purchasings = await purchasingsCollectionRef.get();
    const purchasingsArray = [];
    purchasings.forEach((doc) => {
      purchasingsArray.push(doc.data());
    });
    return purchasingsArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllPurchasingsAgent = async (agent, month) => {
  const purchasingsCollectionRef = firestore
    .collection("purchasing")
    .where("agent", "==", agent)
    .where("month", "==", month);
  try {
    const purchasings = await purchasingsCollectionRef.get();
    const purchasingsArray = [];
    purchasings.forEach((doc) => {
      purchasingsArray.push(doc.data());
    });
    return purchasingsArray;
  } catch (error) {
    alert(error);
  }
};

export const getAllCashIns = async (day) => {
  const expensesCollectionRef = firestore
    .collection("dailyCashIn")
    .where("date", "==", day);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllMonthlyExpense = async () => {
  const expensesCollectionRef = firestore.collection("monthlyExpense");
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllMonthlyCashSummary = async () => {
  const expensesCollectionRef = firestore.collection("cashSummary");
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllMonthlyCashIn = async () => {
  const expensesCollectionRef = firestore.collection("monthlyCashIn");
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllMonthly = async (category, subCategory) => {
  const expensesCollectionRef = firestore
    .collection(`categoryMonthlyExpense`)
    .where("category", "==", category)
    .where("subCategory", "==", subCategory);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
// export const updateMonthly = async () => {
//   const docRef = firestore.doc(
//     `categoryMonthlyExpense/February,2026-SALARY-SHOHEL`,
//   );
//   const data = await docRef.get();
//   try {
//     if (data.exists) {
//       await docRef.update({
//         paid: false,
//       });
//       console.log("updated");
//     }
//   } catch (error) {
//     alert(error);
//   }
// };

export const getMonth = async (employee, month) => {
  const expensesCollectionRef = firestore
    .collection(`categoryMonthlyExpense`)
    .where("category", "==", "SALARY")
    .where("subCategory", "==", employee);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });

    // Same reasoning as isFullyPaid in employee/monthlyExpenseDatatable.js:
    // the stored `paid` field is only recomputed when a batch gets approved
    // or the salary is edited, so it can go stale (e.g. bonus changed after
    // the month was approved at an old total) and leave an already-settled
    // month showing up here as payable again. Deriving it from the actual
    // totals instead is always correct.
    const unPaidMonths = expensesArray.filter((expense) => {
      const totalToBePaid = expense.totalToBePaid
        ? parseInt(expense.totalToBePaid)
        : parseInt(expense.salary ? expense.salary : 0) +
          parseInt(expense.bonus ? expense.bonus : 0);
      const amountPaid = parseInt(expense.amount) || 0;
      const isFullyPaid = totalToBePaid > 0 && amountPaid === totalToBePaid;
      return !isFullyPaid;
    });
    // Old months (e.g. "December,2025") shouldn't still be offered as
    // payable in this picker — only 2026 onward.
    const recentUnpaidMonths = unPaidMonths.filter((expense) => {
      const year = parseInt(String(expense.month || "").split(",")[1], 10);
      return year >= 2026;
    });
    return recentUnpaidMonths;
  } catch (error) {
    alert(error);
  }
};
export const updateSalary = async (employee) => {
  // Addressed by the stored `id` rather than by deriving the name. That only
  // works because the document's `id` field and its document name agree, and
  // for SALARY documents they do — createSalaryMonth writes both from the same
  // helper. Elsewhere they deliberately differ: `id` keeps the readable
  // "August,2026-LC/TT-LC" while the document is named with the "/" escaped,
  // so widening this beyond salary would resurrect the invalid-reference bug.
  const categoryMonthlyExpenseRef = firestore.doc(
    `categoryMonthlyExpense/${employee.id}`,
  );
  const categoryMonthlyExpnese = await categoryMonthlyExpenseRef.get();
  const newTotalToBePaid =
    parseInt(employee.salary ? employee.salary : 0) +
    parseInt(employee.bonus ? employee.bonus : 0);
  const amountAlreadyPaid = parseInt(categoryMonthlyExpnese.data().amount) || 0;
  await categoryMonthlyExpenseRef.update({
    ...categoryMonthlyExpnese.data(),
    salary: employee.salary,
    bonus: employee.bonus,
    totalToBePaid: newTotalToBePaid,
    // Editing salary/bonus changes totalToBePaid, so the `paid` flag (only
    // otherwise recomputed when an expense batch is approved, see
    // approveExpenseBatch) needs to be re-checked against it here too —
    // otherwise a month that's actually fully paid at the new numbers can
    // stay stuck showing "Pending" until its next approval.
    paid: amountAlreadyPaid === newTotalToBePaid,
  });
  const updatedSnapShot = await categoryMonthlyExpenseRef.get();
  console.log(updatedSnapShot.data());
  return updatedSnapShot.data();
};
export const createSalaryMonth = async (month, employee) => {
  // The document name and the `id` field come from the same derivation on
  // purpose: updateSalary addresses these documents by their stored `id`, so
  // the two must agree. For an employee name with no "/" — every one of them —
  // this is the string it has always been.
  const id = categoryMonthlyExpenseId(month, "SALARY", employee.name);
  const categoryMonthlyExpenseRef = firestore.doc(
    `categoryMonthlyExpense/${id}`,
  );
  const categoryMonthlyExpnese = await categoryMonthlyExpenseRef.get();
  if (!categoryMonthlyExpnese.exists) {
    await categoryMonthlyExpenseRef.set({
      id,
      amount: 0,
      category: "SALARY",
      subCategory: employee.name,
      month: month,
      paid: false,
      salary: 0,
      bonus: 0,
      totalToBePaid: 0,
      createdAt: createdAtStamp(),
    });
  }
};
export const getAllFunds = async () => {
  const expensesCollectionRef = firestore
    .collection(`dailyExpenses`)
    .where("category", "==", "FUND");

  const cashInCollectionRef = firestore
    .collection(`dailyCashIn`)
    .where("category", "==", "FUND");

  try {
    const expenses = await expensesCollectionRef.get();
    const cashIns = await cashInCollectionRef.get();
    const expensesArray = [];
    const cashInsArray = [];
    cashIns.forEach((doc) => {
      cashInsArray.push({ ...doc.data(), type: "cashIn" });
    });
    expenses.forEach((doc) => {
      expensesArray.push({ ...doc.data(), type: "expense" });
    });
    let mergedArray = [...expensesArray, ...cashInsArray];
    return mergedArray.sort((a, b) => parseInt(a.id) - parseInt(b.id));
  } catch (error) {
    alert(error);
  }
};
export const getAllLcTt = async () => {
  // The month list needs both directions, and only cash *out* gets rolled up
  // into categoryMonthlyExpense on approval (see approveExpenseBatch) — so the
  // per-month totals have to be summed from the raw entries in both
  // collections rather than read off a rollup doc.
  const expensesCollectionRef = firestore
    .collection(`dailyExpenses`)
    .where("category", "==", "LC/TT");

  const cashInCollectionRef = firestore
    .collection(`dailyCashIn`)
    .where("category", "==", "LC/TT");

  try {
    const expenses = await expensesCollectionRef.get();
    const cashIns = await cashInCollectionRef.get();
    const mergedArray = [];
    expenses.forEach((doc) => {
      mergedArray.push({ ...doc.data(), type: "expense" });
    });
    cashIns.forEach((doc) => {
      mergedArray.push({ ...doc.data(), type: "cashIn" });
    });
    return mergedArray.sort((a, b) => parseInt(a.id) - parseInt(b.id));
  } catch (error) {
    alert(error);
  }
};
export const getAllMonthsCnfBill = async (month) => {
  const monthRef = firestore.doc(`cnfBillMonths/${month}`);
  const monthdata = await monthRef.get();

  if (!monthdata.exists) {
    await monthRef.set({
      month: month,
      createdAt: createdAtStamp(),
    });
  }

  const monthsCollectionRef = firestore.collection(`cnfBillMonths`);
  try {
    const expenses = await monthsCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllMonthlySalary = async () => {
  const expensesCollectionRef = firestore.collection(`monthlySalary`);

  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
export const getSingleMonthlyExpense = async (month) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("month", "==", month);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
export const getSingleMonthlyCashSummary = async (month) => {
  const expensesCollectionRef = firestore
    .collection("cashSummary")
    .where("month", "==", month);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
// Batched replacement for the old per-parcel `totalPaid(parcelId)` (one
// Firestore query per order, awaited sequentially by its caller). Firestore's
// `where(field, "in", [...])` accepts up to 10 values per query (v8 SDK
// limit), so this chunks parcelIds into groups of 10 and fires every chunk
// concurrently via Promise.all — reads have no batch API like writes do,
// so concurrent requests are the read equivalent. Returns a map so a lot
// with N orders costs ceil(N/10) round-trips instead of N.
export const getTotalPaidForParcels = async (parcelIds) => {
  const uniqueIds = [...new Set((parcelIds || []).filter(Boolean))];
  const result = {};
  uniqueIds.forEach((id) => {
    result[id] = 0;
  });
  if (uniqueIds.length === 0) return result;

  const CHUNK_SIZE = 10;
  const chunks = [];
  for (let i = 0; i < uniqueIds.length; i += CHUNK_SIZE) {
    chunks.push(uniqueIds.slice(i, i + CHUNK_SIZE));
  }

  try {
    const snapshots = await Promise.all(
      chunks.map((chunk) =>
        firestore
          .collection("dailyCashIn")
          .where(
            "note",
            "in",
            chunk.map((id) => `Parcel Id-${id}`),
          )
          .get(),
      ),
    );

    snapshots.forEach((snapshot) => {
      snapshot.forEach((doc) => {
        const data = doc.data();
        const match = /^Parcel Id-(.+)$/.exec(data.note || "");
        if (!match || !(match[1] in result)) return;
        // Guard against a missing/non-numeric amount permanently poisoning
        // the running total with NaN (the old totalPaid() had this bug).
        result[match[1]] += parseInt(data.amount) || 0;
      });
    });
  } catch (error) {
    console.error("Failed to fetch total paid for parcels:", error);
  }

  return result;
};
// cashSummary doc ids are "dd-mm-yyyy" display strings, which don't sort
// chronologically, so the most recent summary can't be found with a single
// orderBy().limit(1) — it has to be probed day by day. Probing a batch per
// round trip is what keeps that cheap.
const CASH_SUMMARY_PROBE_BATCH = 30;
const CASH_SUMMARY_MAX_LOOKBACK_DAYS = 365;

const cashSummaryDocId = (dateObj) =>
  dateObj.toLocaleDateString("en-GB").replaceAll("/", "-");

// Built from calendar components rather than by subtracting 86_400_000ms, so
// it stays correct across month, year and DST boundaries.
const daysBefore = (from, days) =>
  new Date(from.getFullYear(), from.getMonth(), from.getDate() - days);

// Finds the most recent cashSummary before today, whose closing balance opens
// today. This was a loop of up to 365 *sequential* `get()`s — and it is the
// first thing awaited on app boot, so on the first load of each day every
// other boot query queued behind it. Same search, same result, issued a batch
// at a time: yesterday almost always exists, so it now settles in one round
// trip, and the batching only matters after a long gap (holidays, a new
// database).
const findLastCashSummary = async (today) => {
  for (
    let offset = 1;
    offset <= CASH_SUMMARY_MAX_LOOKBACK_DAYS;
    offset += CASH_SUMMARY_PROBE_BATCH
  ) {
    const daysBack = [];
    for (
      let i = 0;
      i < CASH_SUMMARY_PROBE_BATCH &&
      offset + i <= CASH_SUMMARY_MAX_LOOKBACK_DAYS;
      i++
    ) {
      daysBack.push(offset + i);
    }
    const snapshots = await Promise.all(
      daysBack.map((days) =>
        firestore
          .doc(`cashSummary/${cashSummaryDocId(daysBefore(today, days))}`)
          .get(),
      ),
    );
    // Nearest day first, so the first hit is the most recent summary.
    const hit = snapshots.find((snapshot) => snapshot.exists);
    if (hit) return hit.data();
  }
  return null;
};

export const getSingleCashSummary = async () => {
  const date = new Date();
  const cashSummaryRef = firestore.doc(`cashSummary/${cashSummaryDocId(date)}`);
  const cashSummary = await cashSummaryRef.get();
  if (cashSummary.exists) {
    return cashSummary.data();
  } else {
    const lastDay = await findLastCashSummary(date);
    // A database with no summary in the past year used to throw here on
    // `lastDay.remainingBalance` — and as the first call in the boot chain,
    // that took the whole app down. Opening at zero is the right answer for a
    // day with nothing to carry forward.
    const openingBalance = parseInt((lastDay && lastDay.remainingBalance) || 0);
    await cashSummaryRef.set({
      month: getMonthName(),
      date: date.toLocaleDateString("en-GB"),
      previousCash: openingBalance,
      totalCashIns: 0,
      totalCashOuts: 0,
      remainingBalance: openingBalance,
    });
    const updatedSnapShot = await cashSummaryRef.get();
    return updatedSnapShot.data();
  }
};
export const createMonth = async () => {
  const date = new Date();
  const monthRef = firestore.doc(`months/${getMonthName()}`);
  const month = await monthRef.get();
  if (month.exists) {
    let monthsArray = [];
    const monthsCollectionRef = firestore.collection("months");
    const monthsCollection = await monthsCollectionRef.get();
    monthsCollection.forEach((doc) => {
      monthsArray.push(doc.data());
    });
    return monthsArray;
  } else {
    await monthRef.set({
      month: getMonthName(),
    });
    let monthsArray = [];
    const monthsCollectionRef = firestore.collection("months");
    const monthsCollection = await monthsCollectionRef.get();
    monthsCollection.forEach((doc) => {
      monthsArray.push(doc.data());
    });
    return monthsArray;
  }
};
export const getSingleMonthlyCashIn = async (month) => {
  const expensesCollectionRef = firestore
    .collection("dailyCashIn")
    .where("month", "==", month);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
export const getSingleMonthly = async (month, category, subCategory) => {
  if (category === "INVEST") {
    const expensesCollectionRef = firestore
      .collection("dailyExpenses")
      .where("month", "==", month)
      .where("category", "==", category)
      .where("subCategory", "==", subCategory);
    const cashInCollectionRef = firestore
      .collection("dailyCashIn")
      .where("month", "==", month)
      .where("category", "==", category)
      .where("subCategory", "==", subCategory);
    try {
      const expenses = await expensesCollectionRef.get();
      const cashIns = await cashInCollectionRef.get();
      const expensesArray = [];
      expenses.forEach((doc) => {
        expensesArray.push(doc.data());
      });

      cashIns.forEach((doc) => {
        expensesArray.push(doc.data());
      });
      return expensesArray.sort((a, b) => a.id - b.id);
    } catch (error) {
      alert(error);
    }
  } else {
    const expensesCollectionRef = firestore
      .collection("dailyExpenses")
      .where("month", "==", month)
      .where("category", "==", category)
      .where("subCategory", "==", subCategory);
    try {
      const expenses = await expensesCollectionRef.get();
      const expensesArray = [];
      expenses.forEach((doc) => {
        expensesArray.push(doc.data());
      });
      return expensesArray;
    } catch (error) {
      alert(error);
    }
  }
};
export const getAllPartnerPaymentTransactions = async (subCategory) => {
  const ref = firestore
    .collection("dailyExpenses")
    .where("category", "==", "PARTNER PAYMENT")
    .where("subCategory", "==", subCategory);
  try {
    const snapshot = await ref.get();
    const arr = [];
    snapshot.forEach((doc) => arr.push(doc.data()));
    return arr;
  } catch (error) {
    alert(error);
  }
};

export const getSingleMonthlySalary = async (month, category) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("month", "==", month)
    .where("category", "==", category);

  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};

export const getSingleMonthlyLoanCashIn = async (month, category) => {
  const cashInsCollectionRef = firestore
    .collection("dailyCashIn")
    .where("month", "==", month)
    .where("category", "==", category);

  try {
    const cashIns = await cashInsCollectionRef.get();
    const cashInsArray = [];
    cashIns.forEach((doc) => {
      cashInsArray.push(doc.data());
    });
    return cashInsArray;
  } catch (error) {
    alert(error);
  }
};

export const getSingleMonthlyLoanCashOut = async (month, category) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("month", "==", month)
    .where("category", "==", category);

  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};

export const getAllPendingExpensesByDay = async (day) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("date", "==", day)
    .where("status", "==", "pending");
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllPendingCashInByDay = async (day) => {
  const cashInsCollectionRef = firestore
    .collection("dailyCashIn")
    .where("date", "==", day)
    .where("status", "==", "pending");
  try {
    const cashIns = await cashInsCollectionRef.get();
    const cashInsArray = [];
    cashIns.forEach((doc) => {
      cashInsArray.push(doc.data());
    });
    return cashInsArray;
  } catch (error) {
    alert(error);
  }
};

export const getAllPendingExpenses = async () => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("status", "==", "pending");
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllLoansCashOuts = async () => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("category", "==", "LOAN");
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllLoansCashOutCustomer = async (customer) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("category", "==", "LOAN")
    .where("uid", "==", customer);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};
export const getSingleCustomerLoan = async (customer) => {
  const loanRef = firestore.doc(`customerLoans/${customer}`);
  try {
    const customerLoan = await loanRef.get();
    return customerLoan.data();
  } catch (error) {
    alert(error);
  }
};

export const getAllInstallmentsCashOutCustomer = async (customer) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("category", "==", "MONTHLY INSTALLMENT")
    .where("uid", "==", customer);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    alert(error);
  }
};

export const getAllPendingCashIns = async () => {
  const cashInsCollectionRef = firestore
    .collection("dailyCashIn")
    .where("status", "==", "pending");
  try {
    const cashIns = await cashInsCollectionRef.get();
    const cashInsArray = [];
    cashIns.forEach((doc) => {
      cashInsArray.push(doc.data());
    });
    return cashInsArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllLoansCashIns = async () => {
  const cashInsCollectionRef = firestore
    .collection("dailyCashIn")
    .where("category", "==", "LOAN");
  try {
    const cashIns = await cashInsCollectionRef.get();
    const cashInsArray = [];
    cashIns.forEach((doc) => {
      cashInsArray.push(doc.data());
    });
    return cashInsArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllLoansCashInCustomer = async (customer) => {
  const cashInsCollectionRef = firestore
    .collection("dailyCashIn")
    .where("category", "==", "LOAN")
    .where("uid", "==", customer);
  try {
    const cashIns = await cashInsCollectionRef.get();
    const cashInsArray = [];
    cashIns.forEach((doc) => {
      cashInsArray.push(doc.data());
    });
    return cashInsArray;
  } catch (error) {
    alert(error);
  }
};

// export const updateCashSummaryCashOut = async (cashOutMonth, date, total) => {
//   const [day, month, year] = date.split("/");
//   let today = new Date();
//   let expenseDate = new Date(year, parseInt(month - 1), day);

//   const diffTime = Math.abs(today - expenseDate);
//   const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

//   console.log(diffDays + " days");
//   // get the last day remaining balance and update today's cash
//   const cashSummaryRef = firestore.doc(
//     `cashSummary/${date.replaceAll("/", "-")}`,
//   );

//   let lastDay;
//   for (let i = 1; i < 365; i++) {
//     let dateObj = new Date(year, parseInt(month - 1), day - i);
//     let previousDate = new Date(dateObj.getTime().toString() - 86400000);
//     const previousDayRef = firestore.doc(
//       `cashSummary/${previousDate
//         .toLocaleDateString("en-GB")
//         .replaceAll("/", "-")}`,
//     );
//     const previouseDay = await previousDayRef.get();
//     if (previouseDay.exists) {
//       lastDay = previouseDay.data();
//       break;
//     }
//   }
//   console.log(lastDay);
//   const cashSummary = await cashSummaryRef.get();
//   if (cashSummary.exists) {
//     cashSummaryRef.update({
//       totalCashOuts:
//         parseInt(cashSummary.data().totalCashOuts) + parseInt(total),
//       remainingBalance:
//         parseInt(cashSummary.data().remainingBalance) - parseInt(total),
//     });
//   } else {
//     cashSummaryRef.set({
//       month: cashOutMonth,
//       date: date,
//       previousCash: parseInt(lastDay.remainingBalance || 0),
//       totalCashIns: 0,
//       totalCashOuts: total,
//       remainingBalance:
//         parseInt(lastDay.remainingBalance || 0) - parseInt(total),
//     });
//   }

//   // update every next day
//   if (diffDays > 0) {
//     for (let i = 1; i < parseInt(diffDays + 1); i++) {
//       let dateObj = new Date(
//         year,
//         parseInt(parseInt(month) - 1),
//         parseInt(day) + i,
//       );
//       dateObj.setDate(dateObj.getDate());

//       console.log(dateObj.toLocaleDateString("en-GB").replaceAll("/", "-"));
//       const nextDayRef = firestore.doc(
//         `cashSummary/${dateObj
//           .toLocaleDateString("en-GB")
//           .replaceAll("/", "-")}`,
//       );
//       const nextDay = await nextDayRef.get();
//       console.log(nextDay.data());
//       if (nextDay.exists) {
//         await nextDayRef.update({
//           previousCash: parseInt(nextDay.data().previousCash) - parseInt(total),
//           remainingBalance:
//             parseInt(nextDay.data().remainingBalance) - parseInt(total),
//         });
//       }
//     }
//   }
// };
export const updateCashSummaryCashOut = async (cashOutMonth, date, total) => {
  const [day, month, year] = date.split("/").map(Number);
  const today = new Date();
  const expenseDate = new Date(year, parseInt(month - 1), day);

  const diffTime = Math.abs(today - expenseDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const cashSummaryRef = firestore.doc(
    `cashSummary/${date.replaceAll("/", "-")}`,
  );

  const previousDayRefs = Array.from({ length: 365 }, (_, i) => {
    const prevDate = new Date(expenseDate);
    prevDate.setDate(prevDate.getDate() - (i + 1));
    return firestore.doc(
      `cashSummary/${prevDate.toLocaleDateString("en-GB").replaceAll("/", "-")}`,
    );
  });

  const nextDayRefs = [];
  for (let i = 1; i <= diffDays; i++) {
    const nextDate = new Date(expenseDate);
    nextDate.setDate(nextDate.getDate() + i);
    nextDayRefs.push(
      firestore.doc(
        `cashSummary/${nextDate.toLocaleDateString("en-GB").replaceAll("/", "-")}`,
      ),
    );
  }

  // Runs the read-modify-write as a single atomic transaction so two
  // approvals that touch the same/overlapping days can't lose each other's
  // update (Firestore retries a transaction on conflict instead of letting
  // a stale read silently overwrite the other write).
  await firestore.runTransaction(async (t) => {
    // 1️⃣ All reads must happen before any writes in a transaction.
    const cashSummarySnap = await t.get(cashSummaryRef);

    let lastDay = null;
    if (!cashSummarySnap.exists) {
      for (const prevRef of previousDayRefs) {
        const snap = await t.get(prevRef);
        if (snap.exists) {
          lastDay = snap.data();
          break;
        }
      }
    }

    const nextDaySnaps = await Promise.all(
      nextDayRefs.map((ref) => t.get(ref)),
    );

    // 2️⃣ Update today's cashSummary
    if (cashSummarySnap.exists) {
      t.update(cashSummaryRef, {
        totalCashOuts:
          parseInt(cashSummarySnap.data().totalCashOuts) + parseInt(total),
        remainingBalance:
          parseInt(cashSummarySnap.data().remainingBalance) - parseInt(total),
      });
    } else {
      const previousBalance =
        lastDay && lastDay.remainingBalance
          ? parseInt(lastDay.remainingBalance)
          : 0;
      t.set(cashSummaryRef, {
        month: cashOutMonth,
        date: date,
        previousCash: previousBalance,
        totalCashIns: 0,
        totalCashOuts: parseInt(total),
        remainingBalance: previousBalance - parseInt(total),
      });
    }

    // 3️⃣ Cascade the delta into every already-existing future day
    nextDaySnaps.forEach((snap) => {
      if (snap.exists) {
        t.update(snap.ref, {
          previousCash: parseInt(snap.data().previousCash) - parseInt(total),
          remainingBalance:
            parseInt(snap.data().remainingBalance) - parseInt(total),
        });
      }
    });
  });
};
// export const updateCashSummaryCashIn = async (cashInMonth, date, total) => {
//   const [day, month, year] = date.split("/");
//   let today = new Date();
//   let expenseDate = new Date(year, parseInt(month - 1), day);

//   const diffTime = Math.abs(today - expenseDate);
//   const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

//   console.log(diffDays + " days");
//   // get the last day remaining balance and update today's cash
//   const cashSummaryRef = firestore.doc(
//     `cashSummary/${date.replaceAll("/", "-")}`,
//   );

//   let lastDay;
//   for (let i = 1; i < 365; i++) {
//     let dateObj = new Date(year, parseInt(month - 1), day - i);
//     let previousDate = new Date(dateObj.getTime().toString() - 86400000);
//     const previousDayRef = firestore.doc(
//       `cashSummary/${previousDate
//         .toLocaleDateString("en-GB")
//         .replaceAll("/", "-")}`,
//     );
//     const previouseDay = await previousDayRef.get();
//     if (previouseDay.exists) {
//       lastDay = previouseDay.data();
//       break;
//     }
//   }
//   console.log(lastDay);
//   const cashSummary = await cashSummaryRef.get();
//   if (cashSummary.exists) {
//     cashSummaryRef.update({
//       totalCashIns: parseInt(cashSummary.data().totalCashIns) + parseInt(total),
//       remainingBalance:
//         parseInt(cashSummary.data().remainingBalance) + parseInt(total),
//     });
//   } else {
//     cashSummaryRef.set({
//       month: cashInMonth,
//       date: date,
//       previousCash: parseInt(lastDay.remainingBalance || 0),
//       totalCashIns: parseInt(total),
//       totalCashOuts: 0,
//       remainingBalance:
//         parseInt(lastDay.remainingBalance || 0) + parseInt(total),
//     });
//   }

//   // update every next day
//   if (diffDays > 0) {
//     for (let i = 1; i < parseInt(diffDays + 1); i++) {
//       let dateObj = new Date(
//         year,
//         parseInt(parseInt(month) - 1),
//         parseInt(day) + i,
//       );
//       dateObj.setDate(dateObj.getDate());

//       console.log(dateObj.toLocaleDateString("en-GB").replaceAll("/", "-"));
//       const nextDayRef = firestore.doc(
//         `cashSummary/${dateObj
//           .toLocaleDateString("en-GB")
//           .replaceAll("/", "-")}`,
//       );
//       const nextDay = await nextDayRef.get();
//       console.log(nextDay.data());
//       if (nextDay.exists) {
//         await nextDayRef.update({
//           previousCash: parseInt(nextDay.data().previousCash) + parseInt(total),
//           remainingBalance:
//             parseInt(nextDay.data().remainingBalance) + parseInt(total),
//         });
//       }
//     }
//   }
// };

export const updateCashSummaryCashIn = async (cashInMonth, date, total) => {
  const [day, month, year] = date.split("/").map(Number);
  const today = new Date();
  const expenseDate = new Date(year, month - 1, day);

  const diffTime = Math.abs(today - expenseDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const cashSummaryRef = firestore.doc(
    `cashSummary/${date.replaceAll("/", "-")}`,
  );

  const previousDayRefs = Array.from({ length: 365 }, (_, i) => {
    const prevDate = new Date(expenseDate);
    prevDate.setDate(prevDate.getDate() - (i + 1));
    return firestore.doc(
      `cashSummary/${prevDate.toLocaleDateString("en-GB").replaceAll("/", "-")}`,
    );
  });

  const nextDayRefs = [];
  for (let i = 1; i <= diffDays; i++) {
    const nextDate = new Date(expenseDate);
    nextDate.setDate(nextDate.getDate() + i);
    nextDayRefs.push(
      firestore.doc(
        `cashSummary/${nextDate.toLocaleDateString("en-GB").replaceAll("/", "-")}`,
      ),
    );
  }

  // Runs the read-modify-write as a single atomic transaction so two
  // approvals that touch the same/overlapping days can't lose each other's
  // update (Firestore retries a transaction on conflict instead of letting
  // a stale read silently overwrite the other write).
  await firestore.runTransaction(async (t) => {
    // 1️⃣ All reads must happen before any writes in a transaction.
    const cashSummarySnap = await t.get(cashSummaryRef);

    let lastDay = null;
    if (!cashSummarySnap.exists) {
      for (const prevRef of previousDayRefs) {
        const snap = await t.get(prevRef);
        if (snap.exists) {
          lastDay = snap.data();
          break;
        }
      }
    }

    const nextDaySnaps = await Promise.all(
      nextDayRefs.map((ref) => t.get(ref)),
    );

    // 2️⃣ Update today's cashSummary
    if (cashSummarySnap.exists) {
      t.update(cashSummaryRef, {
        totalCashIns:
          parseInt(cashSummarySnap.data().totalCashIns) + parseInt(total),
        remainingBalance:
          parseInt(cashSummarySnap.data().remainingBalance) + parseInt(total),
      });
    } else {
      const previousBalance =
        lastDay && lastDay.remainingBalance
          ? parseInt(lastDay.remainingBalance)
          : 0;
      t.set(cashSummaryRef, {
        month: cashInMonth,
        date: date,
        previousCash: previousBalance,
        totalCashIns: parseInt(total),
        totalCashOuts: 0,
        remainingBalance: previousBalance + parseInt(total),
      });
    }

    // 3️⃣ Cascade the delta into every already-existing future day
    nextDaySnaps.forEach((snap) => {
      if (snap.exists) {
        t.update(snap.ref, {
          previousCash: parseInt(snap.data().previousCash) + parseInt(total),
          remainingBalance:
            parseInt(snap.data().remainingBalance) + parseInt(total),
        });
      }
    });
  });
};

// ONE-TIME FIX: cashSummary docs got out of sync with the real
// dailyExpenses/dailyCashIn entries starting 27/06/2026 (26/06/2026 is the
// last known-correct day). This recomputes totalCashIns/totalCashOuts for
// every day in the broken range directly from the underlying entries,
// counting only status == "approved" docs (cashSummary is only ever meant
// to reflect approved entries — see approveExpenseBatch/approveCashInBatch),
// then cascades previousCash/remainingBalance forward day by day. Safe to
// re-run.
export const fixBrokenCashSummaryStats = async (
  startDateStr = "27/06/2026",
  endDateStr = null,
) => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const [startDay, startMonth, startYear] = startDateStr.split("/").map(Number);
  const startDate = new Date(startYear, startMonth - 1, startDay);

  let endDate;
  if (endDateStr) {
    const [endDay, endMonth, endYear] = endDateStr.split("/").map(Number);
    endDate = new Date(endYear, endMonth - 1, endDay);
  } else {
    endDate = new Date();
  }
  endDate.setHours(0, 0, 0, 0);

  // previousCash for the first broken day comes from the last correct day
  // (the day right before startDate).
  const dayBeforeStart = new Date(startDate);
  dayBeforeStart.setDate(dayBeforeStart.getDate() - 1);
  const dayBeforeStartRef = firestore.doc(
    `cashSummary/${dayBeforeStart
      .toLocaleDateString("en-GB")
      .replaceAll("/", "-")}`,
  );
  const dayBeforeStartSnap = await dayBeforeStartRef.get();
  let runningBalance = dayBeforeStartSnap.exists
    ? parseInt(dayBeforeStartSnap.data().remainingBalance) || 0
    : 0;

  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    const dateSlash = cursor.toLocaleDateString("en-GB"); // DD/MM/YYYY
    const dateDash = dateSlash.replaceAll("/", "-"); // DD-MM-YYYY
    const monthLabel = `${monthNames[cursor.getMonth()]},${cursor.getFullYear()}`;

    const [expensesSnap, cashInsSnap] = await Promise.all([
      firestore
        .collection("dailyExpenses")
        .where("date", "==", dateSlash)
        .where("status", "==", "approved")
        .get(),
      firestore
        .collection("dailyCashIn")
        .where("date", "==", dateSlash)
        .where("status", "==", "approved")
        .get(),
    ]);

    let totalCashOuts = 0;
    expensesSnap.forEach((doc) => {
      totalCashOuts += parseInt(doc.data().amount) || 0;
    });

    let totalCashIns = 0;
    cashInsSnap.forEach((doc) => {
      totalCashIns += parseInt(doc.data().amount) || 0;
    });

    const previousCash = runningBalance;
    const remainingBalance = previousCash + totalCashIns - totalCashOuts;

    await firestore.doc(`cashSummary/${dateDash}`).set({
      month: monthLabel,
      date: dateSlash,
      previousCash,
      totalCashIns,
      totalCashOuts,
      remainingBalance,
    });

    console.log(
      `cashSummary/${dateDash} fixed -> in: ${totalCashIns}, out: ${totalCashOuts}, prev: ${previousCash}, balance: ${remainingBalance}`,
    );

    runningBalance = remainingBalance;
    cursor.setDate(cursor.getDate() + 1);
  }

  console.log("✅ cashSummary backfill complete.");
};

// export const approveExpense = async (expense) => {
//   // first update expense status from pending to approved

//   await firestore.runTransaction(async (t) => {
//     console.log(expense);
//     let expenseRef = firestore.doc(`dailyExpenses/${expense.id}`);

//     const snapShot = await t.get(expenseRef);
//     console.log(snapShot.data());
//     // transaction needs all reads to be executed before all writes
//     const categoryMonthlyExpenseRef = firestore.doc(
//       `categoryMonthlyExpense/${snapShot.data().month}-${
//         snapShot.data().category
//       }-${snapShot.data().subCategory.replaceAll("/", "_")}`
//     );
//     const cateogryMonthlyExpnese = await t.get(categoryMonthlyExpenseRef);
//     const monthlyExpenseRef = firestore.doc(
//       `monthlyExpense/${snapShot.data().month}`
//     );
//     const monthlyExpense = await t.get(monthlyExpenseRef);

//     if (snapShot.data().category === "SALARY") {
//       const monthlySalaryRef = firestore.doc(
//         `monthlySalary/${snapShot.data().month}`
//       );
//       const monthlySalary = await t.get(monthlySalaryRef);
//       if (monthlySalary.exists) {
//         t.update(monthlySalaryRef, {
//           amount:
//             parseInt(monthlySalary.data().amount) +
//             parseInt(snapShot.data().amount),
//         });
//       } else {
//         t.set(monthlySalaryRef, {
//           month: snapShot.data().month,
//           amount: parseInt(snapShot.data().amount),
//         });
//       }
//     }
//     if (snapShot.data().category === "LOAN") {
//       const customerRef = firestore.doc(`customerLoans/${snapShot.data().uid}`);
//       const customer = await t.get(customerRef);
//       if (customer.exists) {
//         t.update(customerRef, {
//           amount:
//             parseInt(customer.data().amount) + parseInt(snapShot.data().amount),
//           customer: snapShot.data().subCategory,
//         });
//       } else {
//         t.set(customerRef, {
//           customer: snapShot.data().subCategory,
//           amount: parseInt(snapShot.data().amount),
//           uid: snapShot.data().uid,
//         });
//       }
//     }
//     if (snapShot.data().category === "MONTHLY INSTALLMENT") {
//       const customerRef = firestore.doc(
//         `customerInstallments/${snapShot.data().uid}`
//       );
//       const customer = await t.get(customerRef);
//       if (customer.exists) {
//         t.update(customerRef, {
//           amount:
//             parseInt(customer.data().amount) + parseInt(snapShot.data().amount),
//           customer: snapShot.data().subCategory,
//         });
//       } else {
//         t.set(customerRef, {
//           customer: snapShot.data().subCategory,
//           amount: parseInt(snapShot.data().amount),
//           uid: snapShot.data().uid,
//         });
//       }
//     }

//     // writes and update starts here
//     t.update(expenseRef, { status: "approved" });

//     // categorywise monthly expense update
//     if (cateogryMonthlyExpnese.exists) {
//       if (snapShot.data().category == "SALARY") {
//         t.update(categoryMonthlyExpenseRef, {
//           amount:
//             parseInt(cateogryMonthlyExpnese.data().amount) +
//             parseInt(snapShot.data().amount),
//           paid:
//             cateogryMonthlyExpnese.data().salary ==
//             parseInt(cateogryMonthlyExpnese.data().amount) +
//               parseInt(snapShot.data().amount)
//               ? true
//               : false,
//         });
//       } else {
//         t.update(categoryMonthlyExpenseRef, {
//           amount:
//             parseInt(cateogryMonthlyExpnese.data().amount) +
//             parseInt(snapShot.data().amount),
//         });
//       }
//     } else {
//       t.set(categoryMonthlyExpenseRef, {
//         id: `${snapShot.data().month}-${snapShot.data().category}-${
//           snapShot.data().subCategory
//         }`,
//         amount: parseInt(snapShot.data().amount),
//         category: snapShot.data().category,
//         subCategory: snapShot.data().subCategory,
//         month: snapShot.data().month,
//         paid: false,
//         salary: 0,
//       });
//     }

//     // monthly expense update
//     if (monthlyExpense.exists) {
//       t.update(monthlyExpenseRef, {
//         amount:
//           parseInt(monthlyExpense.data().amount) +
//           parseInt(snapShot.data().amount),
//       });
//     } else {
//       t.set(monthlyExpenseRef, {
//         id: `${snapShot.data().month}`,
//         amount: parseInt(snapShot.data().amount),
//       });
//     }
//   });
// };

/**
 * The document name for one month's rollup of a category.
 *
 * A "/" in either half is a path separator to Firestore, so a category like
 * "LC/TT" turned `categoryMonthlyExpense/August,2026-LC/TT-LC` into a
 * three-segment path — a collection, not a document — and approval died with
 * "Document references must have an even number of segments". Every place that
 * addresses one of these documents derives its name here so that no call site
 * has to remember, and so the same document can never be reached by two
 * different spellings.
 *
 * Only "/" is replaced, and only where it appears, so every rollup written
 * before this existed keeps exactly the name it already had.
 *
 * A regex replace rather than String.replaceAll: that is ES2021, missing from
 * Node 14 (which is what the tests run on) and from any browser older than
 * Chrome 85, where it would throw rather than merely fail to escape.
 *
 * Unit tests: src/firebase/categoryMonthlyExpenseId.test.js
 */
const escapeSegment = (value) => String(value == null ? "" : value).replace(/\//g, "_");

export const categoryMonthlyExpenseId = (month, category, subCategory) =>
  `${month}-${escapeSegment(category)}-${escapeSegment(subCategory)}`;

export const approveExpense = async (expense, approvedBy) => {
  const batch = firestore.batch();
  const expenseRef = firestore.doc(`dailyExpenses/${expense.id}`);

  // Get the expense document snapshot
  const expenseSnapShot = await expenseRef.get();
  console.log(expenseSnapShot.data());

  // Get references to related documents
  const categoryMonthlyExpenseRef = firestore.doc(
    `categoryMonthlyExpense/${categoryMonthlyExpenseId(
      expenseSnapShot.data().month,
      expenseSnapShot.data().category,
      expenseSnapShot.data().subCategory,
    )}`,
  );
  const monthlyExpenseRef = firestore.doc(
    `monthlyExpense/${expenseSnapShot.data().month}`,
  );

  // Get document snapshots
  const categoryMonthlyExpenseSnapShot = await categoryMonthlyExpenseRef.get();
  const monthlyExpenseSnapShot = await monthlyExpenseRef.get();

  // Update expense status. `approvedBy` is only written when the caller
  // supplies a name — Firestore rejects `undefined`, and an approval made
  // without a signed-in admin shouldn't blank out an existing name.
  batch.update(expenseRef, {
    status: "approved",
    ...(approvedBy ? { approvedBy } : {}),
  });

  // Update related documents based on expense category
  if (expenseSnapShot.data().category === "SALARY") {
    const monthlySalaryRef = firestore.doc(
      `monthlySalary/${expenseSnapShot.data().month}`,
    );
    const monthlySalarySnapShot = await monthlySalaryRef.get();
    if (monthlySalarySnapShot.exists) {
      batch.update(monthlySalaryRef, {
        amount:
          parseInt(monthlySalarySnapShot.data().amount) +
          parseInt(expenseSnapShot.data().amount),
      });
    } else {
      batch.set(monthlySalaryRef, {
        month: expenseSnapShot.data().month,
        amount: parseInt(expenseSnapShot.data().amount),
      });
    }
  } else if (
    expenseSnapShot.data().category === "LOAN" ||
    expenseSnapShot.data().category === "MONTHLY INSTALLMENT"
  ) {
    const customerRef = firestore.doc(
      `${
        expenseSnapShot.data().category === "LOAN"
          ? "customerLoans"
          : "customerInstallments"
      }/${expenseSnapShot.data().uid}`,
    );
    const customerSnapShot = await customerRef.get();
    if (customerSnapShot.exists) {
      batch.update(customerRef, {
        amount:
          parseInt(customerSnapShot.data().amount) +
          parseInt(expenseSnapShot.data().amount),
        customer: expenseSnapShot.data().subCategory,
      });
    } else {
      batch.set(customerRef, {
        customer: expenseSnapShot.data().subCategory,
        amount: parseInt(expenseSnapShot.data().amount),
        uid: expenseSnapShot.data().uid,
      });
    }
  }

  // Update categorywise monthly expense
  if (categoryMonthlyExpenseSnapShot.exists) {
    if (expenseSnapShot.data().category == "SALARY") {
      batch.update(categoryMonthlyExpenseRef, {
        amount:
          parseInt(categoryMonthlyExpenseSnapShot.data().amount) +
          parseInt(expenseSnapShot.data().amount),
        paid:
          categoryMonthlyExpenseSnapShot.data().totalToBePaid ==
          parseInt(categoryMonthlyExpenseSnapShot.data().amount) +
            parseInt(expenseSnapShot.data().amount)
            ? true
            : false,
      });
    } else {
      batch.update(categoryMonthlyExpenseRef, {
        amount:
          parseInt(categoryMonthlyExpenseSnapShot.data().amount) +
          parseInt(expenseSnapShot.data().amount),
      });
    }
  } else {
    batch.set(categoryMonthlyExpenseRef, {
      id: `${expenseSnapShot.data().month}-${expenseSnapShot.data().category}-${
        expenseSnapShot.data().subCategory
      }`,
      amount: parseInt(expenseSnapShot.data().amount),
      category: expenseSnapShot.data().category,
      subCategory: expenseSnapShot.data().subCategory,
      month: expenseSnapShot.data().month,
      paid: false,
      salary: 0,
      bonus: 0,
      totalToBePaid: 0,
    });
  }

  // Update monthly expense
  if (monthlyExpenseSnapShot.exists) {
    batch.update(monthlyExpenseRef, {
      amount:
        parseInt(monthlyExpenseSnapShot.data().amount) +
        parseInt(expenseSnapShot.data().amount),
    });
  } else {
    batch.set(monthlyExpenseRef, {
      id: `${expenseSnapShot.data().month}`,
      amount: parseInt(expenseSnapShot.data().amount),
    });
  }

  // Commit the batch
  try {
    await batch.commit();
    return true;
  } catch (error) {
    console.error("Error approving expense:", error);
    return false;
  }
};

export const approveExpenseBatch = async (expenses, onProgress, approvedBy) => {
  const db = firestore;

  // Every expense needs to know whether its categoryMonthlyExpense doc
  // already exists (and, for SALARY, its current amount) before we can
  // decide update-vs-set and the `paid` flag. Fetching that per-expense
  // with a sequential `await` inside the loop below turned a day's worth
  // of approvals into that many round-trips end to end. Fetching every
  // *distinct* doc this batch touches up front, in parallel, cuts that to
  // one round-trip regardless of how many expenses share a doc — and a
  // running tally (below) keeps multiple expenses that target the same
  // doc (e.g. two SALARY payments for the same employee/month) correct,
  // which the old per-item read wasn't guaranteed to be anyway (queued
  // batch writes aren't visible to a `.get()` until the batch commits).
  const categoryRefByPath = new Map();
  expenses.forEach((expense) => {
    // Same derivation as the loop below, which looks refs back out of this map
    // — they have to agree, which is why both go through the one helper.
    const path = categoryMonthlyExpenseId(
      expense.month,
      expense.category,
      expense.subCategory,
    );
    if (!categoryRefByPath.has(path)) {
      categoryRefByPath.set(path, db.doc(`categoryMonthlyExpense/${path}`));
    }
  });
  const categoryPaths = Array.from(categoryRefByPath.keys());
  const categorySnapshots = await Promise.all(
    categoryPaths.map((path) => categoryRefByPath.get(path).get()),
  );
  const categoryExistsByPath = new Map();
  const categoryAmountByPath = new Map();
  const categoryTotalToBePaidByPath = new Map();
  categoryPaths.forEach((path, i) => {
    const snapShot = categorySnapshots[i];
    categoryExistsByPath.set(path, snapShot.exists);
    categoryAmountByPath.set(
      path,
      snapShot.exists ? parseInt(snapShot.data().amount) || 0 : 0,
    );
    categoryTotalToBePaidByPath.set(
      path,
      snapShot.exists ? snapShot.data().totalToBePaid : undefined,
    );
  });

  let batch = db.batch();
  let operationCount = 0;
  let processedCount = 0;

  for (const expense of expenses) {
    const expenseRef = db.doc(`dailyExpenses/${expense.id}`);

    // 1️⃣ Approve expense
    batch.update(expenseRef, {
      status: "approved",
      ...(approvedBy ? { approvedBy } : {}),
    });
    operationCount++;

    const amount = parseInt(expense.amount);
    const month = expense.month;
    const category = expense.category;

    // 2️⃣ Monthly Expense
    const monthlyExpenseRef = db.doc(`monthlyExpense/${month}`);
    batch.set(
      monthlyExpenseRef,
      {
        id: month,
        amount: firebase.firestore.FieldValue.increment(amount),
      },
      { merge: true },
    );
    operationCount++;

    // 3️⃣ Category Monthly Expense
    const categoryPath = categoryMonthlyExpenseId(
      month,
      category,
      expense.subCategory,
    );
    const categoryMonthlyExpenseRef = categoryRefByPath.get(categoryPath);

    const newCategoryAmount = categoryAmountByPath.get(categoryPath) + amount;
    categoryAmountByPath.set(categoryPath, newCategoryAmount);

    if (categoryExistsByPath.get(categoryPath)) {
      if (category === "SALARY") {
        batch.update(categoryMonthlyExpenseRef, {
          amount: firebase.firestore.FieldValue.increment(amount),
          paid:
            categoryTotalToBePaidByPath.get(categoryPath) == newCategoryAmount
              ? true
              : false,
        });
      } else {
        batch.update(categoryMonthlyExpenseRef, {
          amount: firebase.firestore.FieldValue.increment(amount),
        });
      }
    } else {
      batch.set(
        categoryMonthlyExpenseRef,
        {
          // The readable form, with any "/" intact — this is a label, not a
          // path. The document itself is named with it escaped, above.
          id: `${month}-${category}-${expense.subCategory}`,
          category,
          subCategory: expense.subCategory,
          month,
          paid: false,
          salary: 0,
          amount: firebase.firestore.FieldValue.increment(amount),
        },
        { merge: true },
      );
      operationCount++;
      // A later expense in this same batch targeting the same doc should
      // now go through the update branch above, not create it again.
      categoryExistsByPath.set(categoryPath, true);
    }
    // 4️⃣ SALARY Handling
    if (category === "SALARY") {
      const monthlySalaryRef = db.doc(`monthlySalary/${month}`);
      batch.set(
        monthlySalaryRef,
        {
          month,
          amount: firebase.firestore.FieldValue.increment(amount),
        },
        { merge: true },
      );
      operationCount++;
    }

    // 5️⃣ LOAN / INSTALLMENT Handling
    if (category === "LOAN" || category === "MONTHLY INSTALLMENT") {
      const collection =
        category === "LOAN" ? "customerLoans" : "customerInstallments";

      const customerRef = db.doc(`${collection}/${expense.uid}`);

      batch.set(
        customerRef,
        {
          customer: expense.subCategory,
          uid: expense.uid,
          amount: firebase.firestore.FieldValue.increment(amount),
        },
        { merge: true },
      );
      operationCount++;
    }

    // 🔥 Prevent exceeding 500 write limit
    if (operationCount >= 450) {
      await batch.commit();
      batch = db.batch();
      operationCount = 0;
    }

    processedCount++;
    if (onProgress) onProgress(processedCount, expenses.length);
  }

  if (operationCount > 0) {
    await batch.commit();
  }

  return true;
};

// export const approveCashIn = async (expense) => {
//   // first update expense status from pending to approved
//   await firestore.runTransaction(async (t) => {
//     console.log(expense);
//     const expenseRef = firestore.doc(`dailyCashIn/${expense.id}`);

//     const snapShot = await t.get(expenseRef);
//     // transaction needs all reads to be executed before all writes

//     const monthlyExpenseRef = firestore.doc(
//       `monthlyCashIn/${snapShot.data().month}`
//     );
//     const monthlyExpense = await t.get(monthlyExpenseRef);

//     if (snapShot.data().category === "LOAN") {
//       const customerRef = firestore.doc(`customerLoans/${snapShot.data().uid}`);
//       const customer = await t.get(customerRef);
//       if (customer.exists) {
//         t.update(customerRef, {
//           amount:
//             parseInt(customer.data().amount) - parseInt(snapShot.data().amount),
//           customer: snapShot.data().subCategory,
//         });
//       } else {
//         t.set(customerRef, {
//           customer: snapShot.data().subCategory,
//           amount: -parseInt(snapShot.data().amount),
//           uid: snapShot.data().uid,
//         });
//       }
//     }
//     if (snapShot.data().category === "INVEST") {
//       const categoryMonthlyExpenseRef = firestore.doc(
//         `categoryMonthlyExpense/${snapShot.data().month}-${
//           snapShot.data().category
//         }-${snapShot.data().subCategory.replaceAll("/", "_")}`
//       );
//       const cateogryMonthlyExpnese = await t.get(categoryMonthlyExpenseRef);
//       if (cateogryMonthlyExpnese.exists) {
//         t.update(categoryMonthlyExpenseRef, {
//           amount:
//             parseInt(cateogryMonthlyExpnese.data().amount) -
//             parseInt(snapShot.data().amount),
//         });
//       } else {
//         t.set(categoryMonthlyExpenseRef, {
//           id: `${snapShot.data().month}-${snapShot.data().category}-${
//             snapShot.data().subCategory
//           }`,
//           amount: -parseInt(snapShot.data().amount),
//           category: snapShot.data().category,
//           subCategory: snapShot.data().subCategory,
//           month: snapShot.data().month,
//           paid: false,
//           salary: 0,
//         });
//       }
//     }
//     // writes and update starts here
//     t.update(expenseRef, { status: "approved" });

//     // montly expense update
//     if (monthlyExpense.exists) {
//       t.update(monthlyExpenseRef, {
//         amount:
//           parseInt(monthlyExpense.data().amount) +
//           parseInt(snapShot.data().amount),
//       });
//     } else {
//       t.set(monthlyExpenseRef, {
//         id: `${snapShot.data().month}`,
//         amount: parseInt(snapShot.data().amount),
//       });
//     }
//   });
// };

export const approveCashIn = async (expense, approvedBy) => {
  const batch = firestore.batch();
  const expenseRef = firestore.doc(`dailyCashIn/${expense.id}`);

  // Get the expense document snapshot
  const expenseSnapShot = await expenseRef.get();
  console.log(expenseSnapShot.data());

  // Get references to related documents
  const monthlyExpenseRef = firestore.doc(
    `monthlyCashIn/${expenseSnapShot.data().month}`,
  );

  // Get document snapshots
  const monthlyExpenseSnapShot = await monthlyExpenseRef.get();

  // Update expense status — see approveExpense for the `approvedBy` guard.
  batch.update(expenseRef, {
    status: "approved",
    ...(approvedBy ? { approvedBy } : {}),
  });

  // Update related documents based on expense category
  if (expenseSnapShot.data().category === "LOAN") {
    const customerRef = firestore.doc(
      `customerLoans/${expenseSnapShot.data().uid}`,
    );
    const customerSnapShot = await customerRef.get();
    if (customerSnapShot.exists) {
      batch.update(customerRef, {
        amount:
          parseInt(customerSnapShot.data().amount) -
          parseInt(expenseSnapShot.data().amount),
        customer: expenseSnapShot.data().subCategory,
      });
    } else {
      batch.set(customerRef, {
        customer: expenseSnapShot.data().subCategory,
        amount: -parseInt(expenseSnapShot.data().amount),
        uid: expenseSnapShot.data().uid,
      });
    }
  } else if (expenseSnapShot.data().category === "INVEST") {
    const categoryMonthlyExpenseRef = firestore.doc(
      `categoryMonthlyExpense/${categoryMonthlyExpenseId(
        expenseSnapShot.data().month,
        expenseSnapShot.data().category,
        expenseSnapShot.data().subCategory,
      )}`,
    );
    const categoryMonthlyExpenseSnapShot =
      await categoryMonthlyExpenseRef.get();
    if (categoryMonthlyExpenseSnapShot.exists) {
      batch.update(categoryMonthlyExpenseRef, {
        amount:
          parseInt(categoryMonthlyExpenseSnapShot.data().amount) -
          parseInt(expenseSnapShot.data().amount),
      });
    } else {
      batch.set(categoryMonthlyExpenseRef, {
        id: `${expenseSnapShot.data().month}-${
          expenseSnapShot.data().category
        }-${expenseSnapShot.data().subCategory}`,
        amount: -parseInt(expenseSnapShot.data().amount),
        category: expenseSnapShot.data().category,
        subCategory: expenseSnapShot.data().subCategory,
        month: expenseSnapShot.data().month,
        paid: false,
        salary: 0,
      });
    }
  }

  // Update monthly expense
  if (monthlyExpenseSnapShot.exists) {
    batch.update(monthlyExpenseRef, {
      amount:
        parseInt(monthlyExpenseSnapShot.data().amount) +
        parseInt(expenseSnapShot.data().amount),
    });
  } else {
    batch.set(monthlyExpenseRef, {
      id: `${expenseSnapShot.data().month}`,
      amount: parseInt(expenseSnapShot.data().amount),
    });
  }

  // Commit the batch
  try {
    await batch.commit();
    return true;
  } catch (error) {
    console.error("Error approving cash-in:", error);
    return false;
  }
};
export const approveCashInBatch = async (cashIns, onProgress, approvedBy) => {
  const db = firestore;
  let batch = firestore.batch();
  let operationCount = 0;
  let processedCount = 0;

  for (const item of cashIns) {
    const expenseRef = firestore.doc(`dailyCashIn/${item.id}`);
    batch.update(expenseRef, {
      status: "approved",
      ...(approvedBy ? { approvedBy } : {}),
    });
    operationCount++;

    const amount = parseInt(item.amount);
    const month = item.month;

    // Monthly Cash In
    const monthlyCashInRef = firestore.doc(`monthlyCashIn/${month}`);
    batch.set(
      monthlyCashInRef,
      { id: month, amount: firebase.firestore.FieldValue.increment(amount) },
      { merge: true },
    );
    operationCount++;

    // Loan reduction
    if (item.category === "LOAN") {
      const customerRef = db.doc(`customerLoans/${item.uid}`);
      batch.set(
        customerRef,
        {
          customer: item.subCategory,
          uid: item.uid,
          amount: firebase.firestore.FieldValue.increment(-amount),
        },
        { merge: true },
      );
      operationCount++;
    }

    // Invest reverse category
    if (item.category === "INVEST") {
      const categoryMonthlyExpenseRef = db.doc(
        `categoryMonthlyExpense/${categoryMonthlyExpenseId(
          month,
          item.category,
          item.subCategory,
        )}`,
      );

      batch.set(
        categoryMonthlyExpenseRef,
        {
          id: `${month}-${item.category}-${item.subCategory}`,
          category: item.category,
          subCategory: item.subCategory,
          month: month,
          paid: false,
          salary: 0,
          amount: firebase.firestore.FieldValue.increment(-amount),
        },
        { merge: true },
      );
      operationCount++;
    }

    if (operationCount >= 450) {
      await batch.commit();
      batch = db.batch();
      operationCount = 0;
    }

    processedCount++;
    if (onProgress) onProgress(processedCount, cashIns.length);
  }

  if (operationCount > 0) {
    await batch.commit();
  }

  return true;
};
export const getAllExpressRatesParcel = async () => {
  const expressRatesParcelCollectionRef =
    firestore.collection("expressRatesParcel");
  try {
    const expressRatesParcel = await expressRatesParcelCollectionRef.get();
    const expressRatesParcelArray = [];
    expressRatesParcel.forEach((doc) => {
      expressRatesParcelArray.push(doc.data());
    });
    return expressRatesParcelArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllD2DRates = async (freightType, country) => {
  const d2dRatesCollectionRef = firestore.collection(
    `d2d-rates-${freightType}-${country}`,
  );
  try {
    const d2dRates = await d2dRatesCollectionRef.get();
    const d2dRatesArray = [];
    d2dRates.forEach((doc) => {
      d2dRatesArray.push(doc.data());
    });
    return d2dRatesArray;
  } catch (error) {
    alert(error);
  }
};

export const getAllExpressOrders = async () => {
  const expressOrdersCollectionRef = firestore.collection("bookingMonths");
  try {
    const ordersExpress = await expressOrdersCollectionRef.get();
    const ordersExpressArray = [];
    ordersExpress.forEach((doc) => {
      ordersExpressArray.push(doc.data());
    });
    return ordersExpressArray;
  } catch (error) {
    alert(error);
  }
};
// `updateExpressOrder` used to live here: it read the entire `ordersExpress`
// collection, looped over every document into an empty `if` body, and returned
// an array it never filled — an unbounded read that could only ever produce
// `[]`. Its only caller was `updateExpressOrderStatusRedux`, which no component
// dispatches, so the whole chain was unreachable. Removed with the thunk.

export const updateRechargeRequestStatus = async (rechargeRequestObj) => {
  console.log(rechargeRequestObj);
  const rechargeRequestRef = firestore.doc(
    `rechargeRequest/${rechargeRequestObj.rechargeId}`,
  );
  const snapshot = await rechargeRequestRef.get();
  if (snapshot.exists) {
    try {
      await rechargeRequestRef.update({
        status: rechargeRequestObj.status,
      });
      const updatedRechargeRequestObj = await rechargeRequestRef.get();
      return updatedRechargeRequestObj.data();
    } catch (error) {
      alert(error);
    }
  } else {
    return null;
  }
};

// Used standalone for Reject (Approve now goes through approvePaymentRequest
// above, which needs the money written in the same atomic batch as this
// status flip). Previously this ran an unbatched Promise.all of per-order
// updates, then a *separate* status write — if the status write failed
// after the order updates already landed, the request was left stuck
// "pending" with its orders already flipped. One batch, one commit fixes
// that the same way approvePaymentRequest does.
export const updatePaymentRequestStatus = async (paymentRequestObj) => {
  const paymentRequestRef = firestore.doc(
    `paymentRequest/${paymentRequestObj.paymentId}`,
  );
  const snapshot = await paymentRequestRef.get();
  if (!snapshot.exists) return null;

  try {
    const batch = firestore.batch();
    paymentRequestObj.paidInvoices.forEach((invoiceId) => {
      batch.update(firestore.doc(`orders/${invoiceId}`), {
        paymentRequest: paymentRequestObj.status,
      });
    });
    batch.update(paymentRequestRef, { status: paymentRequestObj.status });
    await batch.commit();

    const updatedPaymentRequestObj = await paymentRequestRef.get();
    return updatedPaymentRequestObj.data();
  } catch (error) {
    console.error("Failed to update payment request status:", error);
    return null;
  }
};
export const updatePaymentRequestP2pStatus = async (paymentRequestObj) => {
  const paymentRequestRef = firestore.doc(
    `paymentRequestP2p/${paymentRequestObj.paymentId}`,
  );
  const snapshot = await paymentRequestRef.get();
  if (!snapshot.exists) return null;
  try {
    const batch = firestore.batch();
    paymentRequestObj.paidInvoices.forEach((booking) => {
      const p2pRef = firestore.doc(`p2p/${booking.id}`);
      batch.update(p2pRef, { paymentStatus: paymentRequestObj.status });
    });
    batch.update(paymentRequestRef, { status: paymentRequestObj.status });
    await batch.commit();
    const updatedPaymentRequestObj = await paymentRequestRef.get();
    return updatedPaymentRequestObj.data();
  } catch (error) {
    console.error("Failed to update P2P payment request status:", error);
    return null;
  }
};
export const updatePaymentRequestSourcingStatus = async (paymentRequestObj) => {
  const paymentRequestRef = firestore.doc(
    `paymentRequestSourcing/${paymentRequestObj.paymentId}`,
  );
  const snapshot = await paymentRequestRef.get();
  if (!snapshot.exists) return null;
  try {
    const batch = firestore.batch();
    paymentRequestObj.paidInvoices.forEach((invoice) => {
      const orderRef =
        invoice.category === "sold-products"
          ? firestore.doc(`${invoice.category}/${invoice.paymentId}`)
          : firestore.doc(`${invoice.category}/${invoice.id}`);
      batch.update(orderRef, { paymentRequest: paymentRequestObj.status });
    });
    batch.update(paymentRequestRef, { status: paymentRequestObj.status });
    await batch.commit();
    const updatedPaymentRequestObj = await paymentRequestRef.get();
    return updatedPaymentRequestObj.data();
  } catch (error) {
    console.error("Failed to update payment request status:", error);
    return null;
  }
};
export const updatePaymentRequestStatusExpress = async (paymentRequestObj) => {
  const paymentRequestRef = firestore.doc(
    `paymentRequestExpress/${paymentRequestObj.paymentId}`,
  );
  const snapshot = await paymentRequestRef.get();
  if (!snapshot.exists) return null;
  try {
    const batch = firestore.batch();
    paymentRequestObj.paidInvoices.forEach((bookingId) => {
      batch.update(firestore.doc(`bookingRequest/${bookingId}`), {
        paymentRequest: paymentRequestObj.status,
      });
    });
    batch.update(paymentRequestRef, { status: paymentRequestObj.status });
    await batch.commit();
    const updatedPaymentRequestObj = await paymentRequestRef.get();
    return updatedPaymentRequestObj.data();
  } catch (error) {
    console.error("Failed to update payment request status:", error);
    return null;
  }
};

export const getAllOrdersOfSingleLot = async (lotObj) => {
  const ordersCollectionRef = firestore
    .collection(`orders`)
    .where("lotNo", "==", lotObj.lotNo);
  try {
    const collection = await ordersCollectionRef.get();
    let ordersArray = [];
    collection.forEach((doc) => {
      ordersArray.push(doc.data());
    });
    return ordersArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllInvoiceDue = async () => {
  const ordersCollectionRef = firestore
    .collection(`orders`)
    .where("invoiceStatus", "!=", "Paid")
    .where("invoiceGenerated", "==", true);

  const collection = await ordersCollectionRef.get();
  let ordersArray = [];
  collection.forEach((doc) => {
    ordersArray.push(doc.data());
  });
  return ordersArray;
};

// ── Customer dues aggregate ───────────────────────────────────────────────
//
// The dashboard's "Customer Dues" tile is a single number, but producing it
// used to mean reading *every* unpaid invoiced order — an unbounded scan of
// the orders collection, on the page an admin lands on right after login, at a
// cost that grows forever with the business.
//
// Why a recomputed cache and not an incremental counter: 24 places in this
// file write `invoiceStatus`, several inside batched writes and transactions.
// A counter kept by `FieldValue.increment()` at each of those is exact right
// up until one path is missed or a batch half-fails — and then the figure is
// silently wrong for good, with nothing to notice it. This total is always
// derived from the orders themselves, so the worst case is that it is up to an
// hour old, which the dashboard states outright.
//
// Cost: one document read on almost every dashboard load; the full scan runs
// once an hour, paid by whichever load finds the cache stale.
const CUSTOMER_DUES_DOC = "aggregates/customerDues";
const CUSTOMER_DUES_MAX_AGE_MS = 60 * 60 * 1000;

// Mirrors what the dashboard used to compute inline. `|| 0` on the outside
// too: a non-numeric `finalTotal` yields NaN from parseInt, and one such row
// would otherwise turn the whole total into "NaN".
const sumInvoiceDue = (orders) =>
  (orders || []).reduce(
    (acc, order) => acc + (parseInt(order.finalTotal || 0) || 0),
    0,
  );

export const recomputeCustomerDues = async () => {
  const orders = await getAllInvoiceDue();
  const snapshot = {
    totalDue: sumInvoiceDue(orders),
    invoiceCount: orders.length,
    // Client clock rather than a server timestamp: this value is read back
    // immediately by the same client to decide staleness, and a pending
    // serverTimestamp reads as null until it resolves. Skew between admins is
    // minutes at most against a one-hour window.
    computedAt: firebase.firestore.Timestamp.now(),
  };
  try {
    await firestore.doc(CUSTOMER_DUES_DOC).set(snapshot);
  } catch (error) {
    // A read-only admin still gets the right number; it just isn't cached.
    console.warn("Could not cache the customer dues total:", error);
  }
  return snapshot;
};

export const getCustomerDuesTotal = async ({
  maxAgeMs = CUSTOMER_DUES_MAX_AGE_MS,
} = {}) => {
  try {
    const cached = await firestore.doc(CUSTOMER_DUES_DOC).get();
    if (cached.exists) {
      const data = cached.data();
      const computedAtMs =
        data.computedAt && data.computedAt.toMillis
          ? data.computedAt.toMillis()
          : 0;
      if (Date.now() - computedAtMs < maxAgeMs) return data;
    }
  } catch (error) {
    console.warn("Customer dues cache unreadable, recomputing:", error);
  }
  // Missing, stale or unreadable — fall back to the source of truth, which is
  // also what makes this self-healing rather than drift-prone.
  return recomputeCustomerDues();
};

export const getMultipleOrders = async (parcelIdArray) => {
  const snapshots = await Promise.all(
    parcelIdArray.map((parcelId) => firestore.doc(`orders/${parcelId}`).get()),
  );
  return snapshots.map((snapShot) => snapShot.data());
};

export const getMultipleOrdersExpress = async (bookingIdArray) => {
  const snapshots = await Promise.all(
    bookingIdArray.map((bookingId) =>
      firestore.doc(`bookingRequest/${bookingId}`).get(),
    ),
  );
  return snapshots.map((snapShot) => snapShot.data());
};
// One round trip per row, awaited in sequence, meant an invoice with 40 lines
// took 40 sequential fetches. Its siblings above (getMultipleOrders,
// getMultipleOrdersExpress) already fetch in parallel; this now matches them,
// and preserves the original result order.
export const getMultipleSourcings = async (parcelsArray) => {
  const snapshots = await Promise.all(
    parcelsArray.map((parcel) => {
      const docId =
        parcel.category == "sold-products" ? parcel.paymentId : parcel.id;
      return firestore.doc(`${parcel.category}/${docId}`).get();
    }),
  );
  return snapshots.map((snapShot) => snapShot.data());
};
export const getAllRechargesOfSingleDate = async (date) => {
  const rechargesCollectionRef = firestore
    .collection(`rechargeHistory`)
    .where("rechargedAt", "==", date);
  try {
    const recharges = await rechargesCollectionRef.get();
    let rechargesArray = [];
    recharges.forEach((doc) => rechargesArray.push(doc.data()));
    return rechargesArray;
  } catch (error) {
    alert(error);
    return [];
  }
};

export const getAllPaymentsOfSingleDate = async (date) => {
  const paymentsCollectionRef = firestore
    .collection(`paymentHistory`)
    .where("paidAt", "==", date);
  try {
    let paymentsArray = [];
    const payments = await paymentsCollectionRef.get();
    payments.forEach((payment) => {
      paymentsArray.push(payment.data());
    });
    return paymentsArray;
  } catch (error) {
    alert(error);
    return [];
  }
};
export const getAllPaymentsOfSingleDateExpress = async (date) => {
  const paymentsCollectionRef = firestore
    .collection(`paymentHistoryExpress`)
    .where("paidAt", "==", date);
  try {
    let paymentsArray = [];
    const payments = await paymentsCollectionRef.get();
    payments.forEach((payment) => {
      paymentsArray.push(payment.data());
    });
    return paymentsArray;
  } catch (error) {
    alert(error);
    return [];
  }
};

export const getAllOrdersInvoiceRateSingleLot = async (lotObj) => {
  // try {
  const ordersCollectionRef = firestore
    .collection("orders")
    .where("lotNo", "==", lotObj.lotNo);
  // .where("invoiceStatus", "==", "Paid");

  const orders = await ordersCollectionRef.get();
  let ordersArray = [];
  orders.forEach((snapShot) => {
    ordersArray.push(snapShot.data());
  });
  let totalRevenue = 0;
  let totalWeight = 0;
  let totalPaidFor = 0;
  let totalMoneyToPay = 0;

  console.log(ordersArray);
  ordersArray.forEach((order) => {
    totalWeight = totalWeight + parseFloat(order.grossWeight || 0);
    return totalWeight;
  });
  console.log(totalWeight);

  const totalGeneratedInvoices = ordersArray.filter(
    (order) => order.finalTotal,
  );
  console.log(totalGeneratedInvoices);
  if (ordersArray.length !== totalGeneratedInvoices.length) {
    totalMoneyToPay = 0;
  } else {
    ordersArray.forEach((order) => {
      console.log(order.finalTotal);
      totalMoneyToPay = totalMoneyToPay + parseInt(order.finalTotal || 0);
      console.log(totalMoneyToPay);
      return totalMoneyToPay;
    });
  }
  console.log(totalMoneyToPay);

  ordersArray
    .filter((order) => order.invoiceStatus == "Paid")
    .forEach((order) => {
      totalRevenue = totalRevenue + parseInt(order.finalTotal || 0);
      return totalRevenue;
    });
  console.log(totalRevenue);
  ordersArray
    .filter((order) => order.invoiceStatus == "Paid")
    .forEach((order) => {
      totalPaidFor = totalPaidFor + parseFloat(order.grossWeight);
      return totalPaidFor;
    });
  console.log(totalPaidFor);

  console.log(totalRevenue);
  console.log(totalWeight);
  console.log(totalPaidFor);
  // The expense form used to derive this itself, as
  // `totalMoneyToPay == totalRevenue` — which reads as true for a lot whose
  // orders have no invoices at all, because `totalMoneyToPay` is forced to 0
  // above and an unpaid lot's revenue is also 0. Computed from the orders
  // instead, and shared with the payment-time recompute so the two can never
  // disagree about what "delivered" means.
  return {
    totalWeight,
    totalPaidFor,
    totalRevenue,
    totalMoneyToPay,
    allDelivered: lotIsFullyDelivered(ordersArray),
  };
  // } catch (error) {
  //   return { totalWeight: null, totalPaidFor: null, totalRevenue: null };
  // }
};

// `getAllProductsTax` was removed here — an unreferenced full read of the
// `taxes` collection.

export const updateProduct = async (productObj) => {
  const productRef = firestore.doc(`stock-products/${productObj.id}`);
  const product = await productRef.get();
  try {
    delete productObj.file;
    await productRef.update({ ...productObj });
    const updatedSnapShot = await productRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    alert(error);
  }
};
// export const handleProductPay = async (productObj) => {
//   await firestore.runTransaction(async (t) => {
//     const productRef = firestore.doc(`stock-products/${productObj.id}`);
//     const soldProductRef = firestore.doc(
//       `sold-products/${productObj.paymentId}`
//     );
//     const cashInRef = firestore.doc(`dailyCashIn/${productObj.paymentId}`);

//     const product = await t.get(productRef);
//     const soldProduct = await t.get(soldProductRef);
//     const snapShot = await t.get(cashInRef);
//     if (!snapShot.exists) {
//       try {
//         await cashInRef.set({
//           amount: parseInt(productObj.quantity) * parseInt(productObj.amount),
//           category: "Stock Product Sell",
//           subCategory: `${productObj.id}-${productObj.name}`,
//           note: `${productObj.quantity}pc`,
//           date: productObj.date,
//           id: productObj.paymentId,
//           month: productObj.month,
//           receiveBy: productObj.receiveBy,
//           status: "pending",
//           unEditable: true,
//         });
//       } catch (error) {
//         alert(error);
//       }
//     }
//     let newQuantity =
//       parseInt(product.data().quantity) - parseInt(productObj.quantity);
//     if (newQuantity <= 0) {
//       newQuantity = 0;
//     }
//     await t.update(productRef, {
//       quantity: newQuantity,
//     });
//     if (soldProduct.exists) {
//       await t.update(soldProductRef, {
//         ...productObj,
//       });
//     } else {
//       await t.set(soldProductRef, {
//         ...productObj,
//       });
//     }
//   });
//   const productRef = firestore.doc(`stock-products/${productObj.id}`);
//   const snapShot = await productRef.get();
//   return snapShot.data();
// };

export const handleProductPay = async (productObj) => {
  const batch = firestore.batch();
  const productRef = firestore.doc(`stock-products/${productObj.id}`);
  const soldProductRef = firestore.doc(`sold-products/${productObj.paymentId}`);
  const cashInRef = firestore.doc(`dailyCashIn/${productObj.paymentId}`);

  // Get document snapshots
  const productSnapShot = await productRef.get();
  const soldProductSnapShot = await soldProductRef.get();
  const cashInSnapShot = await cashInRef.get();

  // Create cash-in document if it doesn't exist
  if (!cashInSnapShot.exists) {
    try {
      batch.set(cashInRef, {
        amount: parseInt(productObj.quantity) * parseInt(productObj.amount),
        category: "Stock Product Sell",
        subCategory: `${productObj.id}-${productObj.name}`,
        note: `${productObj.quantity}pc`,
        date: productObj.date,
        id: productObj.paymentId,
        month: productObj.month,
        receiveBy: productObj.receiveBy,
        status: "pending",
        unEditable: true,
        createdAt: createdAtStamp(),
      });
    } catch (error) {
      alert(error);
    }
  }

  // Update product quantity
  let newQuantity =
    parseInt(productSnapShot.data().quantity) - parseInt(productObj.quantity);
  if (newQuantity <= 0) {
    newQuantity = 0;
  }
  batch.update(productRef, {
    quantity: newQuantity,
  });

  // Update or create sold product document
  if (soldProductSnapShot.exists) {
    batch.update(soldProductRef, {
      ...productObj,
    });
  } else {
    batch.set(soldProductRef, {
      ...productObj,
    });
  }

  // Commit the batch
  try {
    await batch.commit();
    const productRef = firestore.doc(`stock-products/${productObj.id}`);
    const snapShot = await productRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
    return false;
  }
};

// export const handleProductPayGenerate = async (productObj) => {
//   await firestore.runTransaction(async (t) => {
//     const productRef = firestore.doc(`stock-products/${productObj.id}`);
//     const soldProductRef = firestore.doc(
//       `sold-products/${productObj.paymentId}`
//     );
//     const product = await t.get(productRef);
//     const soldProduct = await t.get(soldProductRef);
//     let newQuantity =
//       parseInt(product.data().quantity) - parseInt(productObj.quantity);
//     if (newQuantity <= 0) {
//       newQuantity = 0;
//     }
//     await t.update(productRef, {
//       quantity: newQuantity,
//     });

//     await t.set(soldProductRef, {
//       ...productObj,
//     });
//   });
//   const productRef = firestore.doc(`stock-products/${productObj.id}`);
//   const snapShot = await productRef.get();
//   return snapShot.data();
// };

export const handleProductPayGenerate = async (productObj) => {
  const batch = firestore.batch();
  const productRef = firestore.doc(`stock-products/${productObj.id}`);
  const soldProductRef = firestore.doc(`sold-products/${productObj.paymentId}`);

  // Get document snapshots
  const productSnapShot = await productRef.get();

  // Update product quantity
  let newQuantity =
    parseInt(productSnapShot.data().quantity) - parseInt(productObj.quantity);
  if (newQuantity <= 0) {
    newQuantity = 0;
  }
  batch.update(productRef, {
    quantity: newQuantity,
  });

  // Set sold product document
  batch.set(soldProductRef, {
    ...productObj,
  });

  // Commit the batch
  try {
    await batch.commit();
    const productRef = firestore.doc(`stock-products/${productObj.id}`);
    const snapShot = await productRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
    return false;
  }
};

// export const handleSourcingCashIn = async (sourcingObj) => {
//   await firestore.runTransaction(async (t) => {
//     const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);

//     const cashInRef = firestore.doc(`dailyCashIn/${sourcingObj.paymentId}`);

//     const sourcing = await t.get(sourcingRef);

//     const snapShot = await t.get(cashInRef);
//     if (!snapShot.exists) {
//       try {
//         await t.set(cashInRef, {
//           amount: parseInt(sourcingObj.totalPaid),
//           category: "Sourcing",
//           subCategory: `${sourcingObj.agent}`,
//           note: `Invoice:${sourcingObj.id}
//           Date: ${sourcing.data().date}`,
//           date: sourcingObj.date,
//           id: sourcingObj.paymentId,
//           month: sourcingObj.month,
//           receiveBy: sourcingObj.receiveBy,
//           status: "pending",
//           unEditable: true,
//         });
//       } catch (error) {
//         alert(error);
//       }
//     }

//     await t.update(sourcingRef, {
//       ...sourcingObj,
//       date: sourcing.data().date,
//       totalPaid:
//         parseInt(sourcing.data().totalPaid) + parseInt(sourcingObj.totalPaid),
//       status: "approved",
//       finalProfit:
//         parseInt(sourcing.data().totalPaid) +
//         parseInt(sourcingObj.totalPaid) -
//         parseInt(sourcing.data().buyingCost),
//       agentPaid: sourcing.data().agentPaid,
//       algProfit:
//         parseInt(sourcing.data().totalPaid) +
//         parseInt(sourcingObj.totalPaid) -
//         parseInt(sourcing.data().buyingCost) -
//         sourcing.data().agentPaid,
//       invoiceStatus:
//         parseInt(sourcing.data().totalPaid) + parseInt(sourcingObj.totalPaid) >=
//         sourcing.data().totalBill
//           ? "Paid"
//           : "Not Paid",
//     });
//   });
//   const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);
//   const snapShot = await sourcingRef.get();
//   return snapShot.data();
// };

// Uses a transaction (not a plain batch) because the written fields
// (totalPaid, finalProfit, algProfit, invoiceStatus, cashIns) are all
// derived from a freshly-read current value. A plain batch's read happens
// before the write is committed, so two concurrent cash-ins (e.g. a
// double-click) can both read the same stale totalPaid/cashIns and the
// commit that lands last silently overwrites the other's contribution.
// A transaction detects that conflict and retries automatically.
export const handleSourcingCashIn = async (sourcingObj) => {
  const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);
  const cashInRef = firestore.doc(`dailyCashIn/${sourcingObj.paymentId}`);

  try {
    await firestore.runTransaction(async (t) => {
      const sourcingSnapShot = await t.get(sourcingRef);
      const cashInSnapShot = await t.get(cashInRef);
      const currentData = sourcingSnapShot.data();

      const newTotalPaid =
        parseInt(currentData.totalPaid) + parseInt(sourcingObj.totalPaid);
      const newFinalProfit = newTotalPaid - parseInt(currentData.buyingCost);
      const newCashIns = [
        {
          id: sourcingObj.paymentId,
          amount: sourcingObj.totalPaid,
          date: sourcingObj.date,
          expenseBy: sourcingObj.receiveBy,
        },
        ...(currentData.cashIns || []),
      ];

      if (!cashInSnapShot.exists) {
        t.set(cashInRef, {
          amount: parseInt(sourcingObj.totalPaid),
          category: "Sourcing",
          subCategory: `${sourcingObj.agent}`,
          note: `Invoice:${sourcingObj.id}
          Date: ${currentData.date}`,
          date: sourcingObj.date,
          id: sourcingObj.paymentId,
          month: sourcingObj.month,
          receiveBy: sourcingObj.receiveBy,
          status: "pending",
          unEditable: true,
          createdAt: createdAtStamp(),
        });
      }

      t.update(sourcingRef, {
        ...sourcingObj,
        date: currentData.date,
        totalPaid: newTotalPaid,
        status: "approved",
        finalProfit: newFinalProfit,
        agentPaid: currentData.agentPaid,
        algProfit: newFinalProfit - currentData.agentPaid,
        invoiceStatus:
          newTotalPaid >= currentData.totalBill ? "Paid" : "Not Paid",
        cashIns: newCashIns,
      });
    });
    const snapShot = await sourcingRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
    return false;
  }
};

// export const handlePurchasingCashIn = async (purchasingObj) => {
//   await firestore.runTransaction(async (t) => {
//     const purchasingRef = firestore.doc(`purchasing/${purchasingObj.id}`);

//     const cashInRef = firestore.doc(`dailyCashIn/${purchasingObj.paymentId}`);

//     const purchasing = await t.get(purchasingRef);

//     const snapShot = await t.get(cashInRef);
//     if (!snapShot.exists) {
//       try {
//         await t.set(cashInRef, {
//           amount: parseInt(purchasingObj.totalPaid),
//           category: "Purchasing",
//           subCategory: `${purchasingObj.agent}`,
//           note: `Invoice:${purchasingObj.id}
//           Date: ${purchasing.data().date}`,
//           date: purchasingObj.date,
//           id: purchasingObj.paymentId,
//           month: purchasingObj.month,
//           receiveBy: purchasingObj.receiveBy,
//           status: "pending",
//           unEditable: true,
//         });
//       } catch (error) {
//         alert(error);
//       }
//     }

//     await t.update(purchasingRef, {
//       ...purchasingObj,
//       date: purchasing.data().date,
//       totalPaid:
//         parseInt(purchasing.data().totalPaid) +
//         parseInt(purchasingObj.totalPaid),
//       status: "approved",
//       finalProfit:
//         parseInt(purchasing.data().totalPaid) +
//         parseInt(purchasingObj.totalPaid) -
//         parseInt(purchasing.data().buyingCost),
//       invoiceStatus:
//         parseInt(purchasing.data().totalPaid) +
//           parseInt(purchasingObj.totalPaid) >=
//         purchasing.data().totalBill
//           ? "Paid"
//           : "Not Paid",
//     });
//   });
//   const purchasingRef = firestore.doc(`purchasing/${purchasingObj.id}`);
//   const snapShot = await purchasingRef.get();
//   return snapShot.data();
// };

export const handlePurchasingCashIn = async (purchasingObj) => {
  const batch = firestore.batch();
  const purchasingRef = firestore.doc(`purchasing/${purchasingObj.id}`);
  const cashInRef = firestore.doc(`dailyCashIn/${purchasingObj.paymentId}`);

  // Get document snapshots
  const purchasingSnapShot = await purchasingRef.get();
  const cashInSnapShot = await cashInRef.get();

  // Create cash-in document if it doesn't exist
  if (!cashInSnapShot.exists) {
    try {
      batch.set(cashInRef, {
        amount: parseInt(purchasingObj.totalPaid),
        category: "Purchasing",
        subCategory: `${purchasingObj.agent}`,
        note: `Invoice:${purchasingObj.id}
          Date: ${purchasingSnapShot.data().date}`,
        date: purchasingObj.date,
        id: purchasingObj.paymentId,
        month: purchasingObj.month,
        receiveBy: purchasingObj.receiveBy,
        status: "pending",
        unEditable: true,
        createdAt: createdAtStamp(),
      });
    } catch (error) {
      alert(error);
    }
  }

  // Update purchasing document
  batch.update(purchasingRef, {
    ...purchasingObj,
    date: purchasingSnapShot.data().date,
    totalPaid:
      parseInt(purchasingSnapShot.data().totalPaid) +
      parseInt(purchasingObj.totalPaid),
    status: "approved",
    finalProfit:
      parseInt(purchasingSnapShot.data().totalPaid) +
      parseInt(purchasingObj.totalPaid) -
      parseInt(purchasingSnapShot.data().buyingCost),
    invoiceStatus:
      parseInt(purchasingSnapShot.data().totalPaid) +
        parseInt(purchasingObj.totalPaid) >=
      purchasingSnapShot.data().totalBill
        ? "Paid"
        : "Not Paid",
  });

  // Commit the batch
  try {
    await batch.commit();
    const purchasingRef = firestore.doc(`purchasing/${purchasingObj.id}`);
    const snapShot = await purchasingRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
    return false;
  }
};

// export const handleSourcingCashOut = async (sourcingObj) => {
//   await firestore.runTransaction(async (t) => {
//     const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);
//     const transactionRef = firestore.doc(
//       `transactions/${sourcingObj.paymentId}`
//     );
//     const sourcing = await t.get(sourcingRef);

//     const snapShot = await t.get(transactionRef);
//     if (!snapShot.exists) {
//       try {
//         await t.set(transactionRef, {
//           amount: parseFloat(sourcingObj.buyingcost2).toFixed(2),
//           employeeId: sourcingObj.employeeId,
//           paidInvoice: `Invoice:${sourcingObj.id}
//           Date: ${sourcing.data().date}`,
//           month: getMonthName(),
//           category: "Sourcing",
//           subCategory: `${sourcingObj.agent}`,
//           country: sourcingObj.country ? sourcingObj.country : "",
//           rmbBalance: sourcingObj.rmbBalance,
//           rupeeBalance: sourcingObj.rupeeBalance,
//           totalRmb: sourcingObj.totalRmb,
//           date: sourcingObj.date,
//           expenseBy: sourcingObj.expenseBy,
//           buyRate: sourcingObj.buyRate,
//           sellRate: sourcingObj.sellRate,
//           fundType: sourcingObj.fundType,
//         });
//       } catch (error) {
//         alert(error);
//       }
//     }
//     await t.update(sourcingRef, {
//       ...sourcingObj,
//       date: sourcing.data().date,
//       status: "approved",
//       finalProfit:
//         parseInt(sourcing.data().totalPaid) - parseInt(sourcingObj.buyingCost),
//     });
//   });
//   const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);
//   const snapShot = await sourcingRef.get();
//   return snapShot.data();
// };

// Same batch -> transaction fix as handleSourcingCashIn: finalProfit is
// derived from a freshly-read totalPaid, so the read and write must be
// atomic against concurrent callers.
export const handleSourcingCashOut = async (sourcingObj) => {
  const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);
  const transactionRef = firestore.doc(`transactions/${sourcingObj.paymentId}`);

  try {
    await firestore.runTransaction(async (t) => {
      const sourcingSnapShot = await t.get(sourcingRef);
      const transactionSnapShot = await t.get(transactionRef);
      const currentData = sourcingSnapShot.data();

      if (!transactionSnapShot.exists) {
        t.set(transactionRef, {
          amount: parseFloat(sourcingObj.buyingcost2).toFixed(2),
          employeeId: sourcingObj.employeeId,
          paidInvoice: `Invoice:${sourcingObj.id}
          Date: ${currentData.date}`,
          month: getMonthName(),
          category: "Sourcing",
          subCategory: `${sourcingObj.agent}`,
          country: sourcingObj.country ? sourcingObj.country : "",
          rmbBalance: sourcingObj.rmbBalance,
          rupeeBalance: sourcingObj.rupeeBalance,
          totalRmb: sourcingObj.totalRmb,
          date: sourcingObj.date,
          expenseBy: sourcingObj.expenseBy,
          buyRate: sourcingObj.buyRate,
          sellRate: sourcingObj.sellRate,
          fundType: sourcingObj.fundType,
          createdAt: createdAtStamp(),
        });
      }

      t.update(sourcingRef, {
        ...sourcingObj,
        date: currentData.date,
        status: "approved",
        finalProfit:
          parseInt(currentData.totalPaid) - parseInt(sourcingObj.buyingCost),
      });
    });
    const snapShot = await sourcingRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
    return false;
  }
};

// export const handleSourcingAgentPay = async (sourcingObj) => {
//   await firestore.runTransaction(async (t) => {
//     const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);

//     const expenseRef = firestore.doc(`dailyExpenses/${sourcingObj.paymentId}`);

//     const sourcing = await t.get(sourcingRef);

//     const snapShot = await t.get(expenseRef);
//     if (!snapShot.exists) {
//       try {
//         t.set(expenseRef, {
//           amount: parseInt(sourcingObj.agentPaid),
//           category: "Sourcing",
//           subCategory: `${sourcingObj.agent}(agent %)`,
//           note: `Invoice:${sourcingObj.id}
//           Date: ${sourcing.data().date}`,
//           date: sourcingObj.date,
//           id: sourcingObj.paymentId,
//           month: sourcingObj.month,
//           receiveBy: sourcingObj.receiveBy,
//           status: "pending",
//           unEditable: true,
//         });
//         t.update(sourcingRef, {
//           agentPays: sourcingObj.agentPays,
//           agentPaid: sourcing.data().agentPaid
//             ? parseInt(sourcing.data().agentPaid) +
//               parseInt(sourcingObj.agentPaid)
//             : parseInt(sourcingObj.agentPaid),
//           status: "approved",
//           algProfit:
//             parseInt(sourcing.data().totalPaid) -
//             (parseInt(sourcing.data().buyingCost) +
//               (parseInt(sourcing.data().agentPaid) +
//                 parseInt(sourcingObj.agentPaid))),
//         });
//       } catch (error) {
//         alert(error);
//       }
//     }
//   });
//   const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);
//   const snapShot = await sourcingRef.get();
//   return snapShot.data();
// };

export const handleSourcingAgentPay = async (sourcingObj) => {
  const batch = firestore.batch();
  const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);
  const expenseRef = firestore.doc(`dailyExpenses/${sourcingObj.paymentId}`);

  // Get document snapshots
  const sourcingSnapShot = await sourcingRef.get();
  const expenseSnapShot = await expenseRef.get();

  // Create expense document if it doesn't exist
  if (!expenseSnapShot.exists) {
    try {
      batch.set(expenseRef, {
        amount: parseInt(sourcingObj.agentPaid),
        category: "Sourcing",
        subCategory: `${sourcingObj.agent}(agent %)`,
        note: `Invoice:${sourcingObj.id}
          Date: ${sourcingSnapShot.data().date}`,
        date: sourcingObj.date,
        id: sourcingObj.paymentId,
        month: sourcingObj.month,
        receiveBy: sourcingObj.receiveBy,
        status: "pending",
        unEditable: true,
        createdAt: createdAtStamp(),
      });

      batch.update(sourcingRef, {
        agentPays: sourcingObj.agentPays,
        agentPaid: sourcingSnapShot.data().agentPaid
          ? parseInt(sourcingSnapShot.data().agentPaid) +
            parseInt(sourcingObj.agentPaid)
          : parseInt(sourcingObj.agentPaid),
        status: "approved",
        algProfit:
          parseInt(sourcingSnapShot.data().totalPaid) -
          (parseInt(sourcingSnapShot.data().buyingCost) +
            (parseInt(sourcingSnapShot.data().agentPaid) +
              parseInt(sourcingObj.agentPaid))),
      });
    } catch (error) {
      alert(error);
    }
  }

  // Commit the batch
  try {
    await batch.commit();
    const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);
    const snapShot = await sourcingRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
    return false;
  }
};

// export const handleP2pAgentPay = async (p2pObj) => {
//   await firestore.runTransaction(async (t) => {
//     const p2pRef = firestore.doc(`p2p/${p2pObj.id}`);

//     const expenseRef = firestore.doc(`dailyExpenses/${p2pObj.paymentId}`);
//     const snapShot = await t.get(expenseRef);
//     console.log(p2pObj);
//     if (!snapShot.exists) {
//       console.log(p2pObj);
//       try {
//         t.set(expenseRef, {
//           amount: parseInt(p2pObj.agentCost),
//           category: "P2P",
//           subCategory: `Booking Id: ${p2pObj.bookingId}`,
//           note: `Agent Name:${p2pObj.agentsName}`,
//           date: p2pObj.date,
//           id: p2pObj.paymentId,
//           month: p2pObj.month,
//           receiveBy: p2pObj.receiveBy,
//           status: "pending",
//           unEditable: true,
//         });
//         t.update(p2pRef, {
//           agentPaid: "Paid",
//           agentsPaymentDate: p2pObj.date,
//         });
//       } catch (error) {
//         alert(error);
//       }
//     }
//   });
//   const p2pRef = firestore.doc(`p2p/${p2pObj.id}`);
//   const snapShot = await p2pRef.get();
//   return snapShot.data();
// };

export const handleP2pAgentPay = async (p2pObj) => {
  const batch = firestore.batch();
  const p2pRef = firestore.doc(`p2p/${p2pObj.id}`);
  const expenseRef = firestore.doc(`dailyExpenses/${p2pObj.paymentId}`);

  // Get document snapshots
  const expenseSnapShot = await expenseRef.get();
  console.log(p2pObj);

  // Create expense document if it doesn't exist
  if (!expenseSnapShot.exists) {
    console.log(p2pObj);
    try {
      batch.set(expenseRef, {
        amount: parseInt(p2pObj.agentCost),
        category: "P2P",
        subCategory: `Booking Id: ${p2pObj.bookingId}`,
        note: `Agent Name:${p2pObj.agentsName}`,
        date: p2pObj.date,
        id: p2pObj.paymentId,
        month: p2pObj.month,
        receiveBy: p2pObj.receiveBy,
        status: "pending",
        unEditable: true,
        createdAt: createdAtStamp(),
      });

      batch.update(p2pRef, {
        agentPaid: "Paid",
        agentsPaymentDate: p2pObj.date,
      });
    } catch (error) {
      alert(error);
    }
  }

  // Commit the batch
  try {
    await batch.commit();
    const p2pRef = firestore.doc(`p2p/${p2pObj.id}`);
    const snapShot = await p2pRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
    return false;
  }
};

// export const handlePurchasingCashOut = async (purchasingObj) => {
//   await firestore.runTransaction(async (t) => {
//     const purchasingRef = firestore.doc(`purchasing/${purchasingObj.id}`);

//     const transactionRef = firestore.doc(
//       `transactions/${purchasingObj.paymentId}`
//     );

//     const purchasing = await t.get(purchasingRef);

//     const snapShot = await t.get(transactionRef);
//     if (!snapShot.exists) {
//       try {
//         await t.set(transactionRef, {
//           amount: parseFloat(purchasingObj.buyingcost2).toFixed(2),
//           employeeId: purchasingObj.employeeId,
//           paidInvoice: `Invoice:${purchasingObj.id}
//           Date: ${purchasing.data().date}`,
//           month: getMonthName(),
//           category: "Purchasing",
//           subCategory: `${purchasingObj.agent}`,
//           country: purchasingObj.country ? purchasingObj.country : "",
//           rmbBalance: purchasingObj.rmbBalance,
//           rupeeBalance: purchasingObj.rupeeBalance,
//           totalRmb: purchasingObj.totalRmb,
//           date: purchasingObj.date,
//           expenseBy: purchasingObj.expenseBy,
//           buyRate: purchasingObj.buyRate,
//           sellRate: purchasingObj.sellRate,
//           fundType: purchasingObj.fundType,
//         });
//       } catch (error) {
//         alert(error);
//       }
//     }

//     await t.update(purchasingRef, {
//       ...purchasingObj,
//       date: purchasing.data().date,
//       status: "approved",
//       finalProfit:
//         parseInt(purchasing.data().totalPaid) -
//         parseInt(purchasingObj.buyingCost),
//     });
//   });
//   const purchasingRef = firestore.doc(`purchasing/${purchasingObj.id}`);
//   const snapShot = await purchasingRef.get();
//   return snapShot.data();
// };

export const handlePurchasingCashOut = async (purchasingObj) => {
  const batch = firestore.batch();
  const purchasingRef = firestore.doc(`purchasing/${purchasingObj.id}`);
  const transactionRef = firestore.doc(
    `transactions/${purchasingObj.paymentId}`,
  );

  // Get document snapshots
  const purchasingSnapShot = await purchasingRef.get();
  const transactionSnapShot = await transactionRef.get();

  // Create transaction document if it doesn't exist
  if (!transactionSnapShot.exists) {
    try {
      batch.set(transactionRef, {
        amount: parseFloat(purchasingObj.buyingcost2).toFixed(2),
        employeeId: purchasingObj.employeeId,
        paidInvoice: `Invoice:${purchasingObj.id}
          Date: ${purchasingSnapShot.data().date}`,
        month: getMonthName(),
        category: "Purchasing",
        subCategory: `${purchasingObj.agent}`,
        country: purchasingObj.country ? purchasingObj.country : "",
        rmbBalance: purchasingObj.rmbBalance,
        rupeeBalance: purchasingObj.rupeeBalance,
        totalRmb: purchasingObj.totalRmb,
        date: purchasingObj.date,
        expenseBy: purchasingObj.expenseBy,
        buyRate: purchasingObj.buyRate,
        sellRate: purchasingObj.sellRate,
        fundType: purchasingObj.fundType,
        createdAt: createdAtStamp(),
      });
    } catch (error) {
      alert(error);
    }
  }

  // Update purchasing document
  batch.update(purchasingRef, {
    ...purchasingObj,
    date: purchasingSnapShot.data().date,
    status: "approved",
    finalProfit:
      parseInt(purchasingSnapShot.data().totalPaid) -
      parseInt(purchasingObj.buyingCost),
  });

  // Commit the batch
  try {
    await batch.commit();
    const purchasingRef = firestore.doc(`purchasing/${purchasingObj.id}`);
    const snapShot = await purchasingRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
    return false;
  }
};

export const deleteProduct = async (id) => {
  const productRef = firestore.doc(`stock-products/${id}`);
  try {
    await productRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteSourcing = async (id) => {
  const sourcingRef = firestore.doc(`sourcing/${id}`);
  try {
    await sourcingRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteSourcingRequest = async (sourcingObj) => {
  const sourcingRef = firestore.doc(
    `${sourcingObj.category}/${sourcingObj.id}`,
  );
  try {
    await sourcingRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deletePurchasing = async (id) => {
  const purchasingRef = firestore.doc(`purchasing/${id}`);
  try {
    await purchasingRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteLot = async (id) => {
  const lotRef = firestore.doc(`lots/${id}`);
  try {
    await lotRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteNotice = async (id) => {
  const noticeRef = firestore.doc(`notices/${id}`);
  try {
    await noticeRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteBlog = async (id) => {
  const blogRef = firestore.doc(`blogs/${id}`);
  try {
    await blogRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteIntro = async (id) => {
  const introRef = firestore.doc(`intros/${id}`);
  try {
    await introRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const selectIntro = async (id) => {
  const introRef = firestore.doc(`intros/${id}`);
  const introCollectionRef = firestore
    .collection("intros")
    .where("selected", "==", true);
  const introCollection = await introCollectionRef.get();
  introCollection.forEach(async (intro) => {
    const previousIntroRef = firestore.doc(`intros/${intro.data().id}`);
    try {
      await previousIntroRef.update({
        selected: false,
      });
    } catch (error) {
      alert(error);
    }
  });
  try {
    await introRef.update({
      selected: true,
    });
  } catch (error) {
    alert(error);
  }
};

// One page of a user's records, newest first, for the profile page. It used
// to read every matching document on load — a customer with a few thousand
// parcels paid for all of them just to fill one screen — so reads are capped
// at `pageSize` and continued through a cursor only when the admin asks for
// more.
//
// Ordering is attempted in descending order of quality and falls back when
// the database can't satisfy it:
//
//  1. `orderField` (createdAt) descending — genuinely newest first. Firestore
//     silently drops documents that lack the ordered field, so an empty first
//     page is read as "not backfilled yet" and moves on rather than showing
//     an empty history for a customer who has one.
//  2. Document id descending — close to chronological for parcels, whose ids
//     embed the lot number; arbitrary for payments.
//  3. The default (ascending id) ordering, which needs no index at all.
//
// Both ordered forms combine an equality filter with an orderBy, which needs
// a composite index that isn't created automatically; a `failed-precondition`
// therefore steps down instead of leaving the page broken until someone
// follows the console link.
// Shared page reader for the two list screens that used to download their
// whole collection: the lot list (/order/:method/:status) and the user list.
//
// Ordering is the awkward part here, as elsewhere in this codebase: the
// "natural" fields are display strings — a lot's `shipmentDate` and a user's
// `userId` sort lexicographically, so "10" lands before "9". `createdAt` is a
// real timestamp and sorts correctly, but documents written before that field
// existed do not have it, and Firestore silently omits documents missing the
// field being ordered on. So each ordering is tried in turn and steps down on
// a `failed-precondition` (a composite index that has not been created) or on
// an empty first page, exactly like getUserRecordsPage below. Document id is
// the last resort: it needs no index and every document has one.
const readPage = async ({
  base,
  pageSize,
  cursor,
  orderField,
  direction = "desc",
}) => {
  const strategies = [
    ...(orderField
      ? [{ name: orderField, apply: (q) => q.orderBy(orderField, direction) }]
      : []),
    {
      name: `id-${direction}`,
      apply: (q) =>
        q.orderBy(firebase.firestore.FieldPath.documentId(), direction),
    },
    { name: "default", apply: (q) => q },
  ];

  for (let i = 0; i < strategies.length; i++) {
    const strategy = strategies[i];
    const isLast = i === strategies.length - 1;
    try {
      let query = strategy.apply(base).limit(pageSize);
      if (cursor) query = query.startAfter(cursor);
      const snapshot = await query.get();

      // Only the first page can tell "this ordering finds nothing" apart from
      // "the list ended"; an empty follow-up page just means the end.
      if (!snapshot.docs.length && !cursor && !isLast) continue;

      const items = [];
      snapshot.forEach((doc) => items.push({ id: doc.id, ...doc.data() }));
      return {
        items,
        cursor: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: snapshot.docs.length === pageSize,
        orderedBy: strategy.name,
      };
    } catch (error) {
      if (isLast) throw error;
      console.warn(`Paged read by "${strategy.name}" failed, stepping down:`, error);
    }
  }
  return { items: [], cursor: null, hasMore: false, orderedBy: "none" };
};

// Deliberately larger than the table's own page size (25). react-table
// disables its Next button when everything loaded fits on one page, so
// fetching exactly one table-page at a time left Next permanently dead and
// nothing could trigger the next fetch. Fetching four table-pages at a time
// means Next is always live, and stepping onto the last loaded page pulls the
// next batch in behind it.
export const LOTS_PAGE_SIZE = 100;

// The shipment method on a lot is a label like "Air (D2D)" / "Sea (freight)",
// and the page wants all of one family. Firestore cannot match a substring, so
// the two known labels per family are matched with `in` instead — the same set
// the create-lot form offers.
//
// Looked up case-insensitively because the family name arrives from the URL,
// and the sidebar spells it "Freight" (/delivery/Freight, /order/Freight/...)
// while these keys spelled it "freight". A plain SHIPMENT_METHODS[method]
// therefore missed on every Freight page and returned undefined — which
// quietly applied *no* filter at all, so those pages listed the D2D lots too,
// and read newest-first only because a query with no filter needs no
// composite index.
const SHIPMENT_METHODS = {
  d2d: ["Air (D2D)", "Sea (D2D)"],
  freight: ["Air (freight)", "Sea (freight)"],
};

const methodsFor = (shipmentMethod) =>
  SHIPMENT_METHODS[String(shipmentMethod || "").toLowerCase()];

// `excludeDelivered` keeps lots that are finished off the Lot List, which
// shows them by shipment status — a lot's status stops at "Local Warehouse"
// and never becomes "Delivered", so without this a delivered lot sits in both
// that list and Delivered Lots forever. It is an option rather than the rule
// because the Orders/Calculation picker uses this same function and must
// still reach a delivered lot's orders.
//
// Filtered after the read rather than in the query: `where("allDelivered",
// "!=", true)` would drop every lot that has never carried the field — most
// of the collection — because Firestore cannot compare a field that is not
// there.
export const getLotsPage = async ({
  shipmentMethod,
  shipmentStatus,
  pageSize = LOTS_PAGE_SIZE,
  cursor = null,
  excludeDelivered = false,
  deliveredOnly = false,
}) => {
  let base = firestore.collection("lots");
  const methods = methodsFor(shipmentMethod);
  if (methods) base = base.where("shipmentMethod", "in", methods);
  if (shipmentStatus) base = base.where("shipmentStatus", "==", shipmentStatus);
  // The Delivered Lots page is the mirror image of every other list: it wants
  // exactly what `excludeDelivered` throws away. `== true` is a real equality
  // filter, so unlike the exclusion it can be pushed to the server.
  if (deliveredOnly) base = base.where("allDelivered", "==", true);

  // Ordered by `shipmentDate`, not `createdAt`: only 7 of ~1,400 lots have
  // ever been stamped with `createdAt` (it was added later), and Firestore
  // drops documents that lack the field being ordered on — so ordering by it
  // returned an empty page and paging fell back to document order, which is
  // lot ids sorted as text and reads oldest-first. Every lot has a
  // `shipmentDate`, stored as "YYYY-MM-DD", which sorts correctly as a string.
  let from = cursor;
  for (;;) {
    const page = await readPage({
      base,
      pageSize,
      cursor: from,
      orderField: "shipmentDate",
    });
    // Callers key lots by lotNo, which is the document id.
    const items = (
      excludeDelivered
        ? page.items.filter((lot) => !lot.allDelivered)
        : page.items
    ).map((lot) => ({ ...lot, lotNo: lot.id }));

    // A batch made up entirely of delivered lots would hand the caller an
    // empty table, and an empty table has no last row to scroll to — so
    // nothing would ever ask for the next batch. Keep reading instead.
    if (items.length || !page.hasMore) return { ...page, items };
    from = page.cursor;
  }
};

// Same reasoning as LOTS_PAGE_SIZE above.
export const USERS_PAGE_SIZE = 100;

// Paged by signup date, oldest first.
//
// The obvious field to page on would be `userId`, but it is stored as a
// *string*, so Firestore sorts it lexicographically: 1, 10, 100, 1000, 101,
// 11, 2 — which is what put customer 100 straight after customer 9. Sorting
// the fetched rows client-side cannot fix that, because it only reorders the
// rows a page already contains, not which rows the page holds.
//
// `createdAt` is a real timestamp and sorts correctly, and since customer ids
// are handed out in signup order, oldest-first is the same sequence in
// practice. The rows on screen are then sorted numerically by `userId` (see
// the list page), so the visible list reads exactly 1, 2, 3 … 10, 11.
//
// Worth knowing: Firestore omits documents that lack the field being ordered
// on, so any customer without `createdAt` would not appear in this list.
// `npm run backfill:created-at -- --collections=users` would stamp them, but
// users are created by the customer app, so that app should be setting it.
export const getUsersPage = async ({
  pageSize = USERS_PAGE_SIZE,
  cursor = null,
} = {}) => {
  const page = await readPage({
    base: firestore.collection("users"),
    pageSize,
    cursor,
    orderField: "createdAt",
    direction: "asc",
  });
  const items = page.items.map((user) => ({ ...user, uid: user.id }));
  stampUserSearchKeys(items);
  return { ...page, items };
};

export const getUserRecordsPage = async ({
  collectionName,
  matchField,
  userId,
  pageSize,
  cursor,
  orderField,
}) => {
  const base = firestore
    .collection(collectionName)
    .where(matchField, "==", userId);

  const strategies = [
    ...(orderField
      ? [{ name: orderField, apply: (q) => q.orderBy(orderField, "desc") }]
      : []),
    {
      name: "id-desc",
      apply: (q) =>
        q.orderBy(firebase.firestore.FieldPath.documentId(), "desc"),
    },
    { name: "default", apply: (q) => q },
  ];

  let lastError = null;
  for (let i = 0; i < strategies.length; i++) {
    const strategy = strategies[i];
    const isLast = i === strategies.length - 1;
    try {
      let query = strategy.apply(base).limit(pageSize);
      if (cursor) query = query.startAfter(cursor);
      const snapshot = await query.get();

      // Only the first page can diagnose a missing field — once rows are on
      // screen the ordering has already proven itself, and an empty follow-up
      // page just means the end of the list.
      if (!snapshot.docs.length && !cursor && !isLast) {
        console.warn(
          `No ${collectionName} documents ordered by "${strategy.name}". Falling back — run scripts/backfill-created-at.js if this collection has not been backfilled.`,
        );
        continue;
      }

      const items = [];
      snapshot.forEach((doc) => items.push(doc.data()));
      return {
        items,
        cursor: snapshot.docs[snapshot.docs.length - 1] || null,
        // A short page means the collection is exhausted; a full one may or
        // may not be, so the caller offers "load more" and finds out.
        hasMore: snapshot.docs.length === pageSize,
        orderedBy: strategy.name,
      };
    } catch (error) {
      lastError = error;
      if (error && error.code === "failed-precondition" && !isLast) {
        console.warn(
          `Missing index for ${collectionName} ordered by "${strategy.name}". Create the index Firestore suggests in the error below, then this ordering takes over automatically.`,
          error,
        );
        continue;
      }
      throw error;
    }
  }

  throw lastError || new Error(`Could not read ${collectionName}`);
};

// The four getAll*OfSingleUser readers below read a user's whole history in
// one go. They deliberately let Firestore errors propagate instead of
// alert()ing and returning undefined: swallowing the error meant a failed
// read reached the caller as an empty array, so a network failure and a user
// with no bookings looked identical.
export const getAllBookingsOfSingleUser = async (userId) => {
  const bookingsCollectionRef = firestore
    .collection("bookingRequest")
    .where("userId", "==", userId);
  const bookings = await bookingsCollectionRef.get();
  const bookingsArray = [];
  bookings.forEach((doc) => {
    bookingsArray.push(doc.data());
  });
  return bookingsArray.sort((a, b) => b.time - a.time);
};

export const getAllParcelsOfSingleUser = async (userId) => {
  const ordersCollectionRef = firestore
    .collection("orders")
    .where("customerUid", "==", userId);
  const orders = await ordersCollectionRef.get();
  const ordersArray = [];
  orders.forEach((doc) => {
    ordersArray.push(doc.data());
  });
  return ordersArray;
};
export const getAllRechargeRequestsOfSingleUser = async (userId) => {
  const rechargeRequestsCollectionRef = firestore
    .collection("rechargeRequest")
    .where("userId", "==", userId);
  try {
    const rechargeRequests = await rechargeRequestsCollectionRef.get();
    const rechargeRequestssArray = [];
    rechargeRequests.forEach((doc) => {
      rechargeRequestssArray.push(doc.data());
    });
    return rechargeRequestssArray;
  } catch (error) {
    alert(error);
  }
};

export const getAllRechargeOfSingleUser = async (userId) => {
  const rechargeCollectionRef = firestore
    .collection("rechargeHistory")
    .where("uid", "==", userId);
  const recharges = await rechargeCollectionRef.get();
  const rechargesArray = [];
  recharges.forEach((doc) => {
    rechargesArray.push(doc.data());
  });
  return rechargesArray;
};

export const getAllPaymentOfSingleUser = async (userId) => {
  const paymentCollectionRef = firestore
    .collection("paymentHistory")
    .where("uid", "==", userId);
  const payments = await paymentCollectionRef.get();
  const paymentsArray = [];
  payments.forEach((doc) => {
    paymentsArray.push(doc.data());
  });
  return paymentsArray;
};

export const deleteExpressRatesDocuments = async (id) => {
  const countryRef = firestore.doc(`expressRatesDocuments/${id}`);
  const snapShot = await countryRef.get();
  console.log(snapShot.data());
  try {
    await countryRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteOffice = async (officeId) => {
  const officeRef = firestore.doc(`offices/${officeId}`);
  const snapShot = await officeRef.get();
  console.log(snapShot.data());
  try {
    await officeRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteBookingOffice = async (officeId) => {
  const officeRef = firestore.doc(`bookingOffices/${officeId}`);
  const snapShot = await officeRef.get();
  console.log(snapShot.data());
  try {
    await officeRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteCnf = async (cnfId) => {
  const cnfRef = firestore.doc(`cnfs/${cnfId}`);
  const snapShot = await cnfRef.get();
  console.log(snapShot.data());
  try {
    await cnfRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteEmployee = async (employeeId) => {
  const employeeRef = firestore.doc(`employees/${employeeId}`);
  const snapShot = await employeeRef.get();
  console.log(snapShot.data());
  try {
    await employeeRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteTransaction = async (id) => {
  const transactionRef = firestore.doc(`transactions/${id}`);
  const snapShot = await transactionRef.get();
  console.log(snapShot.data());
  try {
    await transactionRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteToken = async (token) => {
  try {
    const usersCollectionRef = firestore
      .collection("users")
      .where("deviceToken", "array-contains", token);

    const usersCollection = await usersCollectionRef.get();
    if (usersCollection.empty) {
      console.log(`No users found with the device token: ${token}`);
      return;
    }

    usersCollection.forEach(async (doc) => {
      const userRef = firestore.doc(`users/${doc.data().uid}`);
      const updatedTokens = doc
        .data()
        .deviceToken.filter((tok) => tok !== token);
      console.log(updatedTokens);
      // Update the deviceToken field only if it's different
      await userRef.update({ deviceToken: updatedTokens });
    });

    console.log(`Successfully removed token: ${token}`);
  } catch (error) {
    console.error(`Error removing token: ${token}`, error);
  }
};

export const getOrderTrackingResult = async (trackingNo) => {
  const ordersCollectionRef = firestore
    .collection("orders")
    .where("trackingNo", "==", trackingNo);
  try {
    const resultOrders = await ordersCollectionRef.get();
    let parcelsArray = [];
    resultOrders.forEach((doc) => {
      parcelsArray.push(doc.data());
    });
    if (parcelsArray.length == 0) {
      return null;
    }
    let lotArray = parcelsArray.map((parcel) => parcel.lotNo);
    let uniqueLotArray = [...new Set(lotArray)];
    let customerUidArray = parcelsArray.map((parcel) => parcel.customerUid);
    const lotNo = lotArray[0];
    const customerUid = customerUidArray[0];
    const lotRef = firestore.doc(`lots/${lotNo}`);
    const lotObj = await lotRef.get();
    const userRef = firestore.doc(`users/${customerUid}`);
    const userObj = await userRef.get();
    return {
      parcelsArray,
      lotObj: lotObj.data(),
      userObj: userObj.data(),
      lotArray: uniqueLotArray.length > 0 ? uniqueLotArray : [],
    };
  } catch (error) {
    alert(error);
    return null;
  }
};

export const uploadImageD2dExpressProduct = async (file) => {
  const imageRef = storage.ref(`d2dExpressProduct/${file.name}`);
  try {
    await imageRef.put(file);
    var imgUrl = [];
    await imageRef.getDownloadURL().then((url) => {
      console.log(url);
      imgUrl.push(url);
    });

    return imgUrl[0];
  } catch (error) {
    return null;
  }
};

export const deleteExpense = async (expenseId) => {
  const expenseRef = firestore.doc(`dailyExpenses/${expenseId}`);
  const snapShot = await expenseRef.get();
  console.log(snapShot.data());
  // Approved entries are already counted in cashSummary's running totals;
  // deleting one here would silently desync cashSummary from the real data.
  if (snapShot.exists && snapShot.data().status === "approved") {
    alert(
      "This expense has already been approved and is included in the cash summary totals. Approved entries cannot be deleted.",
    );
    return;
  }
  try {
    await expenseRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteForwarder = async (forwarderId) => {
  const forwarderRef = firestore.doc(`freight-forwarder/${forwarderId}`);
  const snapShot = await forwarderRef.get();
  console.log(snapShot.data());
  try {
    await forwarderRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteCashIn = async (cashId) => {
  const cashInRef = firestore.doc(`dailyCashIn/${cashId}`);
  const snapShot = await cashInRef.get();
  console.log(snapShot.data());
  // Approved entries are already counted in cashSummary's running totals;
  // deleting one here would silently desync cashSummary from the real data.
  if (snapShot.exists && snapShot.data().status === "approved") {
    alert(
      "This cash in has already been approved and is included in the cash summary totals. Approved entries cannot be deleted.",
    );
    return;
  }
  try {
    await cashInRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteExpressRatesParcel = async (id) => {
  const countryRef = firestore.doc(`expressRatesParcel/${id}`);
  const snapShot = await countryRef.get();
  console.log(snapShot.data());
  try {
    await countryRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteD2DRates = async (freightType, country, id) => {
  const productTypeRef = firestore.doc(
    `d2d-rates-${freightType}-${country}/${id}`,
  );
  const snapShot = await productTypeRef.get();
  console.log(snapShot.data());
  try {
    await productTypeRef.delete();
  } catch (error) {
    alert(error);
  }
};

export const deleteProductTax = async (id) => {
  const productRef = firestore.doc(`taxes/${id}`);
  try {
    await productRef.delete();
  } catch (error) {
    alert(error);
  }
};

export const updateProductTax = async (productObj) => {
  const productRef = firestore.doc(`taxes/${productObj.id}`);
  try {
    await productRef.update({ ...productObj });
  } catch (error) {
    alert(error);
  }
};

export const updateLot = async (lotObj) => {
  const lotRef = firestore.doc(`lots/${lotObj.lotNo}`);
  try {
    await lotRef.update({
      ...lotObj,
      // Same reasoning as uploadLot: derived after the spread so it cannot be
      // overwritten by a stale copy of itself.
      [LOT_DIGITS_FIELD]: lotNoDigits(lotObj.lotNo),
    });
    const snapShot = await lotRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};

export const updateNotice = async (noticeObj) => {
  const noticeRef = firestore.doc(`notices/${noticeObj.id}`);
  try {
    await noticeRef.update({
      ...noticeObj,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
    const snapShot = await noticeRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};

export const updateBlog = async (blogObj) => {
  const blogRef = firestore.doc(`blogs/${blogObj.id}`);
  try {
    await blogRef.update({ ...blogObj });
    const snapShot = await blogRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};

export const updateBooking = async (bookingObj) => {
  const bookingRef = firestore.doc(`bookingRequest/${bookingObj.bookingId}`);
  try {
    await bookingRef.update({
      bookingStatus: bookingObj.bookingStatus,
      ...bookingObj,
    });
    const snapShot = await bookingRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};
export const updateRefund = async (refundObj) => {
  try {
    // update refundStatus in main ordersArray
    const orderRef = firestore.doc(`orders/${refundObj.parcelId}`);
    const order = await orderRef.get();

    await orderRef.update({
      ...refundObj,
    });
    // refundObj is a full client-side copy of the order, so this write can
    // move any of the fields the lot totals are built from.
    await markLotOrdersChanged(
      (order.data() && order.data().lotNo) || refundObj.lotNo,
    );
    // create a recharge object

    // update refundStatus in users parcelArray
    // also input it in rechargeArray and transaction array of user
    // input it in rechargeHistory and rechargeDays of admin
    // FieldValue.increment() instead of read-then-write — approving several
    // refunds for the same customer in one batch (e.g. via Promise.all)
    // would otherwise race: two concurrent reads of the same starting
    // myWallet value would silently drop one of the credits.
    const userRef = firestore.doc(`users/${refundObj.customerUid}`);
    await userRef.update({
      myWallet: firebase.firestore.FieldValue.increment(
        parseInt(refundObj.refundAmount) || 0,
      ),
    });

    // update refund object status in refund request
    const refundRef = firestore.doc(`refundRequest/${refundObj.refundId}`);
    await refundRef.update({
      refundStatus: refundObj.refundStatus,
      refundAmount: refundObj.refundAmount,
    });
    const snapShot = await refundRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};

export const updateExpressRatesDocuments = async (countryObj) => {
  const countryRef = firestore.doc(
    `expressRatesDocuments/${countryObj.country}`,
  );
  try {
    await countryRef.update({ ...countryObj });
    const snapShot = await countryRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};
export const updateOffice = async (officeObj) => {
  const officeRef = firestore.doc(`offices/${officeObj.officeId}`);
  try {
    await officeRef.update({ ...officeObj });
    const snapShot = await officeRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};
export const updateBookingOffice = async (officeObj) => {
  const officeRef = firestore.doc(`bookingOffices/${officeObj.officeId}`);
  try {
    await officeRef.update({ ...officeObj });
    const snapShot = await officeRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};
export const updateCnf = async (cnfObj) => {
  const cnfRef = firestore.doc(`cnfs/${cnfObj.cnfId}`);
  try {
    await cnfRef.update({ ...cnfObj });
    const snapShot = await cnfRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};
export const updateEmployee = async (employeeObj) => {
  const employeeRef = firestore.doc(`employees/${employeeObj.employeeId}`);
  try {
    await employeeRef.update({ ...employeeObj });
    const snapShot = await employeeRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};
export const updateTransaction = async (transactionObj) => {
  const transactionRef = firestore.doc(`transactions/${transactionObj.id}`);
  try {
    await transactionRef.update({ ...transactionObj });
    const snapShot = await transactionRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};
export const updateExpense = async (expenseObj) => {
  const expenseRef = firestore.doc(`dailyExpenses/${expenseObj.id}`);
  try {
    // Approved entries are already counted in cashSummary's running totals;
    // editing the amount here would silently desync cashSummary from the
    // real data (only the approve flow is allowed to affect those totals).
    const currentSnap = await expenseRef.get();
    if (currentSnap.exists && currentSnap.data().status === "approved") {
      alert(
        "This expense has already been approved and is included in the cash summary totals. Approved entries cannot be edited.",
      );
      return currentSnap.data();
    }
    await expenseRef.update({ ...expenseObj });
    const snapShot = await expenseRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};
export const updateForwarder = async (forwarderObj) => {
  const forwarderRef = firestore.doc(`freight-forwarder/${forwarderObj.id}`);
  try {
    await forwarderRef.update({ ...forwarderObj });
    const snapShot = await forwarderRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};
export const updateSourcing = async (sourcingObj) => {
  const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);
  try {
    await sourcingRef.update({ ...sourcingObj });
    const snapShot = await sourcingRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};
export const updateSourcingRequest = async (sourcingObj) => {
  const sourcingRef = firestore.doc(
    `${sourcingObj.category}/${sourcingObj.id}`,
  );
  try {
    await sourcingRef.update({ ...sourcingObj });
    const snapShot = await sourcingRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};

export const updatePurchasing = async (purchasingObj) => {
  const purchasingRef = firestore.doc(`purchasing/${purchasingObj.id}`);
  try {
    await purchasingRef.update({ ...purchasingObj });
    const snapShot = await purchasingRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};

export const updateCashIn = async (cashInObj) => {
  const cashInRef = firestore.doc(`dailyCashIn/${cashInObj.id}`);
  try {
    // Approved entries are already counted in cashSummary's running totals;
    // editing the amount here would silently desync cashSummary from the
    // real data (only the approve flow is allowed to affect those totals).
    const currentSnap = await cashInRef.get();
    if (currentSnap.exists && currentSnap.data().status === "approved") {
      alert(
        "This cash in has already been approved and is included in the cash summary totals. Approved entries cannot be edited.",
      );
      return currentSnap.data();
    }
    await cashInRef.update({ ...cashInObj });
    const snapShot = await cashInRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};
export const updateExpressRatesParcel = async (countryObj) => {
  const countryRef = firestore.doc(`expressRatesParcel/${countryObj.country}`);
  try {
    await countryRef.update({ ...countryObj });
    const snapShot = await countryRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};
export const updateD2DRates = async (freightType, country, productTypeObj) => {
  const productTypeRef = firestore.doc(
    `d2d-rates-${freightType}-${country}/${productTypeObj.id}`,
  );
  try {
    await productTypeRef.update({ ...productTypeObj });
    const snapShot = await productTypeRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};

export const getSingleProductTax = async (id) => {
  const productRef = firestore.doc(`taxes/${id}`);
  try {
    const product = await productRef.get();
    return product.data();
  } catch (error) {
    alert(error);
  }
};
/**
 * Fetches just the lots named, ten per query.
 *
 * The counterpart to getMultipleUsers: pages that resolve a `lotNo` into a
 * status or a country need those lots, not the whole collection. Missing ids
 * are absent from the result rather than an error — a parcel can outlive the
 * lot record it names.
 */
// Lot search, server-side. A lot's document id *is* its lotNo, so a prefix
// range over documentId() answers this without any extra index.
//
// The pickers lowercase both sides before comparing, but a Firestore range is
// byte-ordered and case-sensitive, so the query is run both as typed and
// upper-cased — lot numbers are codes like "CB_711", and admins type them
// either way. Two cheap queries instead of downloading every lot.
//
// As with customer search this is prefix matching, not "contains": "711" will
// not find "CB_711". Typing the start of the lot number does.
export const LOT_SEARCH_LIMIT = 20;

// Prefix search over lot numbers, which are the document ids.
//
// `shipmentMethod` / `shipmentStatus` scope the search the same way
// getLotsPage scopes a page, so a search run from a list page cannot surface
// lots that page would never show. Callers with no scope (the lot pickers)
// search the whole collection exactly as before.
export const searchLots = async (
  query,
  {
    limit = LOT_SEARCH_LIMIT,
    shipmentMethod,
    shipmentStatus,
    excludeDelivered = false,
    deliveredOnly = false,
  } = {},
) => {
  const q = String(query || "").trim();
  if (!q) return [];

  // Non-empty only when the query is all digits. Everything about the digit
  // search below is skipped otherwise, so a query like "CB_59" takes exactly
  // the path it always has.
  const digits = lotDigitsQuery(q);

  const methods = methodsFor(shipmentMethod);
  const scoped = Boolean(methods || shipmentStatus);
  // Same rule as getLotsPage: a list that excludes delivered lots must exclude
  // them from its search too, or searching would surface a lot the list itself
  // will not show.
  const matchesScope = (lot) =>
    (!methods || methods.includes(lot.shipmentMethod)) &&
    (!shipmentStatus || lot.shipmentStatus === shipmentStatus) &&
    (!excludeDelivered || !lot.allDelivered) &&
    (!deliveredOnly || !!lot.allDelivered);

  // Any narrowing done after the read can eat most of a page, so fetch wider
  // and let the cap apply at the end.
  const narrowsAfterRead = scoped || excludeDelivered || deliveredOnly;

  // Lot numbers are stored upper-case; searching both spellings means a
  // lower-case query still matches.
  const variants = [...new Set([q, q.toUpperCase()])];

  const gather = async (applyScope, perQueryLimit) => {
    const snapshots = await Promise.all(
      variants.map((variant) => {
        let base = firestore.collection("lots");
        if (applyScope) {
          if (methods) base = base.where("shipmentMethod", "in", methods);
          if (shipmentStatus) {
            base = base.where("shipmentStatus", "==", shipmentStatus);
          }
        }
        return base
          .orderBy(firebase.firestore.FieldPath.documentId())
          .startAt(variant)
          .endAt(variant + "\uf8ff")
          .limit(perQueryLimit)
          .get();
      }),
    );

    const byLotNo = new Map();
    snapshots.forEach((snapshot) =>
      snapshot.forEach((doc) =>
        byLotNo.set(doc.id, { lotNo: doc.id, ...doc.data() }),
      ),
    );
    // Narrowing is left to matchesScope in `merge`, so there is one place
    // that decides whether a lot belongs in this list.
    return [...byLotNo.values()];
  };

  // Lot numbers are document ids, and a document id can only be matched from
  // its first character — so "590" can never find "CB_590" that way. The
  // digits are stored on the document as `lotNoDigits` precisely so this range
  // query can reach them, whatever prefix and separator the number was typed
  // with. A range over one field is served by Firestore's automatic
  // single-field index, so this needs no composite index; scope is applied
  // afterwards, the same way the missing-index fallback below does it.
  //
  // Lots written before the field existed simply do not come back here —
  // Firestore omits documents that lack the field being ordered on — and are
  // still found by the id-prefix search. Until the backfill runs, this costs
  // one extra query and changes nothing.
  const gatherDigits = async (perQueryLimit) => {
    // Run once, not per case variant: digits have no upper and lower case, so
    // the variants loop would buy two identical reads.
    const snapshot = await firestore
      .collection("lots")
      .orderBy(LOT_DIGITS_FIELD)
      .startAt(digits)
      .endAt(digits + "\uf8ff")
      .limit(perQueryLimit)
      .get();

    const found = [];
    snapshot.forEach((doc) => found.push({ lotNo: doc.id, ...doc.data() }));
    return found;
  };

  // A digit query is unscoped, so a broad one can come back full of lots this
  // list would never show. Over-fetch and let the scope filter cut it down —
  // same factor, same reason, as the fallback below.
  const digitResults = async () => {
    if (!digits) return [];
    try {
      return await gatherDigits(narrowsAfterRead ? limit * 5 : limit);
    } catch (error) {
      // searchLots has never thrown and its callers rely on that. A failure
      // here costs the digit matches, not the search.
      console.warn("Lot digit search failed:", error);
      return [];
    }
  };

  // Prefix hits are inserted first and the cap is applied last, so a lot found
  // by what the user literally typed can never be pushed out of the results by
  // a digit match.
  const merge = (prefixHits, digitHits) => {
    const byLotNo = new Map();
    prefixHits.forEach((lot) => byLotNo.set(lot.lotNo, lot));
    digitHits.forEach((lot) => {
      if (!byLotNo.has(lot.lotNo)) byLotNo.set(lot.lotNo, lot);
    });
    return [...byLotNo.values()].filter(matchesScope).slice(0, limit);
  };

  if (!scoped) {
    // Both reads go out together so adding the digit search does not double
    // how long a search takes.
    const [prefixHits, digitHits] = await Promise.all([
      gather(false, narrowsAfterRead ? limit * 5 : limit).catch((error) => {
        console.warn("Lot search failed:", error);
        return [];
      }),
      digitResults(),
    ]);
    return merge(prefixHits, digitHits);
  }

  const digitHitsPromise = digitResults();

  try {
    return merge(await gather(true, limit), await digitHitsPromise);
  } catch (error) {
    // Combining the scope filters with a document-id range needs a composite
    // index. Until it exists, search on the prefix alone and narrow the
    // (capped) result here — same answers for any realistic query, since lot
    // numbers are already prefixed per shipment method.
    console.warn(
      "Scoped lot search needs a Firestore index; falling back to an unscoped prefix search:",
      error,
    );
    try {
      const wide = await gather(false, limit * 5);
      return merge(wide, await digitHitsPromise);
    } catch (fallbackError) {
      console.warn("Lot search failed:", fallbackError);
      return merge([], await digitHitsPromise);
    }
  }
};

export const getMultipleLots = async (lotNos) => {
  const distinct = [...new Set((lotNos || []).filter(Boolean))];
  if (distinct.length === 0) return [];

  const snapshots = await Promise.all(
    chunkArray(distinct, 10).map((chunk) =>
      firestore
        .collection("lots")
        .where(firebase.firestore.FieldPath.documentId(), "in", chunk)
        .get(),
    ),
  );

  const lots = [];
  snapshots.forEach((snapshot) =>
    snapshot.forEach((doc) => lots.push({ lotNo: doc.id, ...doc.data() })),
  );
  return lots;
};

export const getSingleLot = async (id) => {
  const lotRef = firestore.doc(`lots/${id}`);
  try {
    const lot = await lotRef.get();
    return lot.data();
  } catch (error) {
    alert(error);
  }
};

// One user document. The profile page is deep-linkable
// (/users/list-user/:uid), and reading the whole users collection just to
// pick one out of it — which is what it did before, waiting on the
// getAllUsers preload that app.jsx fires behind five other awaits — made a
// cold load of that page take seconds. Returns null when the uid has no
// document so the caller can show a proper "not found" state rather than an
// endlessly blank page.
/**
 * Fetches just the users named, ten per query.
 *
 * The alternative this replaces is `getAllUsers()` — the entire collection,
 * several megabytes, pulled on every boot so that components could resolve a
 * `customerUid` into a name. A page shows tens of rows, not thousands, so it
 * only ever needed those. Built on the same chunked `documentId() in [...]`
 * shape as `readStoredLotTotals`: 10 ids per *query*, not 10 reads.
 *
 * Missing ids are simply absent from the result — a deleted customer must not
 * fail the page that references them.
 */
// Firestore has no substring or case-insensitive matching, so a customer
// search cannot be "contains" without holding every user in the browser —
// which is exactly what this replaces. Prefix ranges are what the index can
// answer: `startAt(q)` + `endAt(q + "\uf8ff")` bounds the scan to keys
// beginning with `q`, and \uf8ff is a very high code point, so it acts as
// "anything after this prefix".
//
// A range can only match a field exactly as it is stored, which used to mean
// "fm" never reached "FM" and "2" never reached "02". So the normalised keys
// (see utils/userSearchKeys.js) are searched too, and the *query* is put
// through the very same normalisation — canonicalising both sides is what
// makes those two pairs equal.
//
// The raw fields are still searched alongside them, with case and padding
// variants, because customers are created by the customer app and arrive with
// no keys at all. That fallback is what keeps a registration from this
// morning findable; stampUserSearchKeys then fills the keys in as the
// customer is read.
export const USER_SEARCH_LIMIT = 20;

// A capitalised spelling is the one most real names are stored under, so it
// is worth a query even though the lower-cased key usually answers first.
const capitalised = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value;

export const searchUsers = async (query, { limit = USER_SEARCH_LIMIT } = {}) => {
  const q = String(query || "").trim();
  if (!q) return [];

  const range = (field, value) =>
    firestore
      .collection("users")
      .orderBy(field)
      .startAt(value)
      .endAt(value + "\uf8ff")
      .limit(limit)
      .get()
      // A missing single-field index, or documents lacking the field, must
      // return nothing rather than break the picker.
      .catch((error) => {
        console.warn(`User search on ${field} failed:`, error);
        return { forEach: () => {} };
      });

  const digits = userIdDigitsQuery(q);
  const lower = nameLower(q);

  // Only worth padding a short number. Nobody's customer id is "0001234", so
  // for anything longer these would be two reads that can never match.
  const padded =
    digits && digits.length <= 2 ? [`0${digits}`, `00${digits}`] : [];

  // A phone number is not one or two digits, and searching a bare "2" against
  // mobiles returns noise for a read. Same reasoning for email: a query with
  // no letters is not the start of an address.
  const mobile = digits.length >= 4 ? mobileDigits(q) : "";
  const email = /[a-z@]/i.test(q) ? lower : "";

  // Field -> the spellings worth asking for. Deduped so an already-lowercase
  // query does not pay for the same read twice, and empty values dropped so a
  // non-numeric query never touches the numeric fields (and vice versa).
  const wanted = [
    [USER_ID_DIGITS_FIELD, [digits]],
    // Zero-padded spellings cover an unstamped customer whose id really is
    // "02": the canonical range above cannot see them until they are stamped.
    ["userId", [q, ...padded]],
    [NAME_LOWER_FIELD, [lower]],
    // The case variants are for customers the backfill has not reached — the
    // normalised key above answers for everyone else. Deduped below, so an
    // already-capitalised query costs one read, not three.
    ["displayName", [q, q.toUpperCase(), capitalised(q)]],
    [MOBILE_DIGITS_FIELD, [mobile]],
    [EMAIL_LOWER_FIELD, [email]],
  ];

  const queries = [];
  wanted.forEach(([field, values]) => {
    [...new Set(values.filter(Boolean))].forEach((value) => {
      queries.push(range(field, value));
    });
  });

  const snapshots = await Promise.all(queries);

  const byUid = new Map();
  snapshots.forEach((snapshot) =>
    snapshot.forEach((doc) => byUid.set(doc.id, { uid: doc.id, ...doc.data() })),
  );
  const users = [...byUid.values()];
  stampUserSearchKeys(users);
  return users;
};

// One page of suggestions is worth stamping; a bulk read is not. Beyond this
// the backfill script is the right tool, and a search must never turn into a
// write storm.
const STAMP_LIMIT = 20;

/**
 * Writes the normalised search keys onto any customer read without them.
 *
 * Customers are created by the customer app, so a new registration has no
 * keys and can only be found by the raw-field variants in searchUsers. This
 * makes the collection heal itself: the first time any admin so much as sees
 * a customer, they become exactly searchable for everyone afterwards.
 *
 * Deliberately fire-and-forget — never awaited, never able to fail a read.
 * The common case after the backfill is that nothing differs and this costs
 * one comparison per user and no write at all.
 */
export const stampUserSearchKeys = (users) => {
  try {
    const pending = [];
    (users || []).forEach((user) => {
      if (!user || !user.uid || pending.length >= STAMP_LIMIT) return;
      const changed = staleUserSearchKeys(user);
      if (changed) pending.push({ uid: user.uid, changed });
    });
    if (!pending.length) return;

    const batch = firestore.batch();
    pending.forEach(({ uid, changed }) => {
      batch.update(firestore.doc(`users/${uid}`), changed);
    });
    batch.commit().catch((error) => {
      // Rules may not allow it, or a customer may have been deleted between
      // the read and the write. Either way the search already returned.
      console.warn("Stamping customer search keys failed:", error);
    });
  } catch (error) {
    console.warn("Stamping customer search keys failed:", error);
  }
};

export const getMultipleUsers = async (uids) => {
  const distinct = [...new Set((uids || []).filter(Boolean))];
  if (distinct.length === 0) return [];

  const snapshots = await Promise.all(
    chunkArray(distinct, 10).map((chunk) =>
      firestore
        .collection("users")
        .where(firebase.firestore.FieldPath.documentId(), "in", chunk)
        .get(),
    ),
  );

  const users = [];
  snapshots.forEach((snapshot) =>
    snapshot.forEach((doc) => users.push({ uid: doc.id, ...doc.data() })),
  );
  // The rows on screen are exactly the customers an admin is about to search
  // for, so this is where an unstamped one is cheapest to fix.
  stampUserSearchKeys(users);
  return users;
};

export const getSingleUser = async (uid) => {
  const userRef = firestore.doc(`users/${uid}`);
  const snapShot = await userRef.get();
  if (!snapShot.exists) return null;
  return { uid: snapShot.id, ...snapShot.data() };
};

// get all users
export const getAllUsers = async () => {
  const usersCollectionRef = firestore.collection("users");
  try {
    const users = await usersCollectionRef.get();
    const usersArray = [];
    users.forEach((doc) => {
      // console.log(doc.id, " => ", doc.data());
      usersArray.push({ uid: doc.id, ...doc.data() });
    });
    return usersArray;
  } catch (error) {
    alert(error);
  }
};
// attendance for one calendar day (e.g. "2026-07-15") — powers the today-overview page
export const getAttendanceByDate = async (dateStr) => {
  const attendanceCollectionRef = firestore
    .collection("attendance")
    .where("date", "==", dateStr);
  try {
    const snapshot = await attendanceCollectionRef.get();
    const attendanceArray = [];
    snapshot.forEach((doc) => {
      attendanceArray.push({ id: doc.id, ...doc.data() });
    });
    return attendanceArray;
  } catch (error) {
    alert(error);
    return [];
  }
};

// one employee's punch history within a date range (inclusive) — powers the
// per-employee drill-down page, scoped to whatever month/range is currently
// selected there rather than fetching the employee's entire history at once.
// Note: this equality+range combo needs a Firestore composite index — the first
// time it runs, Firestore's error (if the index doesn't exist yet) includes a
// direct link to auto-create it; after that one-time setup it covers every
// employee/month/range combination, not just this one.
export const getAttendanceByEmployee = async (
  deviceUserId,
  startDateStr,
  endDateStr,
) => {
  const attendanceCollectionRef = firestore
    .collection("attendance")
    .where("deviceUserId", "==", deviceUserId)
    .where("date", ">=", startDateStr)
    .where("date", "<=", endDateStr);
  try {
    const snapshot = await attendanceCollectionRef.get();
    const attendanceArray = [];
    snapshot.forEach((doc) => {
      attendanceArray.push({ id: doc.id, ...doc.data() });
    });
    return attendanceArray;
  } catch (error) {
    // Firestore throws a "failed-precondition" error here the first time this
    // deviceUserId+date-range query shape runs without its composite index yet —
    // the error includes a direct link to auto-create it. Logging (not alert()) so
    // the full link is easy to copy out of the browser console.
    console.error("getAttendanceByEmployee failed:", error);
    return [];
  }
};

// Asks the office sync agent (attendance-sync-agent/, running on whichever
// office PC is currently on) to sync new punches. This is just a Firestore
// write, so it works the same from localhost or admin.alg.com.bd — the
// browser never talks to the ZKTeco device directly. See that project's
// README for the full request/status doc protocol.
export const requestAttendanceSync = async () => {
  await firestore.collection("syncRequests").doc("latest").set({
    requestedAt: firebase.firestore.FieldValue.serverTimestamp(),
    status: "pending",
  });
};

// Subscribes to the sync agent's last-run status (updated after every poll
// cycle, and again once a pending request above is handled). Returns the
// unsubscribe function.
export const subscribeToSyncStatus = (callback) => {
  return firestore
    .collection("syncStatus")
    .doc("latest")
    .onSnapshot((doc) => {
      callback(doc.exists ? doc.data() : null);
    });
};

export const getAllDeviceTokens = async () => {
  const tokensCollectionRef = firestore.collection("deviceTokens");
  try {
    const tokens = await tokensCollectionRef.get();
    const tokensArray = [];
    tokens.forEach((doc) => {
      // Every element, not just `deviceToken[0]`: a document usually holds
      // the one token that is also its id, but a second one there used to be
      // dropped on the floor.
      (doc.data().deviceToken || []).forEach((token) => {
        if (token) tokensArray.push(token);
      });
    });
    return tokensArray;
  } catch (error) {
    alert(error);
    return [];
  }
};

/**
 * Every device token worth broadcasting to, from both places this app keeps
 * them.
 *
 * The `deviceTokens` collection is the register the customer app writes on
 * launch, and it is the bigger of the two — but it is not a superset. Tokens
 * also live on the customer's own `users` document, which is what the
 * per-customer sends (a payment request being approved or rejected) read, and
 * a device can be in that list and not in the register. A broadcast that read
 * only the register would silently skip exactly those devices, while the
 * customer kept receiving their own notifications and had every reason to
 * think push was working.
 *
 * Costs a read of both collections, so it belongs to deliberate, occasional
 * events — a lot arriving at the local warehouse — and not to page loads.
 */
export const getBroadcastDeviceTokens = async () => {
  const [registered, users] = await Promise.all([
    getAllDeviceTokens(),
    getAllUsers(),
  ]);

  const tokens = new Set();
  (registered || []).forEach((token) => {
    if (token) tokens.add(token);
  });
  (users || []).forEach((user) => {
    ((user && user.deviceToken) || []).forEach((token) => {
      if (token) tokens.add(token);
    });
  });

  return [...tokens];
};

export const deleteUser = async (id) => {
  const productRef = firestore.doc(`users/${id}`);
  try {
    await productRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const getAllCustomerLoans = async () => {
  const usersCollectionRef = firestore.collection("customerLoans");
  try {
    const users = await usersCollectionRef.get();
    const usersArray = [];
    users.forEach((doc) => {
      // console.log(doc.id, " => ", doc.data());
      usersArray.push({ uid: doc.id, ...doc.data() });
    });
    return usersArray;
  } catch (error) {
    alert(error);
  }
};
export const getAllCustomerInstallments = async () => {
  const usersCollectionRef = firestore.collection("customerInstallments");
  try {
    const users = await usersCollectionRef.get();
    const usersArray = [];
    users.forEach((doc) => {
      // console.log(doc.id, " => ", doc.data());
      usersArray.push({ uid: doc.id, ...doc.data() });
    });
    return usersArray;
  } catch (error) {
    alert(error);
  }
};

// Orders management (get all orders)

// `getAllOrders` was removed here. It read the *entire* orders collection with
// no filter and no limit — at this dataset's size, tens of thousands of
// documents and the same number of billed reads in a single call. Nothing
// referenced it, so it was a landmine rather than a cost: the next person to
// need "all orders" would have found it and called it. Orders are read through
// bounded queries instead — by lot (`getAllOrdersOfSingleLot`), by lot list
// (`getAllOrdersByLots`, chunked), or by id (`getMultipleOrders`).

export const deleteOrder = async (id) => {
  const orderRef = firestore.doc(`orders/${id}`);
  try {
    await orderRef.delete();
  } catch (error) {
    alert(error);
  }
};

export const deleteSingleOrder = async (orderObj) => {
  const orderRef = firestore.doc(`orders/${orderObj.parcelId}`);
  try {
    await orderRef.delete();
    // Callers always hold the full order object here, so the lot it is leaving
    // is known without a re-read — which matters, because after the delete
    // there is nothing left to read it from.
    await markLotOrdersChanged(orderObj.lotNo);
  } catch (error) {
    alert(error);
  }
};

export const getSingleOrder = async (parcelId) => {
  const orderRef = firestore.doc(`orders/${parcelId}`);
  try {
    const snapShot = await orderRef.get();
    return snapShot.data();
  } catch (error) {
    return null;
  }
};
export const getSingleSourcing = async (sourcingId) => {
  const orderRef = firestore.doc(`sourcing/${sourcingId}`);
  try {
    const snapShot = await orderRef.get();
    return snapShot.data();
  } catch (error) {
    return null;
  }
};
export const deleteSourcingProduct = async (sourcingId, productId) => {
  const orderRef = firestore.doc(`sourcing/${sourcingId}`);
  try {
    const snapShot = await orderRef.get();
    await orderRef.update({
      productsArray: snapShot
        .data()
        .productsArray.filter((product) => product.id !== productId),
    });
    const updatedSnapShot = await orderRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    return null;
  }
};
export const deletePurchasingProduct = async (purchasingId, productId) => {
  const orderRef = firestore.doc(`purchasing/${purchasingId}`);
  try {
    const snapShot = await orderRef.get();
    await orderRef.update({
      productsArray: snapShot
        .data()
        .productsArray.filter((product) => product.id !== productId),
    });
    const updatedSnapShot = await orderRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    return null;
  }
};
export const getSinglePurchasing = async (purchasingId) => {
  const orderRef = firestore.doc(`purchasing/${purchasingId}`);
  try {
    const snapShot = await orderRef.get();
    return snapShot.data();
  } catch (error) {
    return null;
  }
};
export const getSingleBooking = async (bookingId) => {
  const bookingRef = firestore.doc(`bookingRequest/${bookingId}`);
  try {
    const snapShot = await bookingRef.get();
    return snapShot.data();
  } catch (error) {
    return null;
  }
};

// Warehouse intake: resolve a scanned/typed value to a booking request.
//
// The value is whatever the receiving desk has to hand — either the booking id
// printed on the customer's booking, or a courier tracking number, which
// customers now enter when they create a booking. One box accepts both so the
// desk can simply scan.
//
// Three lookups, cheapest first:
//   1. bookingId IS the document id, so that is a direct read — no index.
//   2. Newer bookings hold `trackingNumbers` as an array.
//   3. Older ones hold `trackingNo` as a comma-separated string; an equality
//      match therefore only finds bookings with a single tracking number on
//      them. That is a known limit of the old shape, not a bug here.
export const findBookingByIdOrTracking = async (rawValue) => {
  const value = String(rawValue || "").trim();
  if (!value) return null;

  const byId = await firestore.doc(`bookingRequest/${value}`).get();
  if (byId.exists) return byId.data();

  const bookings = firestore.collection("bookingRequest");

  const byArray = await bookings
    .where("trackingNumbers", "array-contains", value)
    .limit(1)
    .get();
  if (!byArray.empty) return byArray.docs[0].data();

  const byString = await bookings.where("trackingNo", "==", value).limit(1).get();
  if (!byString.empty) return byString.docs[0].data();

  return null;
};

// paymet management
// `getAllPayments` was removed here for the same reason as `getAllOrders`
// above: an unfiltered read of a collection that grows forever, referenced by
// nothing. Payments are read by day (`getAllPaymentDays` +
// `getAllPaymentsOfSingleDate`) or by user (`getUserRecordsPage`).

export const deletePayment = async (orderId) => {
  const paymentRef = firestore.doc(`payments/${orderId}`);
  try {
    await paymentRef.delete();
  } catch (error) {
    alert(error);
  }
};

// admins
export const getAllAdmins = async () => {
  const adminsCollectionRef = firestore.collection("admins");
  try {
    const admins = await adminsCollectionRef.get();
    const adminsArray = [];
    admins.forEach((doc) => {
      // console.log(doc.id, " => ", doc.data());
      adminsArray.push({ adminId: doc.id, ...doc.data() });
    });
    return adminsArray;
  } catch (error) {
    alert(error);
  }
};

export const updateProfileImage = async (imgUrl, id) => {
  const adminRef = firestore.doc(`admins/${id}`);
  try {
    const admin = await adminRef.get();
    await adminRef.update({ ...admin.data(), image: imgUrl });
  } catch (error) {
    alert(error);
  }
};

export const getAllp2p = async (status) => {
  const p2pCollectionRef = firestore
    .collection("p2p")
    .where("status", "==", status);
  try {
    const p2ps = await p2pCollectionRef.get();
    const p2psArray = [];
    p2ps.forEach((doc) => {
      p2psArray.push(doc.data());
    });
    return p2psArray.sort((a, b) => b.id - a.id);
  } catch (error) {
    alert(error);
  }
};
export const getAllUnpaidp2p = async () => {
  const p2pCollectionRef = firestore.collection("p2p");
  try {
    const p2ps = await p2pCollectionRef.get();
    const p2psArray = [];
    p2ps.forEach((doc) => {
      const data = doc.data();
      if (data.paymentStatus !== "Paid") {
        p2psArray.push(data);
      }
    });
    return p2psArray.sort((a, b) => b.id - a.id);
  } catch (error) {
    alert(error);
  }
};
export const getAllp2pByDate = async (startDate, endDate) => {
  // Convert string dates → timestamps
  const startTimestamp = new Date(startDate + "T00:00:00").getTime();
  const endTimestamp = new Date(endDate + "T23:59:59.999").getTime();
  const p2pCollectionRef = firestore
    .collection("p2p")
    .where("id", ">=", startTimestamp)
    .where("id", "<=", endTimestamp);
  try {
    const p2ps = await p2pCollectionRef.get();
    const p2psArray = [];
    p2ps.forEach((doc) => {
      p2psArray.push(doc.data());
    });
    return p2psArray.sort((a, b) => b.id - a.id);
  } catch (error) {
    alert(error);
  }
};
export const getAllExpensesByDate = async (startDate, endDate) => {
  // Convert string dates → timestamps
  const startTimestamp = String(new Date(startDate + "T00:00:00").getTime());
  const endTimestamp = String(new Date(endDate + "T23:59:59.999").getTime());
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("id", ">=", startTimestamp)
    .where("id", "<=", endTimestamp);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray.sort((a, b) => b.id - a.id);
  } catch (error) {
    alert(error);
  }
};
export const getAllWarehouseProductsp2p = async (status) => {
  const p2pCollectionRef = firestore
    .collection("p2p")
    .where("status", "==", status)
    .where("deliveryWarehouse", "==", false);
  try {
    const p2ps = await p2pCollectionRef.get();
    const p2psArray = [];
    p2ps.forEach((doc) => {
      p2psArray.push(doc.data());
    });
    return p2psArray.sort((a, b) => b.id - a.id);
  } catch (error) {
    alert(error);
  }
};
export const getAllDeliveryWarehouseProductsp2p = async (status) => {
  const p2pCollectionRef = firestore
    .collection("p2p")
    .where("status", "==", status)
    .where("deliveryWarehouse", "==", true);
  try {
    const p2ps = await p2pCollectionRef.get();
    const p2psArray = [];
    p2ps.forEach((doc) => {
      p2psArray.push(doc.data());
    });
    return p2psArray.sort((a, b) => b.id - a.id);
  } catch (error) {
    alert(error);
  }
};
export const getAllP2pAgent = async (status) => {
  var today = new Date();
  var priorDate = new Date(new Date().setDate(today.getDate() - 30)).getTime();
  const p2pCollectionRef = firestore
    .collection("p2pAgentRequest")
    .where("status", "==", status)
    .where("id", ">", priorDate);
  try {
    const p2ps = await p2pCollectionRef.get();
    const p2psArray = [];
    p2ps.forEach((doc) => {
      p2psArray.push(doc.data());
    });
    return p2psArray.sort((a, b) => b.id - a.id);
  } catch (error) {
    alert(error);
  }
};
export const getAllP2pMember = async (status) => {
  const p2pCollectionRef = firestore
    .collection("p2pMemberRequest")
    .where("status", "==", status);

  try {
    const p2ps = await p2pCollectionRef.get();
    const p2psArray = [];
    p2ps.forEach((doc) => {
      p2psArray.push(doc.data());
    });
    return p2psArray.sort((a, b) => a.userId - b.userId);
  } catch (error) {
    alert(error);
  }
};
export const getAllBanners = async () => {
  const productsCollectionRef = firestore.collection("banners");

  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    alert(error);
  }
};

export const uploadBanner = async (productObj) => {
  const productRef = firestore.doc(`banners/${productObj.id}`);
  const snapShot = await productRef.get();
  const newProductObj = { ...productObj, file: "" };
  if (productObj.secondBanner) {
    const collectionRef = firestore
      .collection(`banners`)
      .where("secondBanner", "==", true);
    const collection = await collectionRef.get();
    collection.forEach(async (doc) => {
      const bannerRef = firestore.doc(`banners/${doc.data().id}`);
      await bannerRef.update({
        secondBanner: false,
      });
    });
  }
  if (!snapShot.exists) {
    try {
      await productRef.set({
        ...newProductObj,
        createdAt: createdAtStamp(),
      });
      const updatedSnapShot = await productRef.get();
      return updatedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert("there is already a category with similar id");
  }
};

export const updateBanner = async (productObj) => {
  const productRef = firestore.doc(`banners/${productObj.id}`);
  const product = await productRef.get();
  if (productObj.secondBanner) {
    const collectionRef = firestore
      .collection(`banners`)
      .where("secondBanner", "==", true);
    const collection = await collectionRef.get();
    collection.forEach(async (doc) => {
      const bannerRef = firestore.doc(`banners/${doc.data().id}`);
      await bannerRef.update({
        secondBanner: false,
      });
    });
  }
  try {
    delete productObj.file;
    await productRef.update({ ...productObj });
    const updatedSnapShot = await productRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    alert(error);
  }
};

export const deleteBanner = async (id) => {
  const productRef = firestore.doc(`banners/${id}`);
  try {
    await productRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const uploadp2p = async (p2pObj) => {
  const p2pRef = firestore.doc(`p2p/${p2pObj.id}`);
  const snapShot = await p2pRef.get();
  if (!snapShot.exists) {
    try {
      await p2pRef.set({
        ...p2pObj,
        createdAt: createdAtStamp(),
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await p2pRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      alert(error);
    }
  } else {
    alert(
      "there is already a booking with similar id, please change the country name and try again",
    );
  }
};

export const updatep2p = async (p2pObj) => {
  const p2pRef = firestore.doc(`p2p/${p2pObj.id}`);
  try {
    await p2pRef.update({ ...p2pObj });
    const snapShot = await p2pRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};

export const updateP2pAgentRequest = async (reqObj) => {
  const p2pRef = firestore.doc(`p2pAgentRequest/${reqObj.id}`);
  try {
    await p2pRef.update({ ...reqObj });
    const snapShot = await p2pRef.get();
    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};
export const updateP2pMemberRequest = async (reqObj) => {
  const p2pRef = firestore.doc(`p2pMemberRequest/${reqObj.uid}`);
  try {
    await p2pRef.update({ ...reqObj });
    const snapShot = await p2pRef.get();
    const userRef = firestore.doc(`users/${reqObj.uid}`);
    if (reqObj.status == "Approved") {
      await userRef.update({
        p2pAgent: true,
        agentsName: reqObj.agentsName,
        agentsMobileNo: reqObj.agentsMobileNo,
        agentsPostcode: reqObj.agentsPostcode,
        agentsAddress: reqObj.agentsAddress,
        agentsNid: reqObj.agentsNid,
        agentsImageUrl: reqObj.imageUrl,
      });
    } else {
      await userRef.update({
        p2pAgent: false,
        p2pMemberRequest: false,
      });
    }

    return snapShot.data();
  } catch (error) {
    alert(error);
  }
};

export const deletep2p = async (p2pId) => {
  const p2pRef = firestore.doc(`p2p/${p2pId}`);
  const snapShot = await p2pRef.get();
  console.log(snapShot.data());
  try {
    await p2pRef.delete();
  } catch (error) {
    alert(error);
  }
};
export const deleteAllLastMonthUnreceivedApprovedP2p = async () => {
  console.log(
    "delete all last month unreceived approved p2p is getting called!",
  );
  var today = new Date();
  var priorDate = new Date(new Date().setDate(today.getDate() - 30)).getTime();
  console.log(priorDate);
  const p2pCollectionRef = firestore
    .collection(`p2p`)
    .where("status", "==", "approved")
    .where("id", "<", priorDate);
  const p2ps = await p2pCollectionRef.get();
  console.log(p2ps);
  p2ps.forEach(async (doc) => {
    console.log(doc.data().id);
    const p2pRef = firestore.doc(`p2p/${doc.data().id}`);
    await p2pRef.delete();
  });
};

export const updateAgentStatusIfNotProductReceivedInDay = async () => {
  var today = new Date();
  var priorDate = new Date(new Date().setDate(today.getDate() - 2)).getTime();
  const p2pCollectionRef = firestore
    .collection(`p2p`)
    .where("agentStatus", "==", "Pending")
    .where("assignedTime", "<", priorDate);
  const p2ps = await p2pCollectionRef.get();
  p2ps.forEach(async (doc) => {
    console.log(doc.data().id);
    const p2pRef = firestore.doc(`p2p/${doc.data().id}`);
    await p2pRef.update({
      agentId: "",
      agentStatus: "",
      assignedTime: "",
    });
  });
};

// ---------------------------------------------------------------------
// Hand Carry
//
// Two collections:
//  - `handCarry/{id}`: one doc per order.
//  - `handCarryMonthlyStats/{month}`: one doc per month, holding running
//    totals only (totalInvoices/totalBill/totalPaid/totalShippingCost/
//    totalProfit/totalRefundGiven). The month-list page and the
//    month-detail "Order Summary" bar read ONLY this collection/doc —
//    never by fetching and summing individual `handCarry` orders — so
//    those reads stay cheap and flat no matter how many orders exist.
//
// Every write below keeps both collections (plus a `dailyCashIn`/
// `dailyExpenses` ledger entry where money moves) in sync atomically,
// via `firestore.runTransaction()` wherever a value is derived from a
// freshly-read current total (payment/shipping-cost/refund amounts,
// edits, deletes), or a plain `firestore.batch()` where the write is
// purely additive with no dependency on the current value (create).
// The monthly-stats side of every write uses
// `firebase.firestore.FieldValue.increment()` inside `set(..., {merge:
// true})`, so it never needs its own read — matching the existing
// `monthlyExpense`/`monthlyCashIn` convention in this file.
// ---------------------------------------------------------------------

export const uploadImageHandCarry = async (file) => {
  const imageRef = storage.ref(`handCarry/${file.name}`);
  try {
    await imageRef.put(file);
    const url = await imageRef.getDownloadURL();
    return url;
  } catch (error) {
    return null;
  }
};

// Total Bill and Total Weight are never trusted from the client — they're
// always the sum of each product row's own bill/weight, recomputed here so
// a stale form state (or a tampered request) can't desync the order total
// from what its line items actually add up to.
const describeHandCarryProducts = (products) => {
  const list = Array.isArray(products) ? products : [];
  if (list.length === 0) return "No products";
  const first = list[0].name || "Unnamed product";
  return list.length > 1 ? `${first} +${list.length - 1} more` : first;
};

const sumHandCarryProducts = (products) => {
  const list = Array.isArray(products) ? products : [];
  return list.reduce(
    (totals, product) => ({
      totalBill: totals.totalBill + (parseFloat(product.bill) || 0),
      totalWeight: totals.totalWeight + (parseFloat(product.weight) || 0),
      totalShippingBill:
        totals.totalShippingBill + (parseFloat(product.shippingCost) || 0),
    }),
    { totalBill: 0, totalWeight: 0, totalShippingBill: 0 },
  );
};

export const createHandCarryOrder = async (orderObj) => {
  const orderRef = firestore.doc(`handCarry/${orderObj.id}`);
  const statsRef = firestore.doc(`handCarryMonthlyStats/${orderObj.month}`);
  const { totalBill, totalWeight, totalShippingBill } = sumHandCarryProducts(
    orderObj.products,
  );
  const batch = firestore.batch();

  batch.set(orderRef, {
    ...orderObj,
    totalBill,
    totalWeight,
    totalShippingBill,
    totalPaid: 0,
    totalShippingCost: 0,
    totalProfit: totalBill,
    totalRefundGiven: 0,
    invoiceStatus: "Not Paid",
    physicalStatus: "Pending",
    cashIns: [],
    cashOuts: [],
    refunds: [],
  });
  batch.set(
    statsRef,
    {
      month: orderObj.month,
      totalInvoices: firebase.firestore.FieldValue.increment(1),
      totalBill: firebase.firestore.FieldValue.increment(totalBill),
      totalPaid: firebase.firestore.FieldValue.increment(0),
      totalShippingCost: firebase.firestore.FieldValue.increment(0),
      totalShippingBill:
        firebase.firestore.FieldValue.increment(totalShippingBill),
      totalProfit: firebase.firestore.FieldValue.increment(totalBill),
      totalRefundGiven: firebase.firestore.FieldValue.increment(0),
    },
    { merge: true },
  );

  try {
    await batch.commit();
    const orderSnapShot = await orderRef.get();
    const statsSnapShot = await statsRef.get();
    return { order: orderSnapShot.data(), stats: statsSnapShot.data() };
  } catch (error) {
    alert(error);
    return false;
  }
};

// Only the editable order-identity fields are accepted here — money/status
// fields (totalPaid, totalShippingCost, invoiceStatus, physicalStatus,
// cashIns/cashOuts/refunds) are always taken from the freshly-read current
// doc, never from the caller, so a stale edit-form snapshot can never
// clobber a payment/shipping-cost/refund that landed after the form opened.
export const updateHandCarryOrder = async (orderObj) => {
  const orderRef = firestore.doc(`handCarry/${orderObj.id}`);
  const statsRef = firestore.doc(`handCarryMonthlyStats/${orderObj.month}`);

  try {
    await firestore.runTransaction(async (t) => {
      const orderSnapShot = await t.get(orderRef);
      const currentData = orderSnapShot.data();

      const oldBill = parseFloat(currentData.totalBill) || 0;
      const oldShippingBill = parseFloat(currentData.totalShippingBill) || 0;
      const {
        totalBill: newBill,
        totalWeight,
        totalShippingBill: newShippingBill,
      } = sumHandCarryProducts(orderObj.products);
      const shippingCost = parseFloat(currentData.totalShippingCost) || 0;
      const paid = parseFloat(currentData.totalPaid) || 0;
      const oldProfit = parseFloat(currentData.totalProfit) || 0;
      const newProfit = newBill - shippingCost;

      t.update(orderRef, {
        customer: orderObj.customer,
        customerUid: orderObj.customerUid,
        customerName: orderObj.customerName,
        customerNumber: orderObj.customerNumber,
        customerAddress: orderObj.customerAddress,
        carrierName: orderObj.carrierName,
        carrierNumber: orderObj.carrierNumber,
        carrierAddress: orderObj.carrierAddress,
        products: orderObj.products,
        totalBill: newBill,
        totalWeight,
        totalShippingBill: newShippingBill,
        totalProfit: newProfit,
        invoiceStatus: paid >= newBill ? "Paid" : "Not Paid",
      });

      t.set(
        statsRef,
        {
          totalBill: firebase.firestore.FieldValue.increment(newBill - oldBill),
          totalShippingBill: firebase.firestore.FieldValue.increment(
            newShippingBill - oldShippingBill,
          ),
          totalProfit: firebase.firestore.FieldValue.increment(
            newProfit - oldProfit,
          ),
        },
        { merge: true },
      );
    });
    const updatedSnapShot = await orderRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    alert(error);
    return false;
  }
};

export const deleteHandCarryOrder = async (orderId, month) => {
  const orderRef = firestore.doc(`handCarry/${orderId}`);
  const statsRef = firestore.doc(`handCarryMonthlyStats/${month}`);

  try {
    await firestore.runTransaction(async (t) => {
      const orderSnapShot = await t.get(orderRef);
      const currentData = orderSnapShot.data();
      if (!currentData) return;

      t.delete(orderRef);
      t.set(
        statsRef,
        {
          totalInvoices: firebase.firestore.FieldValue.increment(-1),
          totalBill: firebase.firestore.FieldValue.increment(
            -(parseFloat(currentData.totalBill) || 0),
          ),
          totalPaid: firebase.firestore.FieldValue.increment(
            -(parseFloat(currentData.totalPaid) || 0),
          ),
          totalShippingCost: firebase.firestore.FieldValue.increment(
            -(parseFloat(currentData.totalShippingCost) || 0),
          ),
          totalShippingBill: firebase.firestore.FieldValue.increment(
            -(parseFloat(currentData.totalShippingBill) || 0),
          ),
          totalProfit: firebase.firestore.FieldValue.increment(
            -(parseFloat(currentData.totalProfit) || 0),
          ),
          totalRefundGiven: firebase.firestore.FieldValue.increment(
            -(parseFloat(currentData.totalRefundGiven) || 0),
          ),
        },
        { merge: true },
      );
    });
    return true;
  } catch (error) {
    alert(error);
    return false;
  }
};

// Pure metadata (where the item physically is) — never touches money or
// monthly stats, so a plain update is safe and sufficient.
export const updateHandCarryPhysicalStatus = async (
  orderId,
  physicalStatus,
) => {
  const orderRef = firestore.doc(`handCarry/${orderId}`);
  try {
    await orderRef.update({ physicalStatus });
    const updatedSnapShot = await orderRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    alert(error);
    return false;
  }
};

export const handleHandCarryCashIn = async ({
  orderId,
  month,
  amount,
  receiveBy,
}) => {
  const orderRef = firestore.doc(`handCarry/${orderId}`);
  const statsRef = firestore.doc(`handCarryMonthlyStats/${month}`);
  const paymentId = `${Date.now().toString()}${Math.floor(Math.random() * 1000)}`;
  const cashInRef = firestore.doc(`dailyCashIn/${paymentId}`);
  const date = new Date().toLocaleDateString("en-GB");

  try {
    await firestore.runTransaction(async (t) => {
      const orderSnapShot = await t.get(orderRef);
      const currentData = orderSnapShot.data();

      const delta = parseFloat(amount) || 0;
      const totalBill = parseFloat(currentData.totalBill) || 0;
      const totalShippingCost = parseFloat(currentData.totalShippingCost) || 0;
      const newTotalPaid = (parseFloat(currentData.totalPaid) || 0) + delta;
      const newCashIns = [
        { id: paymentId, amount: delta, date, expenseBy: receiveBy },
        ...(currentData.cashIns || []),
      ];

      t.set(cashInRef, {
        amount: delta,
        category: "Hand Carry",
        subCategory: `${orderId} - ${describeHandCarryProducts(currentData.products)}`,
        note: `Total Bill: ${totalBill}\nTotal Paid: ${newTotalPaid}\nTotal Shipping Cost: ${totalShippingCost}`,
        date,
        id: paymentId,
        month,
        receiveBy,
        status: "pending",
        unEditable: true,
      });

      t.update(orderRef, {
        totalPaid: newTotalPaid,
        invoiceStatus: newTotalPaid >= totalBill ? "Paid" : "Not Paid",
        cashIns: newCashIns,
      });

      t.set(
        statsRef,
        { totalPaid: firebase.firestore.FieldValue.increment(delta) },
        { merge: true },
      );
    });
    const updatedSnapShot = await orderRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    alert(error);
    return false;
  }
};

export const handleHandCarryCashOut = async ({
  orderId,
  month,
  amount,
  expenseBy,
}) => {
  const orderRef = firestore.doc(`handCarry/${orderId}`);
  const statsRef = firestore.doc(`handCarryMonthlyStats/${month}`);
  const expenseId = `${Date.now().toString()}${Math.floor(Math.random() * 1000)}`;
  const expenseRef = firestore.doc(`dailyExpenses/${expenseId}`);
  const date = new Date().toLocaleDateString("en-GB");

  try {
    await firestore.runTransaction(async (t) => {
      const orderSnapShot = await t.get(orderRef);
      const currentData = orderSnapShot.data();

      const delta = parseFloat(amount) || 0;
      const totalBill = parseFloat(currentData.totalBill) || 0;
      const totalPaid = parseFloat(currentData.totalPaid) || 0;
      const newShippingCost =
        (parseFloat(currentData.totalShippingCost) || 0) + delta;
      const newProfit = totalBill - newShippingCost;
      const newCashOuts = [
        { id: expenseId, amount: delta, date, expenseBy },
        ...(currentData.cashOuts || []),
      ];

      t.set(expenseRef, {
        amount: delta,
        category: "Hand Carry",
        subCategory: `${orderId} - ${describeHandCarryProducts(currentData.products)}`,
        note: `Total Bill: ${totalBill}\nTotal Paid: ${totalPaid}\nTotal Shipping Cost: ${newShippingCost}`,
        date,
        id: expenseId,
        month,
        expenseBy,
        status: "pending",
        unEditable: true,
      });

      t.update(orderRef, {
        totalShippingCost: newShippingCost,
        totalProfit: newProfit,
        cashOuts: newCashOuts,
      });

      t.set(
        statsRef,
        {
          totalShippingCost: firebase.firestore.FieldValue.increment(delta),
          totalProfit: firebase.firestore.FieldValue.increment(-delta),
        },
        { merge: true },
      );
    });
    const updatedSnapShot = await orderRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    alert(error);
    return false;
  }
};

// Bulk "Settle All" — pays off the FULL remaining customer-due and
// shipping-due for every selected order in one click, no amount entry.
// Firestore transactions require every t.get() to happen before any
// t.set/update, so this is a strict two-phase read-all-then-write-all
// (never a per-order sequential get->write loop). Chunked at 100 orders
// per transaction to stay well under Firestore's 500-writes-per-transaction
// cap (worst case here is 3 writes/order + 1 shared stats write); chunks
// are not atomic with each other, an accepted tradeoff given realistic
// per-month order counts.
const HANDCARRY_SETTLE_CHUNK_SIZE = 100;

const settleHandCarryOrdersChunk = async (orderIds, month, adminName) => {
  const statsRef = firestore.doc(`handCarryMonthlyStats/${month}`);
  const date = new Date().toLocaleDateString("en-GB");
  const updatedOrders = [];

  await firestore.runTransaction(async (t) => {
    const orderRefs = orderIds.map((id) => firestore.doc(`handCarry/${id}`));
    const snapshots = await Promise.all(orderRefs.map((ref) => t.get(ref)));

    const statsDelta = { totalPaid: 0, totalShippingCost: 0, totalProfit: 0 };

    snapshots.forEach((snap, i) => {
      if (!snap.exists) return;
      const orderId = orderIds[i];
      const orderRef = orderRefs[i];
      const currentData = snap.data();
      if (currentData.month !== month) return;

      const totalBill = parseFloat(currentData.totalBill) || 0;
      const totalPaid = parseFloat(currentData.totalPaid) || 0;
      const totalShippingBill = parseFloat(currentData.totalShippingBill) || 0;
      const totalShippingCost = parseFloat(currentData.totalShippingCost) || 0;

      const customerDue = totalBill - totalPaid;
      const shippingDue = totalShippingBill - totalShippingCost;
      const doCashIn = customerDue > 0;
      const doCashOut = shippingDue > 0;
      if (!doCashIn && !doCashOut) return;

      let newTotalPaid = totalPaid;
      let newTotalShippingCost = totalShippingCost;
      let newCashIns = currentData.cashIns || [];
      let newCashOuts = currentData.cashOuts || [];

      if (doCashIn) {
        // Loop index + role suffix avoid id collisions: unlike the
        // single-order cash-in/cash-out functions, this loop can mint
        // several ids within the same millisecond.
        const paymentId = `${Date.now()}${Math.floor(Math.random() * 1000)}${i}in`;
        newTotalPaid = totalPaid + customerDue;
        newCashIns = [
          { id: paymentId, amount: customerDue, date, expenseBy: adminName },
          ...newCashIns,
        ];
        t.set(firestore.doc(`dailyCashIn/${paymentId}`), {
          amount: customerDue,
          category: "Hand Carry",
          subCategory: `${orderId} - ${describeHandCarryProducts(currentData.products)}`,
          note: `Bulk Settle — Total Bill: ${totalBill}\nTotal Paid: ${newTotalPaid}\nTotal Shipping Cost: ${totalShippingCost}`,
          date,
          id: paymentId,
          month,
          receiveBy: adminName,
          status: "pending",
          unEditable: true,
        });
        statsDelta.totalPaid += customerDue;
      }

      if (doCashOut) {
        const expenseId = `${Date.now()}${Math.floor(Math.random() * 1000)}${i}out`;
        newTotalShippingCost = totalShippingCost + shippingDue;
        newCashOuts = [
          { id: expenseId, amount: shippingDue, date, expenseBy: adminName },
          ...newCashOuts,
        ];
        t.set(firestore.doc(`dailyExpenses/${expenseId}`), {
          amount: shippingDue,
          category: "Hand Carry",
          subCategory: `${orderId} - ${describeHandCarryProducts(currentData.products)}`,
          note: `Bulk Settle — Total Bill: ${totalBill}\nTotal Paid: ${newTotalPaid}\nTotal Shipping Cost: ${newTotalShippingCost}`,
          date,
          id: expenseId,
          month,
          expenseBy: adminName,
          status: "pending",
          unEditable: true,
        });
        statsDelta.totalShippingCost += shippingDue;
        // totalProfit stays totalBill - totalShippingCost (cash-paid basis);
        // totalShippingBill never enters this formula.
        statsDelta.totalProfit -= shippingDue;
      }

      const orderUpdate = {
        totalPaid: newTotalPaid,
        totalShippingCost: newTotalShippingCost,
        totalProfit: totalBill - newTotalShippingCost,
        invoiceStatus: newTotalPaid >= totalBill ? "Paid" : "Not Paid",
        cashIns: newCashIns,
        cashOuts: newCashOuts,
      };
      t.update(orderRef, orderUpdate);
      updatedOrders.push({ ...currentData, ...orderUpdate });
    });

    if (
      statsDelta.totalPaid ||
      statsDelta.totalShippingCost ||
      statsDelta.totalProfit
    ) {
      t.set(
        statsRef,
        {
          totalPaid: firebase.firestore.FieldValue.increment(
            statsDelta.totalPaid,
          ),
          totalShippingCost: firebase.firestore.FieldValue.increment(
            statsDelta.totalShippingCost,
          ),
          totalProfit: firebase.firestore.FieldValue.increment(
            statsDelta.totalProfit,
          ),
        },
        { merge: true },
      );
    }
  });

  return updatedOrders;
};

export const settleHandCarryOrders = async ({ orderIds, month, adminName }) => {
  const ids = Array.isArray(orderIds) ? orderIds : [];
  const chunks = [];
  for (let i = 0; i < ids.length; i += HANDCARRY_SETTLE_CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + HANDCARRY_SETTLE_CHUNK_SIZE));
  }

  try {
    const results = [];
    for (const chunk of chunks) {
      // Sequential, not Promise.all: chunks share the same statsRef, so
      // running them concurrently only adds contention with no throughput
      // gain.
      results.push(
        ...(await settleHandCarryOrdersChunk(chunk, month, adminName)),
      );
    }
    return results;
  } catch (error) {
    alert(error);
    return false;
  }
};

export const handleHandCarryRefund = async ({
  orderId,
  month,
  amount,
  expenseBy,
}) => {
  const orderRef = firestore.doc(`handCarry/${orderId}`);
  const statsRef = firestore.doc(`handCarryMonthlyStats/${month}`);
  const expenseId = `${Date.now().toString()}${Math.floor(Math.random() * 1000)}`;
  const expenseRef = firestore.doc(`dailyExpenses/${expenseId}`);
  const date = new Date().toLocaleDateString("en-GB");

  try {
    await firestore.runTransaction(async (t) => {
      const orderSnapShot = await t.get(orderRef);
      const currentData = orderSnapShot.data();

      const delta = parseFloat(amount) || 0;
      const totalBill = parseFloat(currentData.totalBill) || 0;
      const totalPaid = parseFloat(currentData.totalPaid) || 0;
      const totalShippingCost = parseFloat(currentData.totalShippingCost) || 0;
      const newRefundGiven =
        (parseFloat(currentData.totalRefundGiven) || 0) + delta;
      const newRefunds = [
        { id: expenseId, amount: delta, date, expenseBy },
        ...(currentData.refunds || []),
      ];

      t.set(expenseRef, {
        amount: delta,
        category: "Hand Carry",
        subCategory: `${orderId} - ${describeHandCarryProducts(currentData.products)}`,
        note: `Total Bill: ${totalBill}\nTotal Paid: ${totalPaid}\nTotal Shipping Cost: ${totalShippingCost}`,
        date,
        id: expenseId,
        month,
        expenseBy,
        status: "pending",
        unEditable: true,
      });

      t.update(orderRef, {
        totalRefundGiven: newRefundGiven,
        refunds: newRefunds,
      });

      t.set(
        statsRef,
        { totalRefundGiven: firebase.firestore.FieldValue.increment(delta) },
        { merge: true },
      );
    });
    const updatedSnapShot = await orderRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    alert(error);
    return false;
  }
};

export const getAllHandCarryMonthlyStats = async () => {
  const statsCollectionRef = firestore.collection("handCarryMonthlyStats");
  try {
    const stats = await statsCollectionRef.get();
    const statsArray = [];
    stats.forEach((doc) => {
      statsArray.push(doc.data());
    });
    return statsArray;
  } catch (error) {
    alert(error);
    return [];
  }
};

export const getHandCarryMonthlyStats = async (month) => {
  const statsRef = firestore.doc(`handCarryMonthlyStats/${month}`);
  try {
    const statsSnapShot = await statsRef.get();
    return statsSnapShot.exists ? statsSnapShot.data() : null;
  } catch (error) {
    alert(error);
    return null;
  }
};

export const getAllHandCarryOrdersByMonth = async (month) => {
  const ordersCollectionRef = firestore
    .collection("handCarry")
    .where("month", "==", month);
  try {
    const orders = await ordersCollectionRef.get();
    const ordersArray = [];
    orders.forEach((doc) => {
      ordersArray.push(doc.data());
    });
    return ordersArray;
  } catch (error) {
    alert(error);
    return [];
  }
};

// Repair/self-heal action: reads every order for the month and overwrites
// (not increments) the stats doc with freshly-summed totals. Deliberately
// NOT on the default read path — this is the only HandCarry function that
// fetches a whole month's worth of order docs, and it only runs when a
// staff member explicitly asks for it.
export const recalculateHandCarryMonthlyStats = async (month) => {
  const orders = await getAllHandCarryOrdersByMonth(month);
  const stats = orders.reduce(
    (acc, order) => {
      acc.totalInvoices += 1;
      acc.totalBill += parseFloat(order.totalBill) || 0;
      acc.totalPaid += parseFloat(order.totalPaid) || 0;
      acc.totalShippingCost += parseFloat(order.totalShippingCost) || 0;
      acc.totalShippingBill += parseFloat(order.totalShippingBill) || 0;
      acc.totalProfit += parseFloat(order.totalProfit) || 0;
      acc.totalRefundGiven += parseFloat(order.totalRefundGiven) || 0;
      return acc;
    },
    {
      totalInvoices: 0,
      totalBill: 0,
      totalPaid: 0,
      totalShippingCost: 0,
      totalShippingBill: 0,
      totalProfit: 0,
      totalRefundGiven: 0,
    },
  );

  const statsRef = firestore.doc(`handCarryMonthlyStats/${month}`);
  try {
    await statsRef.set({ month, ...stats });
    const updatedSnapShot = await statsRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    alert(error);
    return false;
  }
};
